import {
  applySimpleGeometryPropertyChanges,
  appendCreatedEntity,
  computeInverse,
  resolveVpclipBoundaryPolygon,
  buildPropertiesApplyInverseFromSnapshots,
  getSharedPropertiesForSelection,
  mtextLayoutMeasurementDropped,
  viewportHasRealClip,
  type PropertyApplyPrevNext,
} from "@dxf-ai-editor/command-engine";
import {
  assembleDrawingDocument,
  assembleDrawingDocumentInPlace,
  bumpForClasses,
  coerceColor,
  diffBumpClasses,
  EMPTY_BUMP_CLASSES,
  hasAnyBumpClass,
  initialRevisionCounters,
  mintNewDocumentEpoch,
  patchEntityById,
  selectionMembershipChanged,
  serializeColor,
  unionBumpClasses,
  type BumpClassSet,
  type RevisionStateDiff,
  type DimensionStyle,
  type DrawingDocument,
  type DrawingEntity,
  type EntityColor,
  type LayerDefinition,
  type MultileaderStyle,
  type RevisionCounters,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  ALL_COMMAND_DESCRIPTORS,
  resolveAlias,
} from "@dxf-ai-editor/command-registry";
import { create, type StateCreator } from "zustand";

import {
  dynamicBlockCommandSchema,
  editorCommandSchema,
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
  type EntityPreview,
  type HistoryEntry,
  // CC-1c — view-op telemetry (REGEN/REGENALL/ZOOM/PAN) emitter types.
  type CommandSource,
  type ViewOpDetail,
  type ViewOpType,
  // CC-1e — non-command interaction telemetry (clicks / toggles / nav).
  describeUiClickOperation,
  describeUiToggleOperation,
  describeUiNavOperation,
  type TelemetryNavKind,
  type TelemetryTone,
} from "@dxf-ai-editor/contracts";
import { t } from "../i18n";
import {
  applyLiveCeColor,
  applyLiveHeaderSysvars,
  buildCecolorHydration,
  buildShellEntityPreview,
  exportGeometryDocumentToDxfText,
  rawGeometryPropertyValues,
} from "./dxfGeometry";
// @codex PR #785 round 2, F2 — structural PRESENCE-bit diff for the
// `properties/apply` inverse (see its module doc): decides when the
// display-string replay must be composed with the pre-edit typed snapshot.
import { firstGainedOptionalFieldPath } from "./propertiesApplyPresenceRestore";
// DEFUSE S16 — Properties-panel single-select typed READ cutover. The row
// builder sources every descriptor-covered cell from the typed `DrawingEntity`
// and homes the `__`-key filter (`isInternalPropertyKey`), its sole caller.
// S16 remainder r1 (Р16.5 W1.1) adds the MULTI-select value cutover — same
// typed-eligibility rule, applied per shared property across the selection.
import {
  buildTypedMultiSelectPropertyValues,
  buildTypedSingleSelectRows,
} from "./panelPropertyRows";
import { useSysvarStore } from "../dynamicInput/sysvarStore";
import {
  parseColorSetCommand,
  describeColor,
} from "../dynamicInput/colorCommand";
import { parseVpclipCommandText } from "../dynamicInput/vpclipCommand";
import {
  getCeColor,
  stampCeColorOntoCreateCommand,
  useCeColorStore,
} from "./cecolorStore";
import {
  resolveGeometryCommandFromText,
  reduceGeometryDocument,
  reduceGeometryDocumentWithMutations,
} from "./geometryReducer";
// Lane F round-? FINDING 2 — shared INSERT-by-id lookup, reused so the
// undo-inverse builder below snapshots the SAME pre/post INSERT the
// DDATTE/EATTEDIT reducer handlers themselves resolve (ONE lookup truth).
import { resolveInsertAnywhere } from "./attributeReducerHelpers";
import { dimensionStyleTableFor } from "./blockExtentsCache";
import {
  commitMutation,
  isEntityLevelNoOpMutation,
  mergeMutations,
} from "./commitMutation";
// STORE-SPLIT slice 4 — the revision-diff / document-table-fingerprint helper
// family (isEmptyMutation / stableJson / omitSpaceEntitiesWithoutReading /
// documentTableFingerprint / docHeaderChanged / shellEntitiesChanged /
// contextChangedBetween / computeRevisionStateDiff /
// diffDocumentMutationsHasEntityChange) moved verbatim to
// editorStoreRevisionDiff.ts. editorStore.ts calls four of them at the dispatch
// chokepoint; docHeaderChanged is re-exported below to preserve its former
// public export. (This module no longer imports geometryMutationHelpers
// directly — diffDocumentMutations / documentWithoutSyntheticSpaces are now
// consumed only inside the moved diffDocumentMutationsHasEntityChange, in
// editorStoreRevisionDiff.ts.) R17 A01 (STATE-HIST) — `computeRevisionStateDiff`
// is no longer imported here: its only call sites (the undo/redo/abort-batch
// history lifecycle) moved with the handler bodies to
// `editorStoreHistoryUndoRedo.ts` / `editorStoreHistoryReplay.ts`, which import
// it directly from this same leaf module.
import {
  contextChangedBetween,
  docHeaderChanged,
  isEmptyMutation,
  shellEntitiesChanged,
  shellEntitiesWithoutTransitionalSyntheticSpaces,
} from "./editorStoreRevisionDiff";
export { docHeaderChanged } from "./editorStoreRevisionDiff";
// PR1b Phase 2a — whole-document import heal + warn-only export gate.
import {
  buildExportAuditWarning,
  buildExportEntitiesSnapshot,
} from "./referenceHealWiring";
import {
  entityMetadataIndex,
  clearMutationStore,
  // PR2 Codex P2 (#217) — re-sync block-def dep-index containers after a
  // block-edit/refedit command (BEDIT/BSAVE/REFEDIT mutate blocks[name].entities
  // outside the commitMutation space-diff choke point).
  refreshBlockDefDependencyIndexContainers,
  getCompactCache,
  getEntityFromCompactCache,
  setActiveDocument,
  getActiveDocumentId,
  DEFAULT_DOCUMENT_ID,
  // Phase 4 Wave 2 Task 4 — streaming-import wiring.
  forEachSpaceEntity,
  iterateSpaceEntity,
} from "./mutationStore";
// PR2 (plan §13) — register the active-document accessor for the read-only
// DrawingObjectGraph facade (`getLayer` / `getBlockDefinition`). COLLAB-LOCK-
// COMMAND also reads `drawingObjectGraph.getLayer` directly — the SAME
// case-insensitive layer-by-name accessor other commands resolve against,
// reused for `LOCK LAYER <name>` / `UNLOCK LAYER <name>` rather than
// hand-rolling a second lookup.
import { drawingObjectGraph, setDrawingObjectGraphDocumentProvider } from "./drawingObjectGraph";
import {
  enable as enableImportMetrics,
  installGlobalAccessor as installImportMetricsGlobalAccessor,
  recordMetadataBulkLoad,
  recordSetStoreCall,
} from "./importMetrics";
import {
  materializeDocumentSpacesConcretely,
  wrapDocumentAsReadThrough,
} from "./spaceReadThrough";
import { installDefaultTelemetryServerChannel } from "./telemetryServerChannel";
import { NO_MUTATIONS, type EntityMutation } from "./types";
// STORE-SPLIT slice 1 — style-table + layout-plot-settings action family,
// extracted verbatim to `editorStoreStyleTables.ts` and composed back into the
// initializer below via `...createStyleTableActionsSlice(set, get)`. The
// `StyleTableActionsSlice` interface is intersected into `EditorStore` so the
// external type surface is unchanged.
import {
  createStyleTableActionsSlice,
  stampCurrentDimStyleOntoCreateCommand,
  stampCurrentLayerOntoCreateCommand,
  type StyleTableActionsSlice,
} from "./editorStoreStyleTables";
// STORE-SPLIT slice 5 — the initial / demo session-shell + geometry-document
// builders + their seed fixtures + the current-layer reconcile guard, extracted
// verbatim to `editorStoreInitialState.ts`. The four symbols editorStore.ts still
// uses (its initializer + reset + hydrateSessionShell + the dispatch chokepoint's
// reconcileCurrentLayerAgainstTable) are imported back; the three former public
// builders + the `HydrateSessionShellInput` type are re-exported so external
// import paths (tests, App seams) stay unchanged.
import {
  buildInitialEditorShellState,
  buildInitialGeometryDocument,
  canonicalStandardDimStyleName,
  hydrateEditorSessionShellState,
  namesEqualCaseInsensitive,
  reconcileCurrentDimStyleAgainstTable,
  reconcileCurrentLayerAgainstTable,
  type HydrateSessionShellInput,
} from "./editorStoreInitialState";
export {
  buildInitialEditorShellState,
  buildInitialGeometryDocument,
  hydrateEditorSessionShellState,
  type HydrateSessionShellInput,
} from "./editorStoreInitialState";
// STORE-SPLIT slice 2 — the DXF text-import + import-progress-lifecycle action
// family, extracted verbatim to `editorStoreImportActions.ts` and composed back
// into the initializer below via `...createDxfImportActionsSlice(set, get,
// deps)`. The `DxfImportActionsSlice` interface is intersected into
// `EditorStore` so the external type surface is unchanged.
import {
  createDxfImportActionsSlice,
  type DxfImportActionsSlice,
} from "./editorStoreImportActions";
// STORE-SPLIT slice 3 — the two `File`-handle DXF import variants
// (importDxfFileAsync INSERT-style streaming merge; importDxfFileAsyncReplacing
// OPEN-style streaming replace) extracted verbatim to
// `editorStoreImportFileActions.ts` and composed back into the initializer below
// via `...createDxfImportFileActionsSlice(set, get, deps)`. The
// `DxfImportFileActionsSlice` interface is intersected into `EditorStore` so the
// external type surface is unchanged.
import {
  createDxfImportFileActionsSlice,
  type DxfImportFileActionsSlice,
} from "./editorStoreImportFileActions";
// R17 A01 (STATE-HIST) — the seven `history/*` command-handler BODIES
// (`dispatch()`'s undo/redo/begin-batch/commit-batch/abort-batch/replay-
// forwards/replay-inverses branches) extracted VERBATIM to three sibling
// modules, no behaviour change. `dispatch()` below still OWNS the
// `command.type === "history/…"` routing/narrowing and calls each handler
// with its own `set`/`get` (zustand passes the SAME function references into
// this file's `StateCreator` initializer, so `get()` is byte-identical in
// behavior to the former `useEditorStore.getState()` call sites inside these
// bodies — see `editorStoreHistoryUndoRedo.ts`'s header for the full
// precedent/reasoning, shared with `editorStoreImportActions.ts`). The
// COLLAB-LOCK-COMMAND veto-aware history rationale (why every recursive
// bypass-dispatch checks `wasLastDispatchCollabLockVetoed()`) now lives in
// `editorStoreHistoryUndoRedo.ts`'s header comment.
import {
  handleHistoryRedo,
  handleHistoryUndo,
} from "./editorStoreHistoryUndoRedo";
import {
  handleHistoryBeginBatch,
  handleHistoryCommitBatch,
} from "./editorStoreHistoryBatch";
import {
  handleHistoryAbortBatch,
  handleHistoryReplayForwards,
  handleHistoryReplayInverses,
} from "./editorStoreHistoryReplay";
// STORE-SPLIT slice 2 — the import-family MODULE-LEVEL machinery (progress
// type/seed, in-flight worker latch, streaming thresholds, coordinate-origin
// accumulator, sysvar/CECOLOR hydrate-in, empty-seed predicate, worker-document
// defaults, streamed-viewport finalize, import-merge bump) moved verbatim to
// `editorStoreImportHelpers.ts`. The `File`-handle import variants that consumed
// most of this machinery now live in `editorStoreImportFileActions.ts` (slice 3)
// and import it directly; editorStore.ts still uses the initial-progress seed +
// progress type, and RE-EXPORTS the previously-public helpers below so external
// import paths stay unchanged.
import {
  INITIAL_IMPORT_PROGRESS,
  type ImportProgress,
} from "./editorStoreImportHelpers";
// STORE-SPLIT slice 2 — re-export the previously-editorStore-public import
// helpers so every external import path (tests, App seams) stays unchanged.
export {
  createCoordinateOriginAccumulator,
  finalizeStreamedViewportMembership,
  isDocumentEffectivelyEmpty,
  normalizeWorkerDocumentDefaults,
  type ImportProgress,
} from "./editorStoreImportHelpers";
import {
  enrichDimensionEntities,
  enrichMultileaderEntities,
  translateExtendedEntityPropertyChanges,
} from "./propertyTranslator";
import {
  syncShellStateFromGeometryDocument,
  syncShellStateFromMergedGeometryDocument,
  registerCachedEntitiesLookup,
  resolveGeometryDocumentCommand,
} from "./storeHelpers";
// @codex #420 round-3 — the ONE coherent shell↔document entity resolver
// (source-read + undo-snapshot + target-write route through it) and its shared
// document→preview builder. `matchpropStoreDeps` imports only dxfGeometry +
// propertyTranslator (pure preview / predicate helpers) — no back-edge into
// `editorStore`, so this stays acyclic.
import {
  buildDocumentBackedPreview,
  resolveCoherentEntityPreview,
} from "./matchpropStoreDeps";
// Plan §8 Phase 2.6 (2026-05-19) — pure selectors extracted to
// `editorSelectors.ts`. Re-exported below as the editorStore.ts
// barrel surface so existing import sites keep working.
import {
  EMPTY_ENTITIES_IN_SPACE,
  emptyDimensionStyles,
  emptyMultileaderStyles,
  emptyTextStyles,
  selectActiveSpace,
  selectCurrentUser,
  selectDimensionStyles,
  selectEntitiesInActiveSpace,
  selectMatchpropSourceEntity,
  selectModelSpaceEntities,
  selectMultileaderStyles,
  selectTextStyles,
} from "./editorSelectors";
export {
  EMPTY_ENTITIES_IN_SPACE,
  emptyDimensionStyles,
  emptyMultileaderStyles,
  emptyTextStyles,
  selectActiveSpace,
  selectCurrentUser,
  selectDimensionStyles,
  selectEntitiesInActiveSpace,
  selectMatchpropSourceEntity,
  selectModelSpaceEntities,
  selectMultileaderStyles,
  selectTextStyles,
};
import { reduceShellUiCommand } from "./shellUiReducer";
import {
  findLayoutSpaceByName,
  findSwitchableSpaceByName,
  listSwitchableSpaces,
  parseLayoutMutationText,
  parseLayoutSwitchText,
  resolveLayoutMutationCommand,
} from "./spaceMutationGuards";
import { reducePropertyCommand } from "./propertyReducer";
import { reduceGeometryShellCommand } from "./geometryShellReducer";
// ARRAYPATH-OUTCOME — the shell's ARRAYPATH row is optimistic until the
// geometry reducer has run; this settles it from `dispatchMutations`. Wording
// and rewrite rules live in the leaf, not here.
import { applyArrayPathOutcomeToAudit } from "./arrayPathShellAudit";
import { reduceAnnotativeShellCommand } from "./annotativeShellReducer";
// CC-1c — shared audit chokepoint (replaces the removed editorStore-local
// clone) + the view-op emitter used by the `recordViewOp` store action.
import {
  pushAuditEvent,
  pushViewOpAuditEvent,
  pushUiInteractionAuditEvent,
} from "./reducerHelpers";
import {
  entityMetadataFromDrawingEntity,
  type EntityMetadataIndex,
} from "./entityMetadataIndex";
// post-V9 Section 0 — DXF import-stage instrumentation. Stages are
// recorded as `performance.measure` entries the runner reads after the
// import settles. Always-on; cost ~100ns per call.
import {
  markStart as section0MarkStart,
  measureSince as section0MeasureSince,
  getSection0Measures,
  clearSection0Measures,
  sampleEntitySizes,
  measureCompactLineRepresentation,
} from "./section0Instrumentation";
import { useSpaceContextStore } from "./spaceContextStore";
// COLLAB-LOCK-COMMAND — the collab soft-lock connection store (SEPARATE from
// editorStore, mirrors connectionStore's separation) + the pure fail-closed
// edit-veto predicate it feeds. `lockScopeKey` is the ONE canonical scope-key
// helper (client mirror of the server's `collabTypes.ts` — see
// `collabLockTypes.ts`'s header) — reused here so the CLI intercept + the
// veto call site key their lock lookups identically to the client/store.
import {
  useCollabLockStore,
  type CollabLockStoreState,
} from "./collabLockStore";
import { evaluateCollabLockVeto } from "./collabLockVeto";
// R17 A01 (STATE-HIST) — `wasLastDispatchCollabLockVetoed` is no longer
// imported here: its only call sites (the undo/redo/abort-batch/replay-*
// history handlers) moved with the handler bodies to
// `editorStoreHistoryUndoRedo.ts` / `editorStoreHistoryReplay.ts`, which
// import it directly from this same leaf module.
import {
  markDispatchCollabLockVetoed,
  resetDispatchCollabLockVetoFlag,
} from "./collabLockDispatchVetoSignal";
import { lockScopeKey, type LockScope } from "../collab/collabLockTypes";
// CC-2b (S-AIDRAFT-WIRE) — app-side mount of `@dxf-ai-editor/ai-commands`
// behind the safe contour. Translates an AI-proposed command via the CC-2a
// fail-closed guard and routes the result through `dispatch` (validation →
// audit → grouped undo). No UI surface (that is CC-2c/CC-2d).
import {
  runAiCommandThroughSafeContour,
  runAiCommandBatchThroughSafeContour,
  type AiSafeContourResult,
  type RunAiThroughSafeContourOptions,
} from "../ai/aiSafeContour";
// ABB-S4 — the shared single-in-flight latch (INV-INFLIGHT). The window AI seams
// + the AiDraftingPanel Accept + the bridge command.run handler ALL check it so a
// bridge turn can never NEST into an open local batch (`history/begin-batch`
// depth+1 corrupts the one-undo-per-turn contract). `editorStore → agentBridge` is
// the ALLOWED one-directional edge (the latch leaf imports NO editorStore — cycle
// free; verified by `npm run deps:graph`).
import {
  releaseInFlight,
  tryAcquireInFlight,
} from "../agentBridge/inFlightLatch";
// CC-2d — one-truth INSERT-id collector + dispatch-context resolver shared
// with the AI drafting panel (the panel passes the same resolver to the safe
// contour it calls directly). #292 folded the layoutContext derivation +
// defined-only override merge INTO that module (single truth).
import {
  collectAiEntitySnapshot,
  collectAiInsertIds,
  resolveAiDispatchContext as resolveAiDispatchContextFromState,
} from "../ai/aiDispatchContext";
import type {
  AiCommand,
  AiDispatchContext,
} from "@dxf-ai-editor/ai-commands";

// Re-export for external consumers that import from editorStore
export { reduceGeometryDocument } from "./geometryReducer";
// Codex round 2 #8 (PR #87) — cache-aware export. App.tsx's
// `exportedDxfText` useMemo needs to feed `entitiesSnapshot` to
// `exportDrawingDocumentToDxfString` when streamed OPEN left the
// inline `space.entities[]` empty. Re-export the snapshot helper
// here so the import surface stays editorStore-centric.
export {
  snapshotSpaceEntities,
  peekDocumentMemoryHolder,
} from "./mutationStore";

/**
 * ## Function dependency map
 *
 * useEditorStore (zustand store)
 *   dispatch(command)
 *     -> reduceEditorShellState(state, command)
 *         -> reduceShellUiCommand (shellUiReducer.ts)
 *         -> reducePropertyCommand (propertyReducer.ts)
 *         -> reduceGeometryShellCommand (geometryShellReducer.ts)
 *         -> routeLayoutCommand -> pushAuditEvent, resolveGeometryCommandFromText
 *     -> resolveGeometryDocumentCommand (storeHelpers.ts)
 *     -> reduceGeometryDocument (geometryReducer.ts)
 *     -> syncShellStateFromGeometryDocument (storeHelpers.ts)
 *     -> enrichDimensionEntities / enrichMultileaderEntities (propertyTranslator.ts)
 *   DXF text-import + import-progress action family (STORE-SPLIT slice 2):
 *     importDxfText / importDxfTextAsync / abortDxfImport /
 *     setImportProgress / clearImportProgress
 *     -> createDxfImportActionsSlice (editorStoreImportActions.ts)
 *         -> editorStoreImportHelpers.ts (latch, hydrates, empty-seed
 *            predicate, merge-bump — shared with the still-inline File
 *            variants below)
 *         -> deps.rebuildEntityMetadataIndexFromImport(-Async) (this module)
 *   importDxfFileAsync / importDxfFileAsyncReplacing (slice-3 scope, inline)
 *     -> startDxfImportFromFileInWorker (dxfWorkerClient.ts)
 *     -> mergeImportedGeometryDocumentAsync (storeHelpers.ts)
 *     -> syncShellStateFromMergedGeometryDocument (storeHelpers.ts)
 *     -> editorStoreImportHelpers.ts (shared import machinery)
 *   style-table + layout-plot-settings action family (STORE-SPLIT slice 1):
 *     updateDimensionStyle / addDimensionStyle / deleteDimensionStyle /
 *     renameDimensionStyle / updateMultileaderStyle / addMultileaderStyle /
 *     deleteMultileaderStyle / updateTextStyle / addLayer / updateLayer /
 *     deleteLayer / setLayoutPlotSettings
 *     -> createStyleTableActionsSlice (editorStoreStyleTables.ts)
 *         -> syncShellStateFromGeometryDocument (storeHelpers.ts)
 *         -> directSetTableBumpSlice / isUnchangedShallowUpdate (same module)
 *   setCurrentLayer (stays inline; propertyWorkflows current-layer, not a table edit)
 *   hydrateSessionShell -> hydrateEditorSessionShellState
 *   executeBatchCommands -> dispatch (loop)
 *
 * Selectors (pure functions):
 *   selectCurrentUser / selectActiveSpace / selectEntitiesInActiveSpace
 *   selectSelectedEntities / selectMatchpropSourceEntity / selectChpropOptions
 *   selectTextStyles / selectDimensionStyles / selectMultileaderStyles
 *   derivePropertiesView -> getSharedPropertiesForSelection (command-engine)
 *
 * routeLayoutCommand(state, rawText, command)
 *   -> pushAuditEvent / withLastSubmitted
 *   -> resolveGeometryCommandFromText (geometryReducer.ts)
 *   -> reduceEditorShellState (recursive for CHPROP/MATCHPROP)
 */

type PropertyRow = {
  label: string;
  value: string;
  /** S16-ARRAYRECT-READONLY-ALIGN (W2.1) — true when the panel must render
   *  this cell non-interactive (an associative ArrayRect's grid fields /
   *  `Associative` itself — `panelPropertyRows.ts` resolves it against the
   *  typed descriptor's `readOnly` predicate). Absent/`undefined` for the
   *  no-selection default-properties rows (no descriptor to consult) —
   *  `PropertyGrid` treats that the same as `false`. */
  readOnly?: boolean;
};

export type PropertiesViewModel = {
  title: string;
  badge: string;
  note: string;
  rows: PropertyRow[];
};

// STORE-SPLIT slice 5 — `HydrateSessionShellInput` moved to
// editorStoreInitialState.ts (imported + re-exported above).
export type BatchCommandResult = {
  index: number;
  text: string;
  ok: boolean;
  error?: string;
};

// STORE-SPLIT slice 2 — `ImportProgress`, `INITIAL_IMPORT_PROGRESS` and
// `HEAL_TRANSCRIPT_COMMAND` moved verbatim to `editorStoreImportHelpers.ts`
// (imported + re-exported above).

/** Transcript command for the warn-only pre-export audit gate (the export
 * sibling of the import-family `HEAL_TRANSCRIPT_COMMAND`, which lives in
 * `editorStoreImportHelpers.ts`). */
const EXPORT_AUDIT_TRANSCRIPT_COMMAND: EditorCommand = {
  type: "commandline/submit",
  source: "system",
  text: "AUDIT (export gate)",
};

/**
 * D4 completeness-gate source of truth (side B) — `EditorCommand` types whose
 * UNDO inverse is HAND-BUILT here in `dispatch()`, OUTSIDE the package
 * `computeInverse` switch (see `computeInverseCoverage.ts`'s side A for the
 * types covered INSIDE `computeInverse`). These all need BOTH the pre- AND
 * post-mutation document simultaneously (a reorder/snapshot-diff computation)
 * or a document slice `computeInverse` has no visibility into, so the inverse
 * is built right after the `computeInverse(...)` call below rather than as a
 * package-level case:
 *   - `geometry/create-attdef`   — diffs the block's Attdef ids pre/post to
 *     find the freshly-minted one (`geometry/remove-attdef` inverse).
 *   - `geometry/reorder-attdefs` — self-symmetric: snapshots the PRE-mutation
 *     Attdef id order and re-emits the same command type to restore it.
 *   - `file/xattach` / `xbind` / `xunload` / `xreload` / `xdetach` / `xclip`
 *     — snapshot-restore of the four xref-bearing document slices
 *     (`file/xref-restore-snapshot`).
 *   - Lane J (D3-1) style/layer TABLE + layout-plot commands (the eight
 *     direct-set → dispatch add/update/plot commands AND the four dispatch-
 *     routed delete/rename siblings) — snapshot-restore of the touched
 *     document slices (`header/restore-style-tables-snapshot`): `styleTables`
 *     for the style/layer edits, `layouts` + `layoutsBySpaceId` for the plot
 *     edit, plus a materialised `spaces` snapshot for the delete/rename entity
 *     remaps. Before Lane J these were direct-set (no history) / KNOWN_GAP (no
 *     inverse) — routing them here makes LAYER / DIMSTYLE / MLEADERSTYLE /
 *     text-style / PAGESETUP edits undo-able.
 *
 * The app-level completeness test (`computeInverseCompleteness.test.ts`)
 * cross-references this list against `COMPUTE_INVERSE_COVERED_TYPES` and its
 * own non-invertible allowlist so EVERY `EditorCommand` schema variant is
 * accounted for by EXACTLY ONE of the three.
 */
export const HAND_BUILT_INVERSE_COMMAND_TYPES: ReadonlySet<
  EditorCommand["type"]
> = new Set([
  "geometry/create-attdef",
  "geometry/reorder-attdefs",
  "file/xattach",
  "file/xbind",
  "file/xunload",
  "file/xreload",
  "file/xdetach",
  "file/xclip",
  // Lane J (D3-1) — style/layer TABLE + layout-plot snapshot inverses.
  "header/add-dimension-style",
  "header/update-dimension-style",
  "header/add-multileader-style",
  "header/update-multileader-style",
  "header/update-text-style",
  "header/add-layer",
  "header/update-layer",
  "header/set-layout-plot-settings",
  "header/delete-dimension-style",
  "header/rename-dimension-style",
  "header/delete-multileader-style",
  "header/delete-layer",
  // T7 (agent-bridge layer-mgmt) — LAYER rename, mirrors
  // header/rename-dimension-style's snapshot inverse.
  "header/rename-layer",
]);

// STORE-SPLIT slice 2 — the in-flight worker-import latch (now the shared
// `currentImportHandleRef` ref-object) and the streaming-import thresholds
// (`STREAMING_IMPORT_FILE_SIZE_THRESHOLD` / `STREAMING_PROGRESS_CHUNK_INTERVAL`)
// moved verbatim to `editorStoreImportHelpers.ts` (imported above; still used
// by the slice-3-scoped `File`-handle import variants below).

/**
 * post-V9 Section 0 Iter 6.D — the legacy `entitiesCache: Map<spaceId,
 * DrawingEntity[]>` has been removed. Phase 3b PR #6's
 * slim-state-as-pointer guarantees that the reactive
 * `geometryDocument.spaces[].entities` array always holds the full
 * per-space entity payload (for huge imports too); there is no
 * separate non-reactive cache to fall back to.
 *
 * The shell-sync helpers in storeHelpers.ts accept an optional
 * `cacheLookup` parameter that originally answered "is there a
 * non-reactive backup when `space.entities` is empty?". Post-Iter-6.D
 * an empty `space.entities` is a legitimate state (empty space, or all
 * entities deleted), not a "needs rehydration" signal, so the lookup
 * unconditionally returns `undefined` — `space.entities` is the only
 * source of truth.
 *
 * Reading the current zustand snapshot would be incorrect in any case
 * because `syncShellStateFromGeometryDocument` is invoked with the
 * NEXT geometryDocument (pre-`set`), so a global-store read would
 * return stale entities and resurrect rows the user just deleted.
 */
export function getCachedEntitiesForSpace(
  _spaceId: string,
): readonly DrawingEntity[] | undefined {
  return undefined;
}

// Register the cache lookup with storeHelpers so the shell-sync helpers
// can rehydrate huge-import entity payloads without importing editorStore
// directly (which would cause a circular-init issue). See the JSDoc on
// `registerCachedEntitiesLookup` in storeHelpers.ts for the full
// rationale (boils down to: ESM does not have CommonJS `require`).
registerCachedEntitiesLookup(getCachedEntitiesForSpace);

/**
 * PR-V1 — Entity Metadata Index.
 *
 * Lightweight per-entity record (id/type/layer/color/bounds/spaceId,
 * ~80 B each) covering EVERY entity in the drawing — including the
 * tail of huge files that gets capped out of `state.drawing.entities`.
 * The spatial index, viewport-cull renderer (PR-V2) and panels
 * virtualization (PR-V4) hit this index to identify and locate
 * entities without rehydrating their full DrawingEntity payloads.
 *
 * Phase 3b PR #6 — Source of truth migrated to `mutationStore.ts`.
 * `__entityMetadataIndex` is preserved as a local alias. The single
 * underlying index is shared with `commitMutation` so dispatch pipeline
 * updates land in the same object renderer / virtualisation layers
 * read from.
 */
const __entityMetadataIndex = entityMetadataIndex;

export function getEntityMetadataIndex(): EntityMetadataIndex {
  return __entityMetadataIndex;
}

/**
 * O(1) lookup of the full `DrawingEntity` payload for a single id.
 *
 * post-V9 Section 0 Iter 6.D — compact cache is the primary lookup
 * path now that Iter 6.C wired commitMutation through the compact
 * cache helpers (`appendToCompactCache` / `removeFromCompactCache` /
 * `setInCompactCache`). The legacy `entitiesCache: Map` was removed
 * in this iteration. Opt 5 (memory-optimization-v2) — the compact
 * cache is now populated unconditionally for every import regardless
 * of drawing size; the reactive `geometryDocument.spaces[]` walk is
 * a fallback only for unknown-type entities that `populateCompactCache`
 * skips (REVCLOUD, ARRAYRECT, VIEWPORT, future types).
 *
 * **Identity caveat:** `getEntityFromCompactCache` reconstructs a fresh
 * `DrawingEntity` object on every call — callers relying on `===`
 * identity for cache keys must memoise on `entityId`. Current consumers
 * (Properties panel, MATCHPROP, Scene3DStage `buildPreview`) treat the
 * result as a snapshot and re-read after every dispatch.
 */
export function getEntityById(id: string): DrawingEntity | undefined {
  // Primary: compact cache O(1) — populated unconditionally on every
  // import (Opt 5 threshold removed) and kept in sync by every dispatch
  // via the commitMutation -> {append,remove,set}InCompactCache path.
  //
  // Phase 3 followup (codex P2): `getEntityFromCompactCache` now returns
  // `DrawingEntity | AttdefEntity | undefined` so the block-definition
  // children path can resolve AttDef ids. `getEntityById` is the
  // modelspace/paperspace lookup — narrow AttDef back to `undefined`
  // here so existing callers (Properties panel, MATCHPROP, picking)
  // keep their `DrawingEntity` contract. The geometryDocument fallback
  // loop below also only ever walks modelspace entities (AttDef lives
  // inside `blocks[].children`, not `spaces[]`).
  const fromCompact = getEntityFromCompactCache(id);
  if (fromCompact && fromCompact.type !== "Attdef") return fromCompact;
  // Fallback: walk geometryDocument for unknown-type entities that
  // `populateCompactCache` skips — REVCLOUD, ARRAYRECT, VIEWPORT,
  // future types without a compactKindForEntity mapping. Cost O(N) but
  // these entity types are rare in typical drawings.
  const doc = useEditorStore.getState().geometryDocument;
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      if (entity.id === id) return entity;
    }
  }
  return undefined;
}

/**
 * Populate `__entityMetadataIndex` from a freshly imported drawing
 * document. Reads entities directly from `space.entities` for every
 * space — Phase 3b PR #6's slim-state-as-pointer (post-V9 Section 0
 * Iter 6.D) keeps the per-space entity array populated for both
 * cap-busting and below-cap imports, so no Map fallback is needed.
 * Always replaces the previous index contents.
 *
 * Exported so tests can drive the metadata-index pipeline without
 * exercising the full DXF parse/merge stack (the broader test suite
 * already exercises `importDxfText` end-to-end).
 */
export function rebuildEntityMetadataIndexFromImport(
  document: DrawingDocument,
): void {
  const tIndex = section0MarkStart("client:metadataIndex");
  try {
    const records: ReturnType<typeof entityMetadataFromDrawingEntity>[] = [];
    // Name-keyed DIMSTYLE table for the whole rebuild (memoized per
    // `styleTables.dimensionStyles` array identity) — a `Dimension` inside a
    // block definition contributes its NAMED-style extents to the INSERT box
    // (Codex PR #784 round 3 P1, `blockExtentsCache.ts::dimensionStyleTableFor`).
    const dimStyles = dimensionStyleTableFor(document.styleTables?.dimensionStyles);
    for (const space of document.spaces) {
      // post-V9 Section 0 Iter 6.D — slim-state-as-pointer (Phase 3b
      // PR #6) keeps `space.entities` populated for huge imports.
      // Phase 4 Wave 2 Task 4 — streaming-import path produces empty
      // `space.entities[]` arrays (entities live in the compact cache
      // exclusively).
      const inlineEntities = space.entities;
      if (inlineEntities.length > 0) {
        for (const entity of inlineEntities) {
          records.push(entityMetadataFromDrawingEntity(entity, document.blocks, dimStyles));
        }
      } else {
        // Phase 4 Wave 2 (П35Р follow-up) — walk the cache via
        // `forEachSpaceEntity` instead of materialising via
        // `snapshotSpaceEntities`. The latter allocates a full
        // DrawingEntity[] (~150 MB on 685k drawings) just so we can
        // walk it once — defeats the streaming-import peak reduction.
        forEachSpaceEntity(getActiveDocumentId(), space.id, (entity) => {
          records.push(entityMetadataFromDrawingEntity(entity, document.blocks, dimStyles));
        });
      }
    }
    {
      const _t = performance.now?.() ?? Date.now();
      __entityMetadataIndex.bulkLoad(records);
      recordMetadataBulkLoad((performance.now?.() ?? Date.now()) - _t);
    }
    // PR-V4 — bump the reactive version counter so panel selectors that
    // depend on the (non-reactive) index re-evaluate. Done outside of any
    // active `set(() => ({...}))` call so the counter increment lives on
    // its own commit and does not interfere with the `state` /
    // `geometryDocument` slices the import flow is updating.
    const currentState = useEditorStore.getState();
    useEditorStore.setState({
      entityMetadataIndexVersion: currentState.entityMetadataIndexVersion + 1,
      renderTopologyVersion: currentState.renderTopologyVersion + 1,
    });
  } finally {
    section0MeasureSince("client:metadataIndex", tIndex);
  }
}

/**
 * Phase 1 (P35R) — async chunked variant of
 * `rebuildEntityMetadataIndexFromImport`. Same semantics but yields
 * to the event loop every `chunkSize` entities while walking
 * `space.entities`, so the main thread stays responsive on big
 * imports (685k entities → ~3-5s contiguous sync block in the legacy
 * path).
 *
 * The final `__entityMetadataIndex.bulkLoad(records)` is still sync
 * because it sets typed-array columns in a single pass — measured
 * <250ms on 685k entries; safe to keep inline.
 *
 * Phase 4 Wave 2 (П35Р follow-up — streaming-import enable):
 *   The optional `forceCacheWalk` flag forces the cache walker even
 *   when `space.entities` is non-empty. The streaming-import INSERT
 *   path needs this — the merge step backfills the inline arrays
 *   with the PRIOR document's entities, but the cache holds the
 *   union (prior + streamed). Walking inline-first would surface
 *   only the prior entities in the metadata index, hiding every
 *   newly-streamed one from panels / spatial index. Default false
 *   to keep the legacy bulk-populate path's inline-first behaviour
 *   (cheaper because no holder lookup per entity).
 */
export async function rebuildEntityMetadataIndexFromImportAsync(
  document: DrawingDocument,
  chunkSize = 2000,
  options: { forceCacheWalk?: boolean } = {},
): Promise<void> {
  const forceCacheWalk = options.forceCacheWalk === true;
  const tIndex = section0MarkStart("client:metadataIndex");
  try {
    const records: ReturnType<typeof entityMetadataFromDrawingEntity>[] = [];
    // See the sibling sync rebuild above — one memoized DIMSTYLE table per pass.
    const dimStyles = dimensionStyleTableFor(document.styleTables?.dimensionStyles);
    for (const space of document.spaces) {
      const inlineEntities = space.entities;
      if (!forceCacheWalk && inlineEntities.length > 0) {
        for (let i = 0; i < inlineEntities.length; i += chunkSize) {
          const end = Math.min(i + chunkSize, inlineEntities.length);
          for (let j = i; j < end; j++) {
            records.push(entityMetadataFromDrawingEntity(inlineEntities[j]!, document.blocks, dimStyles));
          }
          if (end < inlineEntities.length) {
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
          }
        }
      } else {
        // Phase 1.A (Codex P1) + autocheck follow-up (2026-05-18) —
        // Build metadata records directly inline (no `buf:
        // DrawingEntity[]` temp staging) AND yield mid-walk every
        // `chunkSize` entities so large single-space drawings don't
        // monopolise the main thread.
        //
        // Why a generator (not `forEachSpaceEntity` callback): the
        // sync callback blocks for the full walk duration (~500ms on
        // 685k single-space). The generator lets us interleave
        // `await setTimeout(0)` between batches without materialising
        // an intermediate `DrawingEntity[]` (the prior buf path
        // retained ~150 MB on P35R). Iteration order, tombstone
        // filter, and ATTDEF skip match the callback version (see
        // `iterateLiveEntitiesInSpace` JSDoc — they must stay in
        // lockstep on predicate changes).
        let walkedSinceYield = 0;
        for (const entity of iterateSpaceEntity(
          getActiveDocumentId(),
          space.id,
        )) {
          records.push(entityMetadataFromDrawingEntity(entity, document.blocks, dimStyles));
          walkedSinceYield += 1;
          if (walkedSinceYield >= chunkSize) {
            walkedSinceYield = 0;
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
          }
        }
        // Final yield per space so multi-space documents don't run
        // back-to-back walks without breathing room.
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
    }
    {
      const _t = performance.now?.() ?? Date.now();
      __entityMetadataIndex.bulkLoad(records);
      recordMetadataBulkLoad((performance.now?.() ?? Date.now()) - _t);
    }
    const currentState = useEditorStore.getState();
    useEditorStore.setState({
      entityMetadataIndexVersion: currentState.entityMetadataIndexVersion + 1,
      renderTopologyVersion: currentState.renderTopologyVersion + 1,
    });
  } finally {
    section0MeasureSince("client:metadataIndex", tIndex);
  }
}

// STORE-SPLIT slice 2 — the import-family module-level helpers
// (createCoordinateOriginAccumulator + computeCoordinateOriginForImport,
// hydrateSysvarsFromImportedDocument / hydrateCeColorFromImportedDocument,
// isDocumentEffectivelyEmpty, normalizeWorkerDocumentDefaults,
// finalizeStreamedViewportMembership) moved verbatim to
// `editorStoreImportHelpers.ts` (imported + re-exported above).

export type EditorStore = StyleTableActionsSlice &
  DxfImportActionsSlice &
  DxfImportFileActionsSlice & {
  state: EditorShellState;
  geometryDocument: DrawingDocument;
  /**
   * Progress of the in-flight DXF import. `active: true` while an
   * `importDxfTextAsync` call is running, `false` otherwise. The UI
   * mounts `<ImportProgressDialog />` only when active.
   */
  importProgress: ImportProgress;
  /**
   * PR-V4 — monotonically increasing counter bumped every time the
   * non-reactive `__entityMetadataIndex` is rebuilt from an imported
   * document or cleared via `reset()`. Panel selectors include this
   * value in their dependency tuple so React re-renders the entity
   * list / layer counts / inspector aggregates after a huge import
   * even though the underlying index lives outside Zustand reactive
   * state.
   *
   * Consumers should treat the value as opaque — only the change
   * matters, not the magnitude. Bumped via
   * `useEditorStore.setState({ entityMetadataIndexVersion: v + 1 })`
   * inside `rebuildEntityMetadataIndexFromImport`.
   */
  entityMetadataIndexVersion: number;
  /**
   * Bumps on entity-set/topology mutations but NOT on viewport-property-only
   * commands (lock/scale/vplayer/clip). Scene3DStage render caches key on this
   * to avoid O(N) rebuilds on every VP lock toggle.
   */
  renderTopologyVersion: number;
  /**
   * AGENT-BRIDGE-BROWSER ABB-S1 — the five browser-minted revision counters +
   * documentEpoch (typed model in `@dxf-ai-editor/drawing-model/revisionModel`).
   * The bridge precondition-adjudicates a forwarded `command.run` by EXACT-
   * equality of the agent's `base{Snapshot,Geometry,Table,Context}Revision`
   * against these LIVE counters. NOT a render-cache token — DISTINCT from
   * `entityMetadataIndexVersion` / `renderTopologyVersion` above (INV-9). DUAL-
   * BUMP at the mutation-commit chokepoint; reset + epoch-mint on document swap.
   */
  revisions: RevisionCounters;
  /**
   * ABB-S1 — transient per-batch bump-class accumulator. `null` outside an open
   * undo batch; an accumulating set while `state.history.current !== null`. At
   * `commit-batch` (depth 1, non-empty) the accumulated classes DUAL-BUMP
   * `revisions` ONCE; `abort-batch` and the empty-batch exit discard it without
   * a bump. Not persisted / not part of any snapshot.
   */
  pendingBumpClasses: BumpClassSet | null;
  /**
   * ABB-S1 round-3 F2 — a SEPARATE per-batch accumulator for DIRECT-SET table
   * mutations (`updateLayer` / `addLayer` / `updateDimensionStyle` / … — the 8
   * actions that poke `geometryDocument` straight via `set()`, NOT through
   * `dispatch`). These are NOT part of the batch's forward/inverse transaction,
   * so `abort-batch` (which only replays the batched `inverses`) does NOT revert
   * them — the mutation PERSISTS past an abort. Tracking their bump separately
   * from the batched-command `pendingBumpClasses` lets `abort-batch` discard ONLY
   * the revertible batched classes while STILL bumping the persisted direct-set
   * change (so the agent can detect it). `commit-batch` folds BOTH accumulators.
   * `null` outside an open batch; reset to EMPTY on the outermost begin-batch.
   */
  pendingDirectSetBumpClasses: BumpClassSet | null;
  /**
   * ABB-S1 round-4 GENERALIZATION 2 — the pre-batch BASE document + shell-state
   * snapshot, captured (by reference — both are immutable zustand snapshots) on
   * the OUTERMOST `begin-batch`. `abort-batch` diffs this base against the
   * POST-abort state via `computeRevisionStateDiff` and bumps from the NET
   * surviving delta — so WHATEVER survives an abort (a batched command with a
   * null / non-invertible inverse whose mutation persists, a direct-set table
   * edit outside the forward/inverse transaction, OR any future abort-persist)
   * bumps, and whatever was reverted does not — the same diff-everything
   * mechanism undo/redo uses, closing the abort long-tail in one move instead of
   * per-persist-kind accumulators. `null` outside an open batch; reset on the
   * outermost begin-batch; cleared at commit/abort. Not persisted.
   */
  pendingBatchBaseDoc: DrawingDocument | null;
  pendingBatchBaseState: EditorShellState | null;
  /**
   * Gate #4 / FIX#1 (LIVE commit/abort audit-telemetry leak) — the pre-batch
   * audit high-water (`state.audit.nextSequence`) captured (by value) on the
   * OUTERMOST `begin-batch`, alongside `pendingBatchBaseDoc`. On `abort-batch`
   * the audit ring is REWOUND to this base: `nextSequence` is reset to it and
   * every `events` / `uiEvents` entry with `sequence >= base` is dropped — making
   * the abort SYMMETRIC with the commit on the audit ring (geometry is reverted
   * by the inverse replay; the audit-ring delta must be reverted too). Without it
   * the aborted turn's audit events kept their committed sequence numbers, cleared
   * the telemetry channel's high-water filter, and streamed to the server as if
   * COMMITTED. The rewind is the second half of the symmetric fix; the first half
   * is the telemetry subscriber DEFERRING projection while a batch is open (see
   * `installAuditTelemetryTransport`) so events flushed mid-batch are never
   * un-sendably gone before the rewind runs. `null` outside an open batch; reset on
   * the outermost begin-batch; cleared at commit/abort. Not persisted.
   */
  pendingBatchBaseAuditSequence: number | null;
  /**
   * Gate #4 round-2 (FIX C — telemetryServerChannel.ts:793) — the EXPLICIT
   * discriminator for HOW the most-recent open history batch was closed:
   *   - `"commit"`  — `history/commit-batch` (depth 1): the turn's staged
   *     telemetry events legitimately stream to the server.
   *   - `"abort"`   — `history/abort-batch`: the turn was reverted; staged
   *     telemetry must be DISCARDED, never posted.
   *   - `"discard"` — a NON-commit close that clears `history.current` (and the
   *     pending base) WITHOUT a commit: import / open-new-document / INSERT-merge
   *     / `reset()` document swap. The discarded batch's staged telemetry must
   *     NOT be posted as committed.
   *   - `null`      — no batch has closed yet this session (initial / mid-batch).
   *
   * # Why this exists (the round-2 P2)
   *
   * The telemetry subscriber previously INFERRED "commit" from
   * `history.current → null` + a non-rewound audit high-water (every staged
   * sequence `<= highWater`). But import/open/merge/reset ALSO drive
   * `history.current → null` WITHOUT rewinding the audit ring — so their staged
   * in-batch events satisfied `sequence <= highWater` and got POSTED as
   * committed even though that batch was DISCARDED. This field makes the
   * commit-vs-discard decision EXPLICIT (set at the close site, read by the
   * subscriber) so high-water inference can never misclassify a discarding close
   * as a commit. The subscriber flushes staged telemetry ONLY on
   * `lastBatchClosedBy === "commit"`. Not persisted / not part of any snapshot.
   */
  lastBatchClosedBy: "commit" | "abort" | "discard" | null;
  dispatch: (command: EditorCommand | DynamicBlockCommand) => void;
  appendShellEntity: (input: {
    entity: EntityPreview;
    summary: string;
    source?: EditorCommand["source"];
    commandText?: string;
  }) => void;
  /**
   * PR-10 — record a deferred-propagation audit event for the
   * DimStyle / MLeaderStyle annotative toggle. The dialogs invoke this
   * after the user confirms the inline propagation prompt; per-entity
   * propagation itself is deferred until DIMENSION/MLEADER schemas
   * migrate to `annotScaleContextSchema` (PR-14 territory). Today this
   * helper just pushes a transcript + audit message so the action is
   * visible to the user and discoverable in telemetry.
   */
  recordAnnotativeStylePropagation: (input: {
    styleKind: "dimstyle" | "mleaderstyle";
    styleName: string;
    direction: "enable" | "disable";
    cannoscaleName: string;
  }) => void;
  /**
   * PR-17 — record a deferred-action audit entry from the BEDIT
   * annotative preview tab strip. Replaces the inline `console.info` /
   * `console.warn` calls in `PrimitiveAtlasStage.tsx` so the user's
   * actions surface in the command-line transcript and the audit event
   * log (where downstream telemetry can pick them up). Today both
   * actions are deferred placeholders — see CLAUDE.md "никаких
   * console.* для пользовательских изменений состояния".
   *
   * - `kind: "preview"` fires when the user clicks an annotative scale
   *   tab in the BEDIT preview strip; corresponds to AutoCAD's
   *   "preview decomposition" action.
   * - `kind: "generate"` fires when the user clicks the
   *   "Generate decomposition" CTA on a non-decomposed scale tab.
   *
   * `scaleName` is the scale-tab the user interacted with (e.g.
   * "1:50"), echoed in the audit summary so the transcript identifies
   * which scale was targeted.
   */
  recordBeditDeferredAction: (input: {
    kind: "preview" | "generate";
    scaleName: string;
  }) => void;
  /**
   * CC-1c — emit a typed `view-op` audit/telemetry event for a pure view
   * operation (REGEN/REGENALL/ZOOM/PAN) that does NOT pass through the
   * command schema / `geometryShellReducer`. The App.tsx command router calls
   * this alongside the existing camera-API mutation + command feed so the
   * CC-1a server channel (which observes the audit ring) has a typed event to
   * transport (ТЗ §10.1 / §14). `detail` carries op-specific scalar params
   * (zoom mode, pan displacement, …).
   */
  recordViewOp: (input: {
    opType: ViewOpType;
    summary: string;
    source?: CommandSource;
    tone?: TelemetryTone;
    detail?: ViewOpDetail;
  }) => void;
  /**
   * CC-1e — record a NON-command UI interaction (ТЗ §14 "clicks" axis). Wired
   * at the real UI sites (toolbar/ribbon/quickbar buttons, panel/tab/space
   * switches, drawing-aid toggles). These three actions append a typed audit
   * event to the ring ONLY (no command-feed transcript row) so the CC-1a
   * transport carries them without changing command/geometry behaviour or the
   * user-facing command feed. `source` defaults to "toolbar"; `summary` is a
   * non-localised telemetry string (NOT user-facing UI copy — i18n N/A).
   */
  recordUiClick: (input: {
    target: string;
    controlId: string;
    summary?: string;
    source?: CommandSource;
    tone?: TelemetryTone;
  }) => void;
  recordUiToggle: (input: {
    setting: string;
    enabled: boolean;
    summary?: string;
    source?: CommandSource;
    tone?: TelemetryTone;
  }) => void;
  recordUiNav: (input: {
    navKind: TelemetryNavKind;
    from: string;
    to: string;
    summary?: string;
    source?: CommandSource;
    tone?: TelemetryTone;
  }) => void;
  // STORE-SPLIT slice 2 — the DXF text-import + import-progress-lifecycle
  // action family (importDxfText / importDxfTextAsync / abortDxfImport /
  // setImportProgress / clearImportProgress) is declared by
  // `DxfImportActionsSlice` (editorStoreImportActions.ts), intersected into
  // this type above.
  // STORE-SPLIT slice 3 — the two `File`-handle import variants
  // (`importDxfFileAsync` — INSERT-style streaming merge; and
  // `importDxfFileAsyncReplacing` — OPEN-style streaming replace) are declared
  // by `DxfImportFileActionsSlice` (editorStoreImportFileActions.ts),
  // intersected into this type above. Their full behaviour JSDoc lives on that
  // slice interface + module.
  exportDxfText: () => string;
  /**
   * PR1b Codex P2 (iter-3) — warn-only pre-export reference audit. Builds the
   * SHARED per-space cache snapshot (`buildExportEntitiesSnapshot`), audits the
   * SAME effective entity source the exporter writes, and pushes a transcript
   * warning iff unresolved refs remain. NEVER blocks the save. Exposed as an
   * action so the App.tsx download/copy/preview path (which calls its local
   * `buildExportedDxfText` serialiser directly, bypassing `exportDxfText`)
   * routes through the SAME single audit-emit point — no double-warn, no
   * bypass. Returns the snapshot so the caller can reuse it for the serialise
   * (one snapshot build per export).
   *
   * PR1b Codex P2 (iter-5) — `options.emitWarning` (default `true`) splits the
   * two concerns: (a) BUILD the per-space export snapshot vs (b) EMIT the
   * warn-only audit transcript warning. The small-drawing PREVIEW / render-driven
   * re-serialise path passes `{ emitWarning: false }` so an ordinary edit/import
   * no longer pushes a "save" warning the user never triggered; it still gets the
   * CORRECT snapshot source (streaming provenance + `linetypeScale`). Only
   * user-initiated export (Download / Copy / Ctrl+S / store `exportDxfText`) emits.
   */
  auditExportReferences: (options?: {
    emitWarning?: boolean;
  }) => Map<string, DrawingEntity[]> | undefined;
  hydrateSessionShell: (input: HydrateSessionShellInput) => void;
  // STORE-SPLIT slice 1 — the style-table + layout-plot-settings action family
  // (updateDimensionStyle / addDimensionStyle / deleteDimensionStyle /
  // renameDimensionStyle / updateMultileaderStyle / addMultileaderStyle /
  // deleteMultileaderStyle / updateTextStyle / addLayer / updateLayer /
  // deleteLayer / renameLayer / setCurrentLayer / setLayoutPlotSettings) is
  // declared by `StyleTableActionsSlice` (editorStoreStyleTables.ts),
  // intersected into this type above. T7 (agent-bridge) moved `setCurrentLayer`
  // here (was a stray direct-`set` sibling left OUT of the Lane J slice) and
  // added `renameLayer` — both now dispatch through the SAME chokepoint the
  // rest of the family uses.
  /**
   * AI_BATCH_COMMANDS — execute a sequence of command-line entries atomically.
   * Each entry is dispatched as a normal commandline/submit through the shared
   * command engine, preserving validation, audit trail and undo history.
   *
   * Example:
   *   executeBatchCommands([
   *     "LINE", "0,0", "5000,0", "",
   *     "CIRCLE", "2500,2500", "500",
   *     "TEXT", "100,100", "24", "0", "Hello",
   *   ]);
   */
  executeBatchCommands: (commands: string[]) => BatchCommandResult[];
  reset: () => void;
};

// STORE-SPLIT slice 5 — defaultObjectProperties / reconcileCurrentLayerAgainstTable /
// sessionUsers / spaces (initial-state seed fixtures + the current-layer reconcile
// guard) moved verbatim to editorStoreInitialState.ts (reconcileCurrentLayerAgainstTable
// imported above for the dispatch chokepoint). The `_entities` demo fixture below stays.

const _entities: EntityPreview[] = [
  {
    id: "line-main",
    label: t("demo.line.label"),
    type: "Line",
    spaceId: "model",
    summary: t("demo.line.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Thickness",
      "Length",
      "Angle",
    ],
    properties: {
      Layer: "A-AXIS",
      Color: "ByLayer",
      Linetype: "Center",
      Lineweight: "0.18 mm",
      Thickness: "0",
      "Start X": "120",
      "Start Y": "150",
      "Start Z": "0",
      "End X": "4620",
      "End Y": "150",
      "End Z": "0",
      Length: "4500",
      Angle: "0",
    },
  },
  {
    id: "circle-main",
    label: t("demo.circle.label"),
    type: "Circle",
    spaceId: "model",
    summary: t("demo.circle.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Thickness",
      "Center X",
      "Center Y",
      "Radius",
      "Diameter",
      "Area",
    ],
    properties: {
      Layer: "A-CURVE",
      Color: "ByLayer",
      Linetype: "Continuous",
      Lineweight: "ByLayer",
      Thickness: "0",
      "Center X": "840",
      "Center Y": "150",
      "Center Z": "0",
      Radius: "84",
      Diameter: "168",
      Circumference: "527.788",
      Area: "22167.078",
    },
  },
  {
    id: "arc-main",
    label: t("demo.arc.label"),
    type: "Arc",
    spaceId: "model",
    summary: t("demo.arc.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Thickness",
      "Center X",
      "Center Y",
      "Radius",
      "Start angle",
      "End angle",
      "Arc length",
    ],
    properties: {
      Layer: "A-CURVE",
      Color: "ByLayer",
      Linetype: "Continuous",
      Lineweight: "ByLayer",
      Thickness: "0",
      "Center X": "720",
      "Center Y": "260",
      "Center Z": "0",
      Radius: "130",
      "Start angle": "195",
      "End angle": "340",
      "Total angle": "145",
      "Arc length": "328.867",
    },
  },
  {
    id: "hatch-core",
    label: t("demo.hatch.label"),
    type: "Hatch",
    spaceId: "model",
    summary: t("demo.hatch.summary"),
    editableProperties: ["Layer", "Color", "Pattern", "Scale", "Associative"],
    properties: {
      Layer: "A-HATCH",
      Color: "ByLayer",
      Pattern: "ANSI31",
      Scale: "1",
      Associative: "Yes",
    },
  },
  {
    id: "block-assembly",
    label: t("demo.block.label"),
    type: "Block",
    spaceId: "model",
    summary: t("demo.block.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Name",
      "Insertion point",
      "Scale",
      "Rotation",
    ],
    properties: {
      Layer: "A-EQPM",
      Color: "ByLayer",
      Name: "PUMP_A",
      "Insertion point": "1400,240",
      Scale: "1,1,1",
      Rotation: "0",
    },
  },
  {
    id: "array-rack",
    label: t("demo.array.label"),
    type: "Rectangular Associative Array",
    spaceId: "model",
    summary: t("demo.array.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Rows",
      "Columns",
      "Row spacing",
      "Column spacing",
      "Associative",
    ],
    properties: {
      Layer: "A-GRID",
      Color: "ByLayer",
      Rows: "2",
      Columns: "3",
      "Row spacing": "450",
      "Column spacing": "650",
      Associative: "Yes",
    },
  },
  {
    id: "polyline-route",
    label: t("demo.polyline.label"),
    type: "LWPOLYLINE",
    spaceId: "model",
    summary: t("demo.polyline.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Constant width",
      "Closed",
    ],
    properties: {
      Layer: "A-ROUTE",
      Color: "ByLayer",
      Linetype: "Continuous",
      Lineweight: "ByLayer",
      "Constant width": "0",
      Closed: "No",
    },
  },
  {
    id: "text-grid",
    label: t("demo.text.label"),
    type: "Text",
    spaceId: "model",
    summary: t("demo.text.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Contents",
      "Text style",
      "Height",
      "Rotation",
      "Justify",
    ],
    properties: {
      Layer: "A-ANNO",
      Color: "ByLayer",
      Contents: "GRID A1",
      "Text style": "STANDARD",
      Height: "180",
      Rotation: "0",
      Justify: "Middle center",
    },
  },
  {
    id: "mtext-general-notes",
    label: t("demo.mtext.label"),
    type: "MText",
    spaceId: "model",
    summary: t("demo.mtext.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Contents",
      "Text style",
      "Height",
      "Width",
      "Background fill",
    ],
    properties: {
      Layer: "A-ANNO",
      Color: "ByLayer",
      Contents: "GENERAL NOTES\\PVERIFY ACCESS CLEARANCES\\PISSUE FOR REVIEW",
      "Text style": "STANDARD",
      Height: "160",
      Width: "1200",
      "Background fill": "Off",
    },
  },
  {
    id: "dimension-bay",
    label: t("demo.dimension.label"),
    type: "Dimension",
    spaceId: "model",
    summary: t("demo.dimension.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Dimension style",
      "Measurement",
      "Text override",
    ],
    properties: {
      Layer: "A-DIMS",
      Color: "ByLayer",
      "Dimension style": "ISO-25",
      Measurement: "4500",
      "Text override": "<>",
    },
  },
  {
    id: "viewport-primary",
    label: t("demo.viewport.label"),
    type: "Viewport",
    spaceId: "layout-1",
    summary: t("demo.viewport.summary"),
    editableProperties: ["Display lock", "Scale", "Layer", "Shade plot"],
    properties: {
      "Display lock": "Yes",
      Scale: "1:100",
      Layer: "A-VPORT",
      "Shade plot": "As displayed",
    },
  },
  {
    id: "revision-cloud-a",
    label: t("demo.revcloud.label"),
    type: "Revision Cloud",
    spaceId: "layout-2",
    summary: t("demo.revcloud.summary"),
    editableProperties: [
      "Layer",
      "Color",
      "Cloud style",
      "Approximate arc length",
    ],
    properties: {
      Layer: "A-REVCLOUD",
      Color: "Red",
      "Cloud style": "Normal",
      "Approximate arc length": "120",
    },
  },
  {
    id: "mleader-layout",
    label: t("demo.mleader.label"),
    type: "Multileader",
    spaceId: "layout-1",
    summary: t("demo.mleader.summary"),
    editableProperties: ["Layer", "Color", "Multileader style", "Attachment"],
    properties: {
      Layer: "A-ANNO",
      Color: "ByLayer",
      "Multileader style": "MLEAD_STANDARD",
      Attachment: "Top left",
    },
  },
];

// STORE-SPLIT slice 5 — buildInitialEditorShellState / buildInitialGeometryDocument /
// resolveHydratedCurrentUserEmail / hydrateEditorSessionShellState moved verbatim to
// editorStoreInitialState.ts (imported + re-exported above).

// Plan §8 Phase 2.6 (2026-05-19) — pure selectors extracted to
// `editorSelectors.ts` (see file header there for rationale). The
// re-exports below at the END of this section preserve every existing
// import path; this comment block stands in for the original function
// definitions that lived here for module-locality reference.

export function selectSelectedEntities(
  state: EditorShellState,
): EntityPreview[] {
  // Fast path — small-file imports keep `state.drawing.entities`
  // populated, so a flat scan is O(selection × entities) but both
  // halves are tiny.
  const fromShell = state.selection.entityIds
    .map((entityId) =>
      state.drawing.entities.find((entity) => entity.id === entityId),
    )
    .filter((entity): entity is EntityPreview => Boolean(entity));
  if (fromShell.length === state.selection.entityIds.length) {
    return fromShell;
  }
  // PR-V4 — huge-import path: `state.drawing.entities` was emptied by
  // SHELL_ENTITY_PREVIEW_THRESHOLD, but selection.entityIds may still
  // reference cache-stashed entities. Build EntityPreview on demand
  // for the few selected ids (typically 1–10) so the inspector /
  // properties palette never sees a phantom-empty selection just
  // because the reactive array is short.
  const document = useEditorStore.getState().geometryDocument;
  const dimStylesByName = new Map<string, DimensionStyle>();
  for (const s of document.styleTables?.dimensionStyles ?? []) {
    dimStylesByName.set(s.name, s);
  }
  const blockRecords = document.blockRecords ?? {};
  const blocks = document.blocks ?? {};
  const drawingInsunits = document.insunits ?? 0;
  const fromShellIds = new Set(fromShell.map((e) => e.id));
  const out: EntityPreview[] = [...fromShell];
  for (const id of state.selection.entityIds) {
    if (fromShellIds.has(id)) continue;
    const drawingEntity = getEntityById(id);
    if (!drawingEntity) continue;
    out.push(
      buildShellEntityPreview(
        drawingEntity,
        dimStylesByName,
        blockRecords,
        blocks,
        drawingInsunits,
      ),
    );
  }
  return out;
}

// Plan §8 Phase 2.6 (2026-05-19) — `selectMatchpropSourceEntity` and
// the three style-table selectors (`selectTextStyles`,
// `selectDimensionStyles`, `selectMultileaderStyles`) extracted to
// `editorSelectors.ts`. Empty-array sentinels (`emptyTextStyles`,
// `emptyDimensionStyles`, `emptyMultileaderStyles`) live there too.
//
// `selectChpropOptions` stays in this file because it reaches into
// the in-file `selectSelectedEntities` (which itself uses
// `useEditorStore.getState()` and intra-file helpers — moving that
// pair to editorSelectors.ts would form a circular import).
export function selectChpropOptions(state: EditorShellState): string[] {
  return getSharedPropertiesForSelection(
    selectSelectedEntities(state),
    "chprop",
  );
}

export function derivePropertiesView(
  state: EditorShellState,
): PropertiesViewModel {
  const selectedEntities = selectSelectedEntities(state);

  if (selectedEntities.length === 0) {
    return {
      title: t("props.default_title"),
      badge: t("props.no_selection"),
      note: t("props.default_note"),
      rows: Object.entries(state.propertyWorkflows.defaultObjectProperties).map(
        ([label, value]) => ({
          label,
          value,
        }),
      ),
    };
  }

  if (selectedEntities.length === 1) {
    const entity = selectedEntities[0];
    if (!entity) {
      throw new Error("derivePropertiesView: selectedEntities[0] missing");
    }

    // DEFUSE S16 — typed READ cutover. Source every descriptor-covered cell from
    // the typed `DrawingEntity` (resolved via `getEntityById` — the canonical
    // render-boundary resolver already used by MATCHPROP / Scene3DStage) instead
    // of the string bag; the residual + render-internal keys still come from the
    // bag. Output is byte-identical (pinned by `panelReadCutover.census.test.ts`);
    // `undefined` typed (shell-only draft preview) degrades to all-bag rows.
    const typedEntity = getEntityById(entity.id);
    return {
      title: entity.label,
      badge: entity.type,
      note: entity.summary,
      rows: buildTypedSingleSelectRows(entity, typedEntity).map(
        ({ label, value, readOnly }) => ({ label, value, readOnly }),
      ),
    };
  }

  const sharedProperties = getSharedPropertiesForSelection(
    selectedEntities,
    "properties",
  );
  // DEFUSE S16 remainder r1 — multi-select typed READ cutover (Р16.5 W1.1).
  // Mirrors the S16 single-select cutover above: for every shared property,
  // source EVERY selected entity's value from the typed `DrawingEntity` via
  // `resolveDisplay` when the WHOLE selection resolves a non-residual,
  // non-deferred descriptor for that label (the exact eligibility the
  // single-select cutover pins byte-identical to the bag) — otherwise the
  // WHOLE row stays bag-sourced (never a per-entity mix; see
  // `buildTypedMultiSelectPropertyValues`'s doc comment). `getEntityById` is
  // the canonical render-boundary typed resolver (same one MATCHPROP /
  // Scene3DStage use); an entity absent from the live doc (shell-only draft
  // preview) degrades that property to all-bag, same as single-select's
  // `typed === undefined` path.
  const multiSelectEntries = selectedEntities.map((entity) => ({
    preview: entity,
    typed: getEntityById(entity.id),
  }));
  const rows = buildTypedMultiSelectPropertyValues(
    multiSelectEntries,
    sharedProperties,
  ).map(({ label, values, readOnly }) => ({
    label,
    value: variedValue(values.map((value) => value ?? t("props.varies"))),
    readOnly,
  }));

  return {
    title: t("props.multi_title", { count: String(selectedEntities.length) }),
    badge: t("props.multi_select"),
    note: t("props.multi_note"),
    rows,
  };
}

function variedValue(values: string[]): string {
  const first = values[0];
  if (first === undefined) return t("props.varies");
  return values.every((value) => value === first) ? first : t("props.varies");
}

// CC-1c — `pushAuditEvent` is now the SHARED chokepoint in `reducerHelpers.ts`
// (imported below). The previous editorStore-local copy was a byte-for-byte
// clone of that helper; consolidating to one source of truth lets the typed
// `describeAuditOperation` params attach uniformly across every emitter (the
// shell sub-reducers already used the reducerHelpers copy). See the import of
// `pushAuditEvent` / `pushViewOpAuditEvent` near the sub-reducer imports.

function withLastSubmitted(
  state: EditorShellState,
  rawText: string,
): EditorShellState {
  return {
    ...state,
    commandLine: {
      ...state.commandLine,
      lastSubmitted: rawText,
    },
  };
}

/**
 * Fold a MSPACE/PSPACE command alias onto its canonical id so the exact-match
 * space-switch branches in `routeLayoutCommand` recognise every CLI alias the
 * layout descriptors declare (en `MS`/`PS`, ru `МПРОСТР`/`ПРОСТРМ`/`ПЛИСТ`/
 * `ЛИСТП`). Resolution goes through the command-registry `resolveAlias` (the
 * canonical CLI/`App.tsx` path) so the alias set is sourced from the
 * descriptors at runtime — no local mirror map to drift out of sync with
 * `packages/command-registry/src/commands/layout.ts`.
 *
 * `input` is expected pre-trimmed and upper-cased (as `routeLayoutCommand`
 * passes it). Anything that does not resolve to the MSPACE or PSPACE descriptor
 * is returned unchanged, so non-layout commands and the existing exact-match
 * literals ("MSPACE"/"PSPACE"/"MODEL"/"PROPERTIES"/…) keep their current
 * behaviour verbatim.
 */
function normalizeSpaceCommandAlias(input: string): string {
  const descriptor = resolveAlias(input, ALL_COMMAND_DESCRIPTORS);
  if (descriptor?.id === "MSPACE" || descriptor?.id === "PSPACE") {
    return descriptor.id;
  }
  return input;
}

/**
 * COLLAB-LOCK-COMMAND — report an async (or, for `LOCK LIST`, effectively
 * synchronous) collab-lock operation's EVENTUAL outcome to the command
 * line / audit feed. Reuses the SAME `pushAuditEvent` chokepoint every other
 * command's outcome flows through — invoked via `useEditorStore.setState`
 * (rather than the zustand creator's closed-over `set`) because this fires
 * from a call site OUTSIDE the `dispatch()` stack frame: a `.then()`
 * continuation after `routeLayoutCommand` already returned its synchronous
 * "submitted" acknowledgement state. This is NOT a third notification
 * channel — it is the SAME ring (`state.audit.events` /
 * `state.commandLine.transcript`) every other command writes into, exactly
 * mirroring how `dispatch()` itself already reaches back into
 * `useEditorStore` via `set`/`get` for its other side effects (e.g.
 * `nextCeColorToSync`, applied after `set` further down in this file).
 */
function reportCollabLockOutcome(
  command: EditorCommand,
  message: string,
  tone: "info" | "success" | "warning" | "error",
): void {
  useEditorStore.setState((s) => ({
    state: pushAuditEvent(s.state, command, message, tone),
  }));
}

/**
 * COLLAB-LOCK-COMMAND — shared LOCK/UNLOCK dispatch tail. Builds the typed
 * `collab/lock-request` / `collab/lock-release` command (producing the
 * immediate "submitted" acknowledgement via `reduceEditorShellState` →
 * `shellUiReducer.ts`'s audit-only case), then fires the ACTUAL async
 * `useCollabLockStore` network call as a side effect — reporting its
 * per-scope settle (or a "not connected" warning on rejection) via
 * `reportCollabLockOutcome` once it resolves. Never blocks the synchronous
 * return: the caller's "submitted" state is returned immediately, matching
 * every other async-outcome command in this file.
 *
 * GitHub Codex review (PR #534, round 2) P3 — "Attribute lock outcomes to
 * collab commands". `reportCollabLockOutcome` derives the audit row's
 * `category`/`op` from whatever command it is handed
 * (`describeAuditOperation` → `categoryForCommandType(command.type)`). The
 * async outcome callbacks below used to pass the ORIGINAL raw `command`
 * (the CLI's `commandline/submit` wrapper) straight through — only the
 * immediate "submitted" ack (dispatched via `reduceEditorShellState` right
 * above) used the synthesized `collab/lock-request`/`collab/lock-release`
 * command. So every genuinely informative outcome row (grant/deny/release,
 * the ones an admin/audit viewer actually cares about) was attributed to
 * `category: "commandline"`, not the new `"collab"` category
 * (`auditPayloadPresentation.ts`'s `AUDIT_CATEGORY_LABEL_KEYS`) — only the
 * placeholder ack got it right. Both call sites now share ONE synthesized
 * `collabCommand` (built once, same shape `reduceEditorShellState` already
 * used) for the ack AND every async outcome report.
 */
function dispatchCollabLockCommand(
  state: EditorShellState,
  command: EditorCommand,
  isUnlock: boolean,
  scopes: LockScope[],
): EditorShellState {
  const collabCommand: EditorCommand = {
    type: isUnlock ? "collab/lock-release" : "collab/lock-request",
    source: command.source,
    scopes,
  };
  const ackState = reduceEditorShellState(state, collabCommand);
  const store = useCollabLockStore.getState();
  if (isUnlock) {
    store
      .releaseLocks(scopes)
      .then((outcomes) => {
        const lines = scopes.map((scope) => {
          const confirmed = outcomes.get(lockScopeKey(scope)) ?? false;
          return confirmed
            ? t("collab.lock.release_confirmed", { target: scope.targetId })
            : t("collab.lock.release_no_effect", { target: scope.targetId });
        });
        reportCollabLockOutcome(collabCommand, lines.join(" "), "info");
      })
      .catch((err: unknown) => {
        console.warn("collab-lock: releaseLocks failed", err);
        reportCollabLockOutcome(collabCommand, t("collab.lock.not_connected"), "warning");
      });
  } else {
    store
      .requestLocks(scopes)
      .then((outcomes) => {
        let anyDenied = false;
        const lines = scopes.map((scope) => {
          const outcome = outcomes.get(lockScopeKey(scope));
          if (outcome === undefined) {
            anyDenied = true;
            return t("collab.lock.denied", { target: scope.targetId, owner: "?" });
          }
          if (outcome.status === "granted") {
            return t("collab.lock.granted", {
              target: outcome.lock.scope.targetId,
              owner: outcome.lock.ownerName,
            });
          }
          anyDenied = true;
          // P3 (post-review, round 5) — `LockDenyReason` is a typed union
          // (`"held-by-other" | "role-cannot-lock" | "lock-limit"`), not a
          // bare bool — only `"held-by-other"` carries a real `heldBy` owner
          // to name. Reusing `collab.lock.denied` (which ALWAYS formats an
          // owner) for the other two reasons produced a misleading "already
          // locked by ?" when there was, in fact, no owner to blame — the
          // requester's OWN role or lock quota was the actual reason. Branch
          // on `reason` so each denial reports its true cause.
          if (outcome.reason === "role-cannot-lock") {
            return t("collab.lock.denied_role");
          }
          if (outcome.reason === "lock-limit") {
            return t("collab.lock.denied_limit");
          }
          return t("collab.lock.denied", {
            target: outcome.scope.targetId,
            owner: outcome.heldBy?.ownerName ?? "?",
          });
        });
        reportCollabLockOutcome(collabCommand, lines.join(" "), anyDenied ? "warning" : "success");
      })
      .catch((err: unknown) => {
        console.warn("collab-lock: requestLocks failed", err);
        reportCollabLockOutcome(collabCommand, t("collab.lock.not_connected"), "warning");
      });
  }
  return ackState;
}

function routeLayoutCommand(
  state: EditorShellState,
  rawText: string,
  command: EditorCommand,
): EditorShellState {
  const trimmedText = rawText.trim();
  // MSPACE/PSPACE command-alias normalization. The space-switch branches below
  // match the canonical command ids exactly (`=== "MSPACE"` / `=== "PSPACE"`),
  // but the layout descriptors declare additional CLI aliases — MSPACE: en
  // [MS], ru [МПРОСТР, ПРОСТРМ]; PSPACE: en [PS], ru [ПЛИСТ, ЛИСТП]. Without
  // expansion those aliases fall through to the unknown-command path. We resolve
  // the uppercased token against the registry (the SAME `resolveAlias` path the
  // CLI/App.tsx use — single source of truth, zero drift with layout.ts) and,
  // when it resolves to the MSPACE or PSPACE descriptor, fold it onto the
  // canonical id. Other commands (and unknown text) are left untouched, so the
  // existing exact-match behaviour for "MSPACE"/"PSPACE"/"MODEL"/etc. is
  // preserved verbatim (those still resolve to their own id).
  const normalizedText = normalizeSpaceCommandAlias(trimmedText.toUpperCase());
  const stateWithLastSubmitted = withLastSubmitted(state, rawText);

  // Bare-command-entry audit-tone fix — a caller (`useDraftSession
  // .beginDrafting`'s "<command> armed in <space>." confirmation, dispatched
  // the moment the REAL interactive command line starts a draft session) can
  // mark `text` as an ALREADY-RESOLVED status message via `resolvedTone`
  // instead of raw command-line input. Honour it FIRST, before any
  // text-shape guess below (PROPERTIES/MODEL/MSPACE/…, COLOR, VPCLIP, the
  // LINE/POINT/CIRCLE/ARC text resolver, the not-wired fallback) gets a
  // chance to misinterpret it — none of those are meant to run against text
  // that is not a user-submitted candidate command string at all. Recording
  // it verbatim with the supplied tone and returning immediately is what
  // keeps a session-start confirmation like "LINE armed in Model." from
  // being reparsed as malformed LINE coordinates and stamped `error` (see
  // the schema doc comment on `commandline/submit.resolvedTone`,
  // packages/contracts/src/editor.ts). Absent `resolvedTone` (the default)
  // falls through unchanged — a real scripted/typed submission is still
  // resolved exactly as before, INCLUDING a genuinely malformed one still
  // getting the `error` tone.
  if (command.type === "commandline/submit" && command.resolvedTone) {
    return pushAuditEvent(
      stateWithLastSubmitted,
      command,
      trimmedText,
      command.resolvedTone,
    );
  }

  if (normalizedText === "PROPERTIES" || normalizedText === "CTRL+1") {
    return pushAuditEvent(
      {
        ...stateWithLastSubmitted,
        ui: {
          activePanel: "properties",
        },
      },
      command,
      t("audit.properties_shell_activated"),
      "success",
    );
  }

  if (normalizedText === "MODEL") {
    return pushAuditEvent(
      {
        ...stateWithLastSubmitted,
        drawing: {
          ...state.drawing,
          activeSpaceId: "model",
          layoutContext: "model",
        },
      },
      command,
      t("audit.model_space_activated"),
      "success",
    );
  }

  if (normalizedText === "MSPACE") {
    return pushAuditEvent(
      {
        ...stateWithLastSubmitted,
        drawing: {
          ...state.drawing,
          activeSpaceId:
            state.drawing.activeSpaceId === "model"
              ? "layout-1"
              : state.drawing.activeSpaceId,
          layoutContext: "viewport",
        },
      },
      command,
      t("audit.mspace_activated"),
      "success",
    );
  }

  if (normalizedText === "PSPACE") {
    return pushAuditEvent(
      {
        ...stateWithLastSubmitted,
        drawing: {
          ...state.drawing,
          activeSpaceId:
            state.drawing.activeSpaceId === "model"
              ? "layout-1"
              : state.drawing.activeSpaceId,
          layoutContext: "paper",
        },
      },
      command,
      t("audit.pspace_activated"),
      "success",
    );
  }

  if (normalizedText === "MATCHPROP SOURCE") {
    if (state.selection.entityIds.length !== 1) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("error.matchprop_source_requires_one"),
        "warning",
      );
    }

    const sourceEntityId = state.selection.entityIds[0];
    if (!sourceEntityId) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("error.matchprop_source_requires_one"),
        "warning",
      );
    }
    return reduceEditorShellState(stateWithLastSubmitted, {
      type: "matchprop/set-source",
      source: command.source,
      entityId: sourceEntityId,
    });
  }

  const chpropMatch = /^CHPROP\s+([^=]+?)\s*=\s*(.+)$/i.exec(trimmedText);

  if (chpropMatch) {
    if (state.selection.entityIds.length === 0) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("error.chprop_requires_selection"),
        "warning",
      );
    }

    const chpropProperty = chpropMatch[1]?.trim();
    const chpropValue = chpropMatch[2]?.trim();
    if (chpropProperty === undefined || chpropValue === undefined) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("error.chprop_requires_selection"),
        "warning",
      );
    }
    return reduceEditorShellState(stateWithLastSubmitted, {
      type: "chprop/apply",
      source: command.source,
      entityIds: state.selection.entityIds,
      property: chpropProperty,
      value: chpropValue,
    });
  }

  const matchpropModeMatch = /^MATCHPROP(?:\s+(BASIC|SPECIAL|ALL))?$/i.exec(
    trimmedText,
  );

  if (matchpropModeMatch) {
    if (state.selection.entityIds.length === 0) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("error.matchprop_requires_targets"),
        "warning",
      );
    }

    return reduceEditorShellState(stateWithLastSubmitted, {
      type: "matchprop/apply",
      source: command.source,
      targetEntityIds: state.selection.entityIds,
      mode:
        matchpropModeMatch[1]?.toLowerCase() === "special"
          ? "special"
          : matchpropModeMatch[1]?.toLowerCase() === "basic"
            ? "basic"
            : "all",
    });
  }

  // Phase MC1 — LAYOUT (bare): list available model/layout spaces.
  // The switchable-tab set (Model + every paper-space layout, synthetic
  // block-edit / refedit spaces excluded) is resolved by the SHARED
  // `listSwitchableSpaces` — the SAME set the `LAYOUT <name>` switch
  // below resolves against, so the command can never advertise a name it
  // then refuses to accept.
  if (normalizedText === "LAYOUT") {
    const list = listSwitchableSpaces(state.drawing.spaces)
      .map((s) => s.name)
      .join(", ");
    return pushAuditEvent(
      stateWithLastSubmitted,
      command,
      t("audit.layout_list", { list }),
      "info",
    );
  }

  // ── LAYOUT SET <name> — AutoCAD LAYOUT `Set` option (activate by name) ──
  // AutoCAD parity: `LAYOUT` → `Set` → layout name makes that paper-space
  // layout the current/active space. We accept `LAYOUT SET <name>` and the
  // `S` abbreviation (EN), plus the МАКЕТ/РАЗМЛИСТ command names with the
  // УСТАН/У sub-keyword (RU). The name is resolved case-insensitively
  // (case-sensitive first, then a forgiving CI fallback) — mirroring the
  // LAYOUT RENAME resolver in App.tsx.
  //
  // AutoCAD's LAYOUT Set operates on PAPER-space layouts only — the Model
  // tab is not selectable via this option — so the lookup is scoped to
  // `kind === "layout"` spaces (which also excludes synthetic block-edit /
  // refedit spaces projected as kind:"model"). A name that resolves to no
  // paper-space layout is a fail-closed no-op with a warning, matching how
  // RENAME rejects an unknown name.
  //
  // Activation reuses the CANONICAL `shell/activate-space` command (the same
  // command the layout tab strip dispatches) — single source of truth for
  // `activeSpaceId` + `layoutContext`, no parallel activation path. Setting
  // the already-current layout is idempotent: a true no-op when already in
  // paper context, or a paper-context restore (via shell/set-layout-context)
  // when the user is currently inside a viewport (MSPACE) on that same
  // layout — exactly mirroring App.tsx's `activateSpace()` helper.
  const layoutSetMatch =
    /^\s*(?:-?LAYOUT|МАКЕТ|РАЗМЛИСТ)\s+(?:SET|S|УСТАН|У)\s+(.+?)\s*$/iu.exec(
      trimmedText,
    );

  if (layoutSetMatch && layoutSetMatch[1]) {
    const requestedName = layoutSetMatch[1].trim();
    // Shared one-truth resolver (`spaceMutationGuards.ts`) — the SAME
    // exact-then-case-insensitive lookup LAYOUT DELETE runs, so SET and
    // DELETE can never disagree about which tab a name denotes.
    const target = findLayoutSpaceByName(state.drawing.spaces, requestedName);

    if (!target) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("layout.rename.notfound", { name: requestedName }),
        "warning",
      );
    }

    if (target.id === state.drawing.activeSpaceId) {
      // Already the current layout. In paper context this is a pure no-op;
      // if the user is inside a viewport (MSPACE) on that layout, restore
      // paper context through the canonical set-layout-context command.
      if (state.drawing.layoutContext === "paper") {
        return pushAuditEvent(
          stateWithLastSubmitted,
          command,
          t("layout.set.already_current", { name: target.name }),
          "info",
        );
      }
      return reduceEditorShellState(stateWithLastSubmitted, {
        type: "shell/set-layout-context",
        source: command.source,
        layoutContext: "paper",
      });
    }

    return reduceEditorShellState(stateWithLastSubmitted, {
      type: "shell/activate-space",
      source: command.source,
      spaceId: target.id,
      layoutContext: "paper",
    });
  }

  // ── LAYOUT NEW [name] / LAYOUT DELETE [name] — LAYOUT-COMMAND-HANDLERS ──
  // AutoCAD parity:
  //   - NEW creates an EMPTY paper-space layout (default name "Layout<N>",
  //     next free N — mirroring the existing tab naming convention) and
  //     does NOT activate it (AutoCAD stays on the current tab);
  //   - DELETE removes a layout by name (case-insensitive lookup like SET;
  //     bare `LAYOUT D` defaults to the CURRENT layout — AutoCAD's prompt
  //     default); the Model tab and the last remaining layout are
  //     protected, and deleting the active layout activates the adjacent
  //     remaining tab.
  // Parsing + name/id resolution live in `spaceMutationGuards.ts` — the
  // SAME deterministic helpers `resolveGeometryDocumentCommand` runs for
  // the geometry-document arm of this dispatch, so both arms mint the
  // same space id / resolve the same target. The typed shell command is
  // re-entered through `reduceEditorShellState` (mirrors the SET arm) and
  // validated by the shared guards inside `shellUiReducer.ts`.
  // History note (B2-r9): NEW/DELETE ship NON-invertible by design —
  // cmd-layout·history is deferred to LAYOUT-MANAGE-UI-HIST.
  const layoutMutationParse = parseLayoutMutationText(trimmedText);
  if (layoutMutationParse) {
    const resolution = resolveLayoutMutationCommand(
      state.drawing.spaces,
      state.drawing.activeSpaceId,
      layoutMutationParse,
    );
    if (!resolution.ok) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        resolution.errorKey === "model"
          ? t("layout.delete.cannot_delete_model")
          : resolution.errorKey === "toolong"
            ? // Codex P2-4 — overlong (>255 char) LAYOUT NEW name is
              // rejected in the resolver BEFORE the synthetic command is
              // minted; mirror the rejection as a user-facing warning.
              t("layout.name.too_long")
            : t("layout.rename.notfound", { name: resolution.name }),
        "warning",
      );
    }
    return reduceEditorShellState(stateWithLastSubmitted, {
      ...resolution.command,
      source: command.source,
    });
  }

  // ── LAYOUT <name> — AutoCAD's DEFAULT option at the LAYOUT prompt ──
  // Autodesk LAYOUT: `Enter layout option
  // [Copy/Delete/New/Template/Rename/SAveas/Set/?] <set>:` — the option
  // in angle brackets is the DEFAULT, so a bare name entered at the
  // LAYOUT prompt means "make that tab current". This branch is that
  // default: `LAYOUT АБИ 1` == `LAYOUT SET АБИ 1`, plus the Model tab
  // (bare `LAYOUT` lists Model alongside the layouts, and the tab strip
  // renders it, so a name taken from that list must be accepted; the
  // strict `LAYOUT SET` option above stays paper-space-only per its own
  // AutoCAD parity rule and its pinned tests).
  //
  // WHY THIS EXISTS: the previous implementation matched the literal
  // token `LAYOUT-<digits>` and switched to `token.toLowerCase()` as the
  // space ID. Real drawings name their tabs — `apps/web/public/
  // abi1-test.dxf` carries `Model` / `t_sheet` / `АБИ 1` / `КРОНА`,
  // whose ids are `model` / `layout:t_sheet` / `layout:АБИ 1` /
  // `layout:КРОНА` (minted as `layout:${name}` in
  // `dxfBridge.spaces.ts`, PRESERVING the tab name's case). So no
  // user-visible name reached the command at all, and `name.toLowerCase()`
  // is never the id. Resolution now goes through the shared
  // `findSwitchableSpaceByName` (name exact -> name CI -> id exact ->
  // id CI), which keeps the historical by-id `LAYOUT LAYOUT-1` form
  // working while making every REAL tab name reachable.
  //
  // Option keywords never reach here (`parseLayoutSwitchText` filters
  // them), so `LAYOUT R Old New`, `LAYOUT ?` and the unimplemented
  // Copy/Template/SAveas options keep their existing handling instead of
  // being misread as a tab name.
  //
  // Activation reuses the CANONICAL `shell/activate-space` command (the
  // same command the tab strip and LAYOUT SET dispatch) — one source of
  // truth for `activeSpaceId` + `layoutContext`, no parallel mutation.
  // `layoutContext` follows the TARGET's kind: "paper" for a layout,
  // "model" for the Model tab (`shell/activate-space` pins Model to the
  // model context itself). Re-selecting the already-current tab is
  // idempotent: a true no-op when the context already matches, or a
  // context restore (via `shell/set-layout-context`) when the user is
  // inside a viewport (MSPACE) on that same layout — mirroring the SET
  // arm above.
  const requestedSwitchName = parseLayoutSwitchText(trimmedText);

  if (requestedSwitchName !== null) {
    const target = findSwitchableSpaceByName(
      state.drawing.spaces,
      requestedSwitchName,
    );

    if (!target) {
      // Fail-closed, never a silent no-op: name the tabs that DO exist
      // (the same list bare `LAYOUT` prints) so a typo is self-correcting.
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("layout.switch.notfound", {
          name: requestedSwitchName,
          list: listSwitchableSpaces(state.drawing.spaces)
            .map((space) => space.name)
            .join(", "),
        }),
        "warning",
      );
    }

    if (target.id === state.drawing.activeSpaceId) {
      // Re-selecting the CURRENT tab. The Model tab has exactly one
      // context ("model"), so that is always a pure no-op. A layout can
      // be current in `paper` OR `viewport` (the user double-clicked into
      // a floating viewport — MSPACE), and AutoCAD's Set pulls focus back
      // to paper there; `shell/set-layout-context` is the canonical
      // command for that half-step (it takes paper/viewport only).
      if (target.kind === "layout" && state.drawing.layoutContext !== "paper") {
        return reduceEditorShellState(stateWithLastSubmitted, {
          type: "shell/set-layout-context",
          source: command.source,
          layoutContext: "paper",
        });
      }
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("layout.set.already_current", { name: target.name }),
        "info",
      );
    }

    return reduceEditorShellState(stateWithLastSubmitted, {
      type: "shell/activate-space",
      source: command.source,
      spaceId: target.id,
      // `shell/activate-space` pins the Model tab to the model context
      // itself; a paper-space layout takes "paper".
      layoutContext: target.kind === "layout" ? "paper" : "model",
    });
  }

  // ── LOCK / UNLOCK (COLLAB-LOCK-COMMAND) — soft-lock a selected entity or a
  // named layer so OTHER real-time collaborators cannot edit it. AutoCAD has
  // no direct LOCK/UNLOCK CLI analogue (realtime collaboration is a
  // browser-native extension of this editor) — the EN/RU keyword-casing +
  // spacing rules mirror LAYOUT's (МАКЕТ) established convention. Forms:
  //   LOCK / БЛОКИР                          — lock every selected entity
  //   LOCK LAYER <name> / БЛОКИР СЛОЙ <name> — lock a named layer
  //   LOCK LIST / LOCK ? / БЛОКИР СПИСОК     — list currently held locks
  //   UNLOCK / РАЗБЛОК                       — release every selected entity's lock
  //   UNLOCK LAYER <name> / РАЗБЛОК СЛОЙ <name> — release a named layer's lock
  // The layer name is resolved case-insensitively via `drawingObjectGraph.
  // getLayer` — the SAME layer-by-name accessor other read paths use (see
  // the import site above); an unknown name is the same fail-closed no-op-
  // with-warning pattern LAYOUT SET/DELETE use for an unknown layout name.
  const lockLayerMatch =
    /^\s*(LOCK|БЛОКИР|UNLOCK|РАЗБЛОК)\s+(?:LAYER|СЛОЙ)\s+(.+?)\s*$/iu.exec(
      trimmedText,
    );
  const lockListMatch = /^\s*(?:LOCK|БЛОКИР)\s+(?:LIST|СПИСОК|\?)\s*$/iu.exec(
    trimmedText,
  );
  const bareLockMatch = /^\s*(LOCK|БЛОКИР|UNLOCK|РАЗБЛОК)\s*$/iu.exec(
    trimmedText,
  );

  if (lockListMatch) {
    // Reduced through the typed `collab/list-locks` command for the audit-
    // taxonomy/reducer coverage every CLI command gets. The REAL listing
    // content (this is a SYNCHRONOUS local-snapshot read, no network round
    // trip) is pushed DIRECTLY onto that ack state below and returned as ONE
    // combined state — a real informative message, never a silent no-op,
    // satisfying the command's own "no тихий no-op" requirement independent
    // of the fail-closed EDIT-veto gate (that gate lives in `dispatch()`,
    // further down this file).
    //
    // P2 #1 (post-review, round 3) — this branch runs SYNCHRONOUSLY inside
    // `dispatch()`'s own `set((store) => {...})` updater (unlike the
    // LOCK/UNLOCK async `.then()` case in `dispatchCollabLockCommand`, which
    // genuinely fires AFTER `dispatch()` has already returned). Reporting the
    // outcome via `reportCollabLockOutcome`'s OWN nested
    // `useEditorStore.setState(...)` — as this branch used to — writes the
    // real message WHILE the outer updater is still running, and that write
    // is then silently DISCARDED the moment the outer updater finally
    // returns its own (older, message-less) state object: zustand merges
    // that returned object over the nested write. So this branch computes
    // the outcome message FIRST, then folds it onto `ackState` with a
    // SINGLE `pushAuditEvent` call and returns THAT directly — matching
    // every other synchronous CLI branch in this file (e.g.
    // `layout.rename.notfound` above). Do NOT route this case through
    // `reportCollabLockOutcome` — that helper's nested `setState` is correct
    // ONLY for the genuinely-async LOCK/UNLOCK `.then()` path.
    // GitHub Codex review (PR #534, round 3) P3 — "Use the collab command
    // for LOCK LIST results". Saved to a variable (was previously an inline
    // object literal handed straight to `reduceEditorShellState` and
    // discarded) so the SAME synthesized `collab/list-locks` command backs
    // BOTH the immediate ack AND the informative result row below — mirrors
    // `dispatchCollabLockCommand`'s own `collabCommand` fix (round 2, P3)
    // for the async LOCK/UNLOCK outcome path. Before this, `pushAuditEvent`
    // below was handed the raw `command` (the original `commandline/submit`
    // wrapper carrying the literal "LOCK LIST" text), so `describeAuditOperation`
    // derived `category: "commandline"` for the actual list contents —
    // the one row a `LOCK LIST` user cares about — leaving it in the same
    // generic bucket as unrelated CLI submissions instead of the `collab`
    // category this PR's audit taxonomy change added.
    const collabListLocksCommand: EditorCommand = {
      type: "collab/list-locks",
      source: command.source,
    };
    const ackState = reduceEditorShellState(stateWithLastSubmitted, collabListLocksCommand);
    const collabState = useCollabLockStore.getState();
    let message: string;
    let tone: "info" | "warning";
    if (collabState.status !== "connected") {
      message = t("collab.lock.not_connected");
      tone = "warning";
    } else if (collabState.locks.size === 0) {
      message = t("collab.lock.list_empty");
      tone = "info";
    } else {
      message = [...collabState.locks.values()]
        .map((lock) =>
          t("collab.lock.list_item", {
            kind: lock.scope.kind,
            target: lock.scope.targetId,
            owner: lock.ownerName,
          }),
        )
        .join("; ");
      tone = "info";
    }
    return pushAuditEvent(ackState, collabListLocksCommand, message, tone);
  }

  if (lockLayerMatch && lockLayerMatch[1] && lockLayerMatch[2]) {
    const isUnlock =
      lockLayerMatch[1].toUpperCase() === "UNLOCK" ||
      lockLayerMatch[1].toUpperCase() === "РАЗБЛОК";
    const requestedLayerName = lockLayerMatch[2].trim();
    // P2 (post-review, round 5) — UNLOCK must be able to release a lock
    // whose layer no longer resolves by name (renamed or deleted since the
    // LOCK). Both LOCK and UNLOCK previously resolved `requestedLayerName`
    // through `drawingObjectGraph.getLayer` at COMMAND TIME, using the
    // CURRENT layer table — but the server-side lock's `targetId` is the
    // RAW name it was locked under, unaffected by a later client-side
    // rename/delete of that layer record. `UNLOCK LAYER <old name>` on a
    // vanished layer therefore failed the `getLayer` lookup and could NEVER
    // release the orphaned server-side lock, wedging it until the owner's
    // tab disconnects. LOCK still REQUIRES a live layer (you can only lock a
    // layer that currently exists — AutoCAD parity); UNLOCK skips the
    // existence check entirely and releases by the RAW typed name — the
    // release protocol is idempotent/self-correcting (the companion's
    // `handleLockRelease` silently no-ops if the caller does not actually
    // own that scope), so there is no new safety risk in attempting a
    // release against a name that does not resolve to a live layer.
    if (isUnlock) {
      // P2 (post-review, round 6) — a STILL-LIVE layer must release by its
      // CANONICAL `layer.name` (the exact string `LOCK LAYER` stored as the
      // scope's `targetId`, resolved case-insensitively via `getLayer`), not
      // the raw text the user just typed. `lockScopeKey`/the companion's
      // `LockManager.release` are EXACT-STRING matches (no case folding —
      // see `packages/collab-protocol/src/lockScope.ts`), so `LOCK LAYER
      // Walls` followed by `UNLOCK LAYER walls` previously sent a DIFFERENT
      // scope (`layer:walls` vs the held `layer:Walls`), silently no-opping
      // server-side and wedging the user's own lock until disconnect. Only
      // when the layer no longer resolves at all (renamed/deleted since the
      // LOCK — the round-5 fix this preserves) do we fall back to releasing
      // by the RAW typed name; that release remains idempotent/self-
      // correcting (the companion silently no-ops a release the caller does
      // not own), so there is no new safety risk in attempting it.
      const liveLayer = drawingObjectGraph.getLayer(requestedLayerName);
      // GitHub Codex review (PR #534, round 3) P2 — "Release the originally
      // held layer scope after recases". `liveLayer.name` is the layer's
      // CURRENT canonical casing — always, unconditionally — even when the
      // ONLY reason it differs from the held lock's scope is that the SAME
      // user (a self-owned edit `evaluateCollabLockVeto` allows through) did
      // a case-only rename AFTER locking it: `LOCK LAYER Walls` then a
      // self-rename to "walls" leaves the round-6 fix above always resolving
      // to "walls" — `UNLOCK LAYER Walls` and `UNLOCK LAYER walls` BOTH now
      // send `targetId: "walls"`, and the owner has no way to type the raw
      // OLD spelling anymore (`getLayer` always resolves to the new
      // canonical name), so the held `layer:Walls` scope can never be
      // targeted again and wedges until disconnect. Search the CLIENT'S OWN
      // live lock snapshot for a SELF-OWNED layer lock whose scope
      // case-insensitively matches either the raw typed name or the layer's
      // current name — if one exists, release using ITS exact `targetId`
      // (the literal string the scope is actually keyed under server-side),
      // not a freshly re-derived name that may no longer match. Falls back
      // to the round-6 behaviour (current canonical name, or the round-5 raw
      // fallback for a vanished layer) when no matching self-owned lock is
      // held — e.g. UNLOCKing a layer this participant never locked, which
      // stays a harmless idempotent no-op exactly as before.
      const unlockCollabState = useCollabLockStore.getState();
      const unlockCandidateNames = new Set(
        [requestedLayerName, liveLayer?.name].filter(
          (name): name is string => name !== undefined,
        ),
      );
      let releaseTargetId = liveLayer ? liveLayer.name : requestedLayerName;
      for (const lock of unlockCollabState.locks.values()) {
        if (
          lock.scope.kind === "layer" &&
          lock.ownerId === unlockCollabState.selfParticipantId &&
          [...unlockCandidateNames].some(
            (name) => name.toLowerCase() === lock.scope.targetId.toLowerCase(),
          )
        ) {
          releaseTargetId = lock.scope.targetId;
          break;
        }
      }
      const scopes: LockScope[] = [{ kind: "layer", targetId: releaseTargetId }];
      return dispatchCollabLockCommand(
        stateWithLastSubmitted,
        command,
        isUnlock,
        scopes,
      );
    }
    const layer = drawingObjectGraph.getLayer(requestedLayerName);
    if (!layer) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("collab.lock.layer_notfound", { name: requestedLayerName }),
        "warning",
      );
    }
    const scopes: LockScope[] = [{ kind: "layer", targetId: layer.name }];
    return dispatchCollabLockCommand(
      stateWithLastSubmitted,
      command,
      isUnlock,
      scopes,
    );
  }

  if (bareLockMatch && bareLockMatch[1]) {
    const isUnlock =
      bareLockMatch[1].toUpperCase() === "UNLOCK" ||
      bareLockMatch[1].toUpperCase() === "РАЗБЛОК";
    if (state.selection.entityIds.length === 0) {
      return pushAuditEvent(
        stateWithLastSubmitted,
        command,
        t("collab.lock.no_selection"),
        "warning",
      );
    }
    // P2 (post-review, round 5) — `state.selection.entityIds` is not itself
    // deduped, so a caller-side duplicate here would previously fail the
    // WHOLE `requestLocks()` batch (see `CollabLockClient.requestLocks()`'s
    // doc comment: a same-call duplicate scope used to synchronously reject
    // its own first promise). Fixed ONCE at the shared choke point
    // (`CollabLockClient.requestLocks()` / `releaseLocks()` now dedupe by
    // `lockScopeKey` before sending) rather than here, so no change is
    // needed at this call site beyond this cross-reference.
    const scopes: LockScope[] = state.selection.entityIds.map(
      (id): LockScope => ({ kind: "entity", targetId: id }),
    );
    return dispatchCollabLockCommand(
      stateWithLastSubmitted,
      command,
      isUnlock,
      scopes,
    );
  }

  // COLOR / -COLOR / SETCOLOR / ЦВЕТ <value> (EPIC-COLOR-UNION round-6 FINDING C)
  // — the value-form COLOR command is resolved on the GEOMETRY arm
  // (`storeHelpers.resolveGeometryDocumentCommand` → `header/set-cecolor`, which
  // mutates `doc.cecolor`). The shell arm (this function) ran FIRST and, lacking
  // a COLOR branch, fell through to the generic `command_not_wired` warning — so a
  // batch / CLI `COLOR 5` succeeded (cecolor mutated) yet the audit/feed claimed
  // it was not wired. Recognize the SAME value-form here (shared `parseColorSetCommand`
  // detector) and emit a success audit so the feed matches the real mutation. The
  // document mutation stays owned by the geometry arm — this branch is audit-only,
  // exactly like the other create/modify shell branches. A bare `COLOR`/`-COLOR`
  // (no value, UI-only picker/prompt) returns null and falls through unchanged.
  const shellColorSetParse = parseColorSetCommand(trimmedText);
  if (shellColorSetParse) {
    return pushAuditEvent(
      stateWithLastSubmitted,
      command,
      t("audit.cecolor_set", { color: describeColor(shellColorSetParse) }),
      "success",
    );
  }

  // VPCLIP / ВЭКРЕЗ <vp> [P pts | D | O objId | pts] (@codex #496 P2, round-2)
  // — the scripted VPCLIP form is resolved on the GEOMETRY arm
  // (`resolveGeometryDocumentCommand` → typed `geometry/viewport-clip-*`, applied
  // by the geometry reducer). The shell arm (this function) ran FIRST and, lacking
  // a VPCLIP branch, fell through to the generic `command_not_wired` warning — so a
  // batch / AI `VPCLIP …` clipped the viewport yet the audit/feed claimed it was
  // NOT wired AND omitted the viewport success audit (mirrors the COLOR FINDING C
  // seam directly above). Recognize the SAME scripted form here via the shared
  // one-truth `parseVpclipCommandText` (the parser the interactive
  // `submitCommandText` and the geometry arm both use) and RE-ENTER
  // `reduceEditorShellState` with the RESOLVED typed command so it reaches the
  // canonical `reduceViewportEditShellAudit` emitter — the IDENTICAL viewport
  // success audit (and no-op mutated-gate) the interactive typed dispatch
  // produces. This re-entry is AUDIT-ONLY: the viewport-clip shell path never
  // mutates the geometry document (that stays owned by the geometry arm), exactly
  // like the LAYOUT / MATCHPROP re-entries above. A bare / unparsable `VPCLIP`
  // returns null and falls through unchanged.
  const shellVpclipCommand = parseVpclipCommandText(trimmedText, command.source);
  if (shellVpclipCommand) {
    return reduceEditorShellState(stateWithLastSubmitted, shellVpclipCommand);
  }

  const geometryCommandResult = resolveGeometryCommandFromText(
    state,
    trimmedText,
    command.source,
  );

  if (geometryCommandResult.error) {
    // ТЗ §14 "errors": an unparseable command argument (e.g. "LINE expects
    // `x,y x,y` coordinates.") is a genuine PARSE ERROR, not a soft warning.
    // Emit it with the `error` tone so it renders as a red command-feed row
    // AND the CC-1a transport projects it to a `status:"error"` telemetry
    // record (the message becomes the server-side error-aggregation key). This
    // is the production site that exercises the error-status audit path.
    return pushAuditEvent(
      stateWithLastSubmitted,
      command,
      geometryCommandResult.error,
      "error",
    );
  }

  if (geometryCommandResult.command) {
    return reduceEditorShellState(
      stateWithLastSubmitted,
      geometryCommandResult.command,
    );
  }

  return pushAuditEvent(
    stateWithLastSubmitted,
    command,
    t("audit.command_not_wired", { text: trimmedText }),
    "warning",
  );
}

export function reduceEditorShellState(
  state: EditorShellState,
  rawCommand: EditorCommand | DynamicBlockCommand,
  geometryDocument?: DrawingDocument,
): EditorShellState {
  // Route DYN commands through their own parser; everything else through
  // the main editorCommandSchema. We keep them separate to avoid pushing
  // the main discriminatedUnion past TypeScript's inference complexity
  // budget (which manifests as `any`-widening in the zustand creator).
  const command: EditorCommand | DynamicBlockCommand =
    rawCommand.type === "dynamic/list" ||
    rawCommand.type === "dynamic/set" ||
    rawCommand.type === "dynamic/reset" ||
    // Phase N.4 — per-INSERT representation override commands route
    // through dynamicBlockCommandSchema for consistency with the other
    // INSERT-targeted operations.
    rawCommand.type === "geometry/set-insert-parameter-override" ||
    rawCommand.type === "geometry/clear-insert-parameter-override" ||
    rawCommand.type === "geometry/set-insert-visibility-state"
      ? dynamicBlockCommandSchema.parse(rawCommand)
      : editorCommandSchema.parse(rawCommand);

  // PR-17 — annotative shell sub-reducer (CANNOSCALE / ANNOUPDATE /
  // ANNORESET / OBJECTSCALE add+remove / ANNOALLVISIBLE / ANNOAUTOSCALE
  // / ANNOMONITOR / SCALELISTEDIT add+remove). Runs BEFORE the geometry
  // shell delegate so it can stamp an audit event before any further
  // shell-state mutation. The geometry mutation itself happens later
  // inside `dispatch` (reduceGeometryDocument), which is independent of
  // this audit pass. Falls back to the live store snapshot when no
  // explicit `geometryDocument` is provided (recursive callers from
  // routeLayoutCommand don't have one in scope, but those code paths
  // never dispatch annotative commands directly).
  const resolvedGeometryDocument =
    geometryDocument ?? useEditorStore.getState().geometryDocument;
  const annotativeResult = reduceAnnotativeShellCommand(
    state,
    command,
    resolvedGeometryDocument,
  );
  if (annotativeResult) return annotativeResult;

  // Delegate to shell UI sub-reducer (session/*, shell/*).
  // Phase 3b PR #1 — these sub-reducers now return
  // `ShellReducerMutationResult`; entity-level mutations are tracked
  // for parity but not yet routed to `commitMutation` (that wiring
  // lands once the geometry reducer is migrated, mini-PR #6).
  const shellUiResult = reduceShellUiCommand(state, command);
  if (shellUiResult) return shellUiResult.state;

  // Delegate to property sub-reducer (properties/*, chprop/*, matchprop/*).
  // Thread the resolved pre-mutation geometry document (Codex S12b P2) so a
  // grip-drag `properties/apply` translates the arc-endpoint inverse in the SAME
  // frame the grip rendered in — the geometry reducer already keys on the same
  // `document.spaces` presence, and without it a no-op arc drag left a mirrored
  // angle in the shell preview/properties. Also thread `getEntityById` — the
  // ONE-TRUTH typed-entity resolver the grip RENDER (`resolveTypedEntityById`)
  // uses — so the shell inverse keys its typed-vs-legacy frame on the EXACT same
  // signal (compact-cache first) rather than a divergent `spaces` linear scan
  // that would miss a compact-cached arc on a slimmed-`spaces` large import
  // (post-merge remediation of #493).
  const propertyResult = dispatchPropertyCommand(
    state,
    command,
    resolvedGeometryDocument,
    getEntityById,
  );
  if (propertyResult) return propertyResult.state;

  // Delegate to geometry shell sub-reducer (geometry/*). Pass the resolved
  // pre-mutation geometry document so the viewport-edit audit gate can decide
  // whether the command is a genuine mutation (FIX #269 — no false success on a
  // stale / control-viewport / no-op viewport edit).
  const geometryResult = reduceGeometryShellCommand(
    state,
    command,
    resolvedGeometryDocument,
  );
  if (geometryResult) return geometryResult;

  // Remaining cases handled inline
  switch (command.type) {
    case "commandline/submit":
      return routeLayoutCommand(state, command.text, command);

    default:
      return state;
  }
}

const dispatchPropertyCommand = (
  ...args: Parameters<typeof reducePropertyCommand>
): ReturnType<typeof reducePropertyCommand> => reducePropertyCommand(...args);

function snapshotPropertiesApplyPrevEntities(
  shouldRecordHistory: boolean,
  command: Extract<EditorCommand, { type: "properties/apply" }> | null,
  preMutationDoc: DrawingDocument,
): Map<string, DrawingEntity> {
  const snapshot = new Map<string, DrawingEntity>();
  if (!shouldRecordHistory || command === null) return snapshot;

  const touchedIds = new Set(command.entityIds ?? []);
  if (touchedIds.size === 0) return snapshot;

  findTouched: for (const space of preMutationDoc.spaces) {
    for (const entity of space.entities) {
      if (!touchedIds.has(entity.id)) continue;
      snapshot.set(entity.id, entity);
      if (snapshot.size === touchedIds.size) break findTouched;
    }
  }
  return snapshot;
}

// ── Phase 4.1.d.2 — no-op detection helpers ─────────────────────────────────

// STORE-SPLIT slice 4 — the revision-diff / document-table-fingerprint helper
// family (isEmptyMutation / stableJson / omitSpaceEntitiesWithoutReading /
// documentTableFingerprint / docHeaderChanged) moved verbatim to
// editorStoreRevisionDiff.ts (imported + docHeaderChanged re-exported above).


// STORE-SPLIT slice 2 — `IMPORT_MERGE_BUMP_NO_SELECTION` /
// `IMPORT_MERGE_BUMP_WITH_SELECTION` and `importMergeBumpForPriorSelection`
// (the import-merge revision bump set) moved verbatim to
// `editorStoreImportHelpers.ts` (imported above; still used by the
// slice-3-scoped `File`-handle import variants below).

// STORE-SPLIT slice 1 — `DirectSetBumpStoreSlice`, `directSetTableBumpSlice`,
// `isUnchangedShallowUpdate` and `TABLE_ONLY_BUMP` (the private machinery used
// ONLY by the direct-set style-table action family) moved verbatim to
// `editorStoreStyleTables.ts` alongside their only callers.

// STORE-SPLIT slice 4 — shellEntitiesChanged / contextChangedBetween /
// computeRevisionStateDiff / diffDocumentMutationsHasEntityChange moved verbatim
// to editorStoreRevisionDiff.ts (imported above).

// ── COLLAB-LOCK-COMMAND — P1 (post-review, round 5): veto-aware history ────
// R17 A01 (STATE-HIST) — the full rationale (why `dispatch()`'s seven
// `history/*` branches each recursively bypass-dispatch and check
// `wasLastDispatchCollabLockVetoed()`, and why the flag is module-scoped
// rather than a `dispatch()` return value) moved with the handler BODIES to
// `editorStoreHistoryUndoRedo.ts`'s header comment. `dispatch()` below still
// resets the flag as the FIRST statement of every call (including recursive
// ones) and still owns the `command.type === "history/…"` routing.

const editorStoreInitializer: StateCreator<EditorStore> = (set, get) => ({
  state: buildInitialEditorShellState(),
  geometryDocument: buildInitialGeometryDocument(),
  importProgress: { ...INITIAL_IMPORT_PROGRESS },
  // PR-V4 — bumped by `rebuildEntityMetadataIndexFromImport` and `reset`.
  entityMetadataIndexVersion: 0,
  renderTopologyVersion: 0,
  // ABB-S1 — five browser revision counters + documentEpoch (typed model).
  revisions: initialRevisionCounters(),
  pendingBumpClasses: null,
  pendingDirectSetBumpClasses: null,
  pendingBatchBaseDoc: null,
  pendingBatchBaseState: null,
  pendingBatchBaseAuditSequence: null,
  // Gate #4 round-2 (FIX C) — no batch has closed yet at boot.
  lastBatchClosedBy: null,
  dispatch: (command) => {
    // COLLAB-LOCK-COMMAND — P1 (post-review, round 5): reset on EVERY call
    // (including recursive `__bypassHistory` calls the history handlers make
    // below) so a caller that checks the flag right after dispatching sees
    // ONLY that call's outcome. See the module-scoped flag's own doc comment.
    resetDispatchCollabLockVetoFlag();
    // DIMSTYLE command handling — intercepts commandline/submit events that
    // match `DIMSTYLE <Action> [<name>]` syntax. These commands manipulate
    // the geometry document's dimensionStyles table via dedicated store
    // actions (addDimensionStyle / deleteDimensionStyle / renameDimensionStyle)
    // and do not go through the normal drafting reducer pipeline.
    //
    // Supported syntax (case-insensitive), mirroring AutoCAD DIMSTYLE:
    //   DIMSTYLE New <name>          — create a new style copied from current
    //   DIMSTYLE Set <name>          — set as current (applied to new dims)
    //   DIMSTYLE Apply [<name>]      — apply current (or named) to selection
    //   DIMSTYLE Delete <name>       — remove a style (Standard protected)
    //   DIMSTYLE Rename <old> <new>  — rename a style
    //   DIMSTYLE Edit <name>         — open editor for a specific style
    if (command.type === "commandline/submit") {
      const dimStyleMatch =
        /^DIMSTYLE\s+(NEW|SET|APPLY|DELETE|RENAME|EDIT)(?:\s+(.+))?$/i.exec(
          command.text.trim(),
        );
      if (dimStyleMatch && dimStyleMatch[1]) {
        const action = dimStyleMatch[1].toUpperCase();
        const argsText = (dimStyleMatch[2] ?? "").trim();
        const actions = useEditorStore.getState();
        let summary = "";
        let status: "success" | "warning" = "success";

        if (action === "NEW") {
          if (!argsText) {
            summary = t("audit.dimstyle_new_requires_name");
            status = "warning";
          } else {
            const result = actions.addDimensionStyle(argsText);
            if (result.ok) {
              summary = t("audit.dimstyle_created", { name: argsText });
            } else {
              summary = result.error;
              status = "warning";
            }
          }
        } else if (action === "DELETE") {
          if (!argsText) {
            summary = t("audit.dimstyle_delete_requires_name");
            status = "warning";
          } else {
            const result = actions.deleteDimensionStyle(argsText);
            summary = result.ok
              ? t("audit.dimstyle_deleted", { name: argsText })
              : result.error;
            status = result.ok ? "success" : "warning";
          }
        } else if (action === "RENAME") {
          const parts = argsText.split(/\s+/);
          const oldName = parts[0];
          const newName = parts[1];
          if (parts.length < 2 || !oldName || !newName) {
            summary = t("audit.dimstyle_rename_requires_names");
            status = "warning";
          } else {
            const result = actions.renameDimensionStyle(oldName, newName);
            summary = result.ok
              ? t("audit.dimstyle_renamed", {
                  oldName,
                  newName,
                })
              : result.error;
            status = result.ok ? "success" : "warning";
          }
        } else if (action === "SET") {
          // Set the CURRENT dimension style (DIMSTYLE sysvar) — the style new
          // dimensions inherit. Routes through `setCurrentDimensionStyle` (the
          // ONE truth shared with the Dimension Style Manager "Set Current"
          // button), which persists it into `defaultObjectProperties` via the
          // Command Engine (undo-able), rather than the prior no-op UI hint.
          if (!argsText) {
            summary = t("audit.dimstyle_requires_name", { action });
            status = "warning";
          } else {
            const result = actions.setCurrentDimensionStyle(argsText);
            summary = result.ok
              ? t("audit.dimstyle_set_current", { name: argsText })
              : result.error;
            status = result.ok ? "success" : "warning";
          }
        } else if (action === "EDIT" || action === "APPLY") {
          // Edit/Apply — UI hints at this point. Confirm the style exists.
          const styleName = argsText;
          if (styleName) {
            const exists =
              actions.geometryDocument.styleTables?.dimensionStyles?.some(
                (s) => s.name === styleName,
              );
            if (!exists) {
              summary = t("audit.dimstyle_not_exist", { name: styleName });
              status = "warning";
            } else {
              summary = t("audit.dimstyle_action", { action, name: styleName });
            }
          } else if (action === "APPLY") {
            summary = t("audit.dimstyle_apply_current");
          } else {
            summary = t("audit.dimstyle_requires_name", { action });
            status = "warning";
          }
        }

        // Push an audit event so the user sees feedback in the command-line
        // shell, without running the normal drafting pipeline.
        set((store) => ({
          state: pushAuditEvent(store.state, command, summary, status),
        }));
        return;
      }
    }

    // ── Phase 4.1.c.2 — history/undo ────────────────────────────────
    // R17 A01 (STATE-HIST) — body extracted verbatim to
    // `editorStoreHistoryUndoRedo.ts`'s `handleHistoryUndo`. `dispatch()`
    // still owns the routing/narrowing; `set`/`get` are the SAME function
    // references this initializer closes over (see that module's header for
    // the `get()` ≡ `useEditorStore.getState()` equivalence).
    if (command.type === "history/undo") {
      handleHistoryUndo(command, set, get);
      return;
    }

    // ── Phase 4.1.c.2 — history/redo ────────────────────────────────
    // R17 A01 (STATE-HIST) — body extracted verbatim to
    // `editorStoreHistoryUndoRedo.ts`'s `handleHistoryRedo`.
    if (command.type === "history/redo") {
      handleHistoryRedo(command, set, get);
      return;
    }

    // ── Phase 4.1.f — history/begin-batch ────────────────────────────
    // R17 A01 (STATE-HIST) — body extracted verbatim to
    // `editorStoreHistoryBatch.ts`'s `handleHistoryBeginBatch`.
    if (command.type === "history/begin-batch") {
      handleHistoryBeginBatch(command, set);
      return;
    }

    // ── Phase 4.1.f — history/commit-batch ───────────────────────────
    // R17 A01 (STATE-HIST) — body extracted verbatim to
    // `editorStoreHistoryBatch.ts`'s `handleHistoryCommitBatch`. That body
    // never reads `command` (the original inline branch didn't either), so
    // only `set` is threaded through.
    if (command.type === "history/commit-batch") {
      handleHistoryCommitBatch(set);
      return;
    }

    // ── Phase 4.1.f — history/abort-batch ────────────────────────────
    // R17 A01 (STATE-HIST) — body extracted verbatim to
    // `editorStoreHistoryReplay.ts`'s `handleHistoryAbortBatch`.
    if (command.type === "history/abort-batch") {
      handleHistoryAbortBatch(command, set, get);
      return;
    }

    // ── Phase 4.1.f — history/replay-forwards / history/replay-inverses ─
    // R17 A01 (STATE-HIST) — bodies extracted verbatim to
    // `editorStoreHistoryReplay.ts`'s `handleHistoryReplayForwards` /
    // `handleHistoryReplayInverses`. Neither body calls `set`, only `get`
    // (for the recursive bypass-dispatch), matching the original inline
    // branches.
    if (command.type === "history/replay-forwards") {
      handleHistoryReplayForwards(command, get);
      return;
    }
    if (command.type === "history/replay-inverses") {
      handleHistoryReplayInverses(command, get);
      return;
    }

    // ── Phase 4.1.c.2 — bypass flag ─────────────────────────────────
    // Commands dispatched during undo/redo carry `__bypassHistory: true`
    // to prevent recursive recording. We strip the flag before routing
    // so the normal reducers never see it.
    const isBypassHistory =
      (command as EditorCommand & { __bypassHistory?: boolean })
        .__bypassHistory === true;
    // Also skip recording for history/* commands (already handled above)
    // and for command types that don't produce geometry mutations.
    const shouldRecordHistory =
      !isBypassHistory && !command.type.startsWith("history/");

    // CECOLOR — centralized live-store sync (one-truth). The
    // `header/set-cecolor` command is the SINGLE source of truth for the
    // current colour: the geometry reducer writes `doc.cecolor`, and HERE we
    // mirror the resulting document value into the live `cecolorStore` (the
    // store the create-dispatch boundary stamps onto new entities and the
    // ribbon swatch renders). Driving the store FROM the dispatch — rather than
    // from each UI call site (App.tsx CLI/picker, CeColorSwatch) — means the
    // CLI COLOR command, the picker, the AI `SET_CURRENT_COLOR`, AND undo/redo
    // all converge on the SAME live value without per-caller `setCeColor`
    // calls. Captured inside the `set` body (where `nextGeometryDocument` is in
    // hand) and applied AFTER `set` so the zustand updater stays side-effect-
    // free. `null` = not a CECOLOR command → no sync.
    let nextCeColorToSync: EntityColor | null = null;

    set((store) => {
      // ── Phase 4.1.c.2 — pre-mutation snapshot for delete inverse ────
      // For DELETE commands, we need the entity BEFORE mutation so we
      // snapshot the pre-mutation doc here. For CREATE commands we need
      // the entity AFTER mutation (to get its minted id), so we defer
      // inverse computation until after the geometry reducer runs.
      const preMutationDoc = store.geometryDocument;

      const resolvedGeometryCommand = resolveGeometryDocumentCommand(
        store.state,
        command,
        // EPIC-COLOR-UNION S1 round-2 (leak-hunt P2) — pass the pre-mutation
        // document so the CLI MATCHPROP arm can copy the source's TYPED colour
        // losslessly (the preview `Color` is lossy and dropped rich 62/430
        // siblings onto targets).
        preMutationDoc,
      );
      // CECOLOR — stamp the live current colour onto a stampable create command
      // at the SINGLE dispatch chokepoint (one truth). Covers the command-line
      // `LINE`/`CIRCLE`/… text path (which builds create commands without a
      // colour in `resolveGeometryCommandFromText`), the interactive draft, AND
      // the AI base-primitive contour — all flow through here. No-op when the
      // command already carries an explicit colour (interactive draft / a
      // programmatic caller that named one) or is not a stampable create.
      // @codex P1 — ALSO stamp the current DIMSTYLE onto a create-dimension at
      // the SAME chokepoint (nested, order-independent — each stamp gates on its
      // own command type + explicit-value guard). New dimensions inherit the
      // persisted current dimension style (`defaultObjectProperties["Dimension
      // style"]`); before this every new dimension used `dimensionStyles[0]`, so
      // "Set Current" / `DIMSTYLE SET` never reached the create path.
      // T7 (agent-bridge), local Codex round-16 P2 (github PR #527 review) —
      // ALSO stamp CLAYER (`defaultObjectProperties.Layer`) onto every
      // stampable create at the SAME chokepoint, mirroring CECOLOR exactly
      // (see `stampCurrentLayerOntoCreateCommand`'s doc comment,
      // editorStoreStyleTables.ts, for the full "SET_ACTIVE_LAYER had zero
      // downstream effect" root-cause rationale).
      const geometryCommand = resolvedGeometryCommand
        ? (stampCurrentLayerOntoCreateCommand(
            stampCurrentDimStyleOntoCreateCommand(
              stampCeColorOntoCreateCommand(
                resolvedGeometryCommand as EditorCommand,
              ),
              store.state.propertyWorkflows.defaultObjectProperties[
                "Dimension style"
              ],
              // @codex P2 — pass the pre-mutation document so the stamp can resolve
              // the CONCRETE fallback style (the reducer's first-entry / "Standard")
              // when no current DIMSTYLE is set, and record it on the forward. Redo
              // then replays the SAME style deterministically (setCurrentDimensionStyle
              // preserves the redo stack, so a later "Set Current" must not change
              // what an earlier create replays as).
              store.geometryDocument,
            ),
            store.state.propertyWorkflows.defaultObjectProperties.Layer,
            // Round-19 P2 — validate against the LIVE layer table (catches a
            // CLAYER pointer preserved from a document the OPEN/import merge
            // path just swapped away from — see the stamp's doc comment).
            store.geometryDocument,
          ) as typeof resolvedGeometryCommand)
        : resolvedGeometryCommand;
      // FINDING 4, GENERALIZED (@codex P2, PR #468 review) — a
      // `commandline/submit` (CLI text / AI / batch script) resolves into an
      // underlying typed EditorCommand INSIDE `resolveGeometryDocumentCommand`
      // BEFORE the document-mutation path runs: COLOR → `header/set-cecolor`,
      // CHPROP/MATCHPROP → `properties/apply`, LAYOUT NEW/DELETE/RENAME → a
      // typed `shell/*-space` command, everything else →
      // `resolveGeometryCommandFromText`'s `GeometryCreateCommand` family
      // (LINE/CIRCLE/ARC/POINT/ELLIPSE/SPLINE/LWPOLYLINE/…). The live-store
      // sync AND `computeInverse` must see that RESOLVED command — the
      // wrapper's OWN `.type` is ALWAYS "commandline/submit" and matches NO
      // `computeInverse` switch case. BEFORE this fix only the CECOLOR
      // resolution was unwrapped here, so a CLI "LINE 0,0 1,1" (or
      // CIRCLE/ARC/POINT/…) mutated the document but `computeInverse` fell
      // through to `default: return null` — Ctrl+Z silently did nothing for
      // CLI-drawn geometry (the `commandline/submit` NON_INVERTIBLE_ALLOWLIST
      // entry in computeInverseCompleteness.test.ts claims "the RESOLVED
      // command carries its own inverse contract", which was only actually
      // true for the two special-cased types below this point). `LAYOUT`
      // resolves to a `shell/*-space` type that has no computeInverse case
      // either way (KNOWN_GAP, tracked separately, unaffected either way) and
      // `properties/apply` keeps its OWN dedicated live-snapshot inverse
      // further below (`effectivePropertiesApplyCommand`, unconditionally
      // overwrites `inverseCommand` regardless of what this variable
      // resolves to) — so generalizing here only changes behaviour for the
      // CREATE family, exactly the class of command this bug affects. Used
      // for the CECOLOR live-store sync + the base `computeInverse` seam;
      // every direct (non-`commandline/submit`) dispatch keeps the original
      // `command` unchanged.
      const effectiveCommand: EditorCommand =
        command.type === "commandline/submit" && geometryCommand
          ? (geometryCommand as EditorCommand)
          : (command as EditorCommand);
      // MATCHPROP persistence (@codex P1 #420) — a typed `matchprop/apply` (AI
      // safe contour + Inspector toolbar) resolves to a `properties/apply` in
      // `resolveGeometryDocumentCommand`, exactly like the CLI `MATCHPROP` /
      // `CHPROP` text arms. The snapshot-inverse block below (the AUTHORITATIVE
      // `properties/apply` undo, `buildPropertiesApplyInverseFromSnapshots`) must
      // see that RESOLVED `properties/apply` — keyed on the wrapper
      // `matchprop/apply` / `commandline/submit` it would NOT fire, so the copied
      // properties would persist to the document but leave undo history empty.
      // Mirrors `effectiveCommand`. `command` itself when a typed
      // `properties/apply` is dispatched directly (grips / Properties panel).
      const effectivePropertiesApplyCommand:
        | Extract<EditorCommand, { type: "properties/apply" }>
        | null =
        command.type === "properties/apply"
          ? (command as Extract<EditorCommand, { type: "properties/apply" }>)
          : geometryCommand?.type === "properties/apply"
            ? (geometryCommand as Extract<
                EditorCommand,
                { type: "properties/apply" }
              >)
            : null;
      // FINDING B (round-3) — the history `forward` must record the STAMPED
      // (colour-resolved) create command, not the raw input. For a create command
      // that omitted `color`, the dispatch stamped only the LOCAL `geometryCommand`
      // (via `stampCeColorOntoCreateCommand`); recording the raw unstamped
      // `command` would let redo re-stamp against whatever CECOLOR is current at
      // redo time (draw@red → undo → set blue → redo would replay BLUE). Recording
      // the stamped form makes the redo re-stamp a no-op (`color` already present),
      // so redo restores the ORIGINAL entity colour regardless of the live CECOLOR.
      // Only diverges from `command` when the chokepoint actually stamped (a
      // stampable create that omitted colour); every other path keeps `command`.
      const recordedForward: EditorCommand =
        // @codex P1 (#420) — record the RESOLVED `properties/apply` for a typed
        // `matchprop/apply` so REDO replays the concrete copied values
        // deterministically, independent of whether the transient MATCHPROP
        // source is still armed at redo time. Mirrors the CECOLOR stamped-forward
        // precedent (FINDING B) — record the resolved form, not the wrapper.
        // @codex P2-B (#420 round-2) — a CLI `CHPROP` / `MATCHPROP`
        // `commandline/submit` ALSO resolves to a `properties/apply`; recording
        // the wrapper would make REDO re-resolve against the current selection /
        // armed source (both transient) → a broken / wrong-target redo. Record
        // the resolved form for that path too so redo is deterministic.
        (command.type === "matchprop/apply" ||
          command.type === "commandline/submit") &&
        geometryCommand?.type === "properties/apply"
          ? (geometryCommand as EditorCommand)
          : geometryCommand && geometryCommand !== resolvedGeometryCommand
            ? (geometryCommand as EditorCommand)
            : (command as EditorCommand);
      // CECOLOR round-6 FINDING B (P2 redo) — the create-attdef inverse block
      // (below) may override the recorded `forward` with a session-independent
      // redo (carrying the materialized AttdefEntity). When set, the history
      // entry / batch forward uses this instead of the BEDIT-gated raw create.
      let recordedForwardOverride: EditorCommand | null = null;
      // @codex #496 P2 (redo) — VPCLIP "Select clipping object" records only
      // `boundaryEntityId`. Redo replays `recordedForward`, which would
      // RE-RESOLVE that object against the doc at redo time — a later edit /
      // delete of the boundary object would then replay a DIFFERENT polygon (or
      // no-op) instead of the clip the user authored. Freeze the resolved
      // paper-space boundary into the recorded forward NOW (against the SAME
      // pre-mutation doc + SAME shared resolver the forward authored with) so
      // redo replays the EXACT polygon deterministically. Covers BOTH the
      // interactive typed dispatch AND the resolved `commandline/submit` (batch /
      // AI) path — `effectiveCommand` is the typed set-polygon in both; an
      // explicit-`boundary` command already carries its frozen polygon. Mirrors
      // the CECOLOR / MATCHPROP resolved-forward precedent (record the concrete
      // resolved form, not the transient reference).
      if (
        effectiveCommand.type === "geometry/viewport-clip-set-polygon" &&
        effectiveCommand.boundaryEntityId &&
        !effectiveCommand.boundary
      ) {
        const frozenBoundary = resolveVpclipBoundaryPolygon(
          preMutationDoc,
          effectiveCommand.viewportId,
          { boundaryEntityId: effectiveCommand.boundaryEntityId },
        );
        if (frozenBoundary) {
          recordedForwardOverride = {
            type: "geometry/viewport-clip-set-polygon",
            source: effectiveCommand.source,
            viewportId: effectiveCommand.viewportId,
            // Freeze the resolved polygon for deterministic REDO geometry …
            boundary: frozenBoundary,
            // … AND retain the boundary object id so REDO re-records the object's
            // 340 clip-boundary handle (`applyViewportClipSetPolygon` uses the
            // frozen `boundary` for the polygon but `boundaryEntityId` for the
            // export handle). Without it, redo of an object-mode VPCLIP would drop
            // the 340 and lose the clip on save — the same defect the forward path
            // fixes (@codex #496 P1).
            boundaryEntityId: effectiveCommand.boundaryEntityId,
          } as EditorCommand;
        }
      }
      // ARRAYPATH-OUTCOME — the audit sequence the shell reducer is ABOUT to
      // allocate for this command's row. Captured BEFORE the shell reduce (the
      // ring prepends and is capped at 24, so nothing positional survives a
      // busy session) so `applyArrayPathOutcomeToAudit` below can find exactly
      // the row THIS dispatch pushed and no other. See that function's contract.
      const auditSequenceBefore = store.state.audit.nextSequence;
      const nextState = reduceEditorShellState(
        store.state,
        command,
        store.geometryDocument,
      );
      // Phase 2.3: BEDIT / BSAVE / BCLOSE commands don't go through
      // resolveGeometryDocumentCommand (the shell reducer doesn't know
      // about them) — pass them straight to the geometry reducer so
      // its dispatcher can update the document. The reducer also
      // pipes the result through syncBlockEditSpaceToDefinition so
      // any regular geometry mutation issued while a BEDIT session is
      // active lands in the target BlockDefinition automatically.
      const blockEditCommand =
        command.type === "space/enter-block-edit" ||
        command.type === "space/exit-block-edit" ||
        command.type === "space/save-block-edit" ||
        command.type === "space/enter-block-test" ||
        command.type === "space/exit-block-test" ||
        command.type === "space/enter-refedit" ||
        command.type === "space/exit-refedit" ||
        command.type === "space/refedit-add-to-working-set" ||
        command.type === "space/refedit-remove-from-working-set"
          ? command
          : null;
      // Phase 3b PR #6 — switch to the `*WithMutations` variant so the
      // dispatch flow can feed `commitMutation` an explicit
      // {added/removed/updated/styleTablesTouched} payload. The legacy
      // `reduceGeometryDocument` thin wrapper is kept on the module
      // export for tests / external callers; here we want the diff.
      const blockEditResult = blockEditCommand
        ? reduceGeometryDocumentWithMutations(
            store.geometryDocument,
            blockEditCommand,
          )
        : null;
      const afterBlockEdit = blockEditResult?.document ?? store.geometryDocument;
      const blockEditMutations = blockEditResult?.mutations ?? NO_MUTATIONS;
      // #518 ROOT FIX — BEDIT/BLOCKTEST/REFEDIT enter+exit are GEO-FIRST space
      // transitions: `dispatchBlockEditCommand` (via `enterBlockEditSpace` /
      // `exitBlockEditSpace` / `enterBlockTestSpace` / `exitBlockTestSpace` /
      // `enterRefeditSpace` / `exitRefeditSpace` in drawing-model/helpers.ts)
      // writes `document.activeSpaceId` directly; `reduceEditorShellState`
      // (the SHELL reducer, run above into `nextState`) has NO case for any
      // `space/*` type, so `nextState.drawing.activeSpaceId` is left exactly
      // as it was pre-dispatch. `syncShellStateFromGeometryDocument`'s
      // `resolvedActiveSpaceId` normally PREFERS the shell's (stale, here)
      // `state.drawing.activeSpaceId` over `geoDoc.activeSpaceId` whenever the
      // stale value still names a space that still exists in `geoDoc.spaces`
      // — true for BEDIT-enter (the previous active space, e.g. "model", is
      // NOT removed, only superseded), so the stale shell value silently won
      // and the freshly-entered block-edit space was discarded before it
      // ever reached `geometryDocument.activeSpaceId` (App.tsx's
      // `isInBlockEdit` gate never flipped). Detecting the divergence
      // directly — did THIS dispatch's block-edit-family command actually
      // move `activeSpaceId`? — is a by-construction signal that needs no
      // per-command-type allowlist and covers BEDIT/BCLOSE/BSAVE/TESTBLOCK/
      // CLOSETESTBLOCK/REFEDIT/REFCLOSE uniformly.
      const geoActiveSpaceAuthoritative =
        afterBlockEdit.activeSpaceId !== store.geometryDocument.activeSpaceId;
      // @codex P2-C (#420 round-2) — the geometry reducer's `properties/apply`
      // target lookup reads `previewEntities.find(id)`. On large imports
      // `state.drawing.entities` is emptied above SHELL_ENTITY_PREVIEW_THRESHOLD,
      // so EVERY target would miss and the copy would no-op — the source-side fix
      // (document-backed source in `resolveMatchpropApplyToPropertiesApply`) is
      // only half the seam. Build document-backed previews on demand for the
      // resolved `properties/apply` targets the shell lacks (mirrors
      // `selectSelectedEntities` PR-V4 + the one-truth `getEntityById`), so
      // MATCHPROP / CHPROP persist to targets regardless of the threshold. No-op
      // on small imports (every id is already in the shell → `missing` empty).
      const reducerPreviewEntities: EntityPreview[] = (() => {
        if (!effectivePropertiesApplyCommand) {
          return store.state.drawing.entities;
        }
        const shellIds = new Set(
          store.state.drawing.entities.map((e) => e.id),
        );
        const missing = (
          effectivePropertiesApplyCommand.entityIds ?? []
        ).filter((id) => !shellIds.has(id));
        if (missing.length === 0) return store.state.drawing.entities;
        // @codex #420 round-3 — synthesise the missing target previews through
        // the SAME one-truth `buildDocumentBackedPreview` the source-read /
        // undo-snapshot resolver uses. `getEntityById` (O(1) compact cache on
        // the live pre-mutation document) stays the lookup here — the reducer
        // needs the PRE-mutation preview it then mutates, and this is the hot
        // path (potentially many targets), so we keep the cache lookup rather
        // than the resolver's O(spaces) document scan.
        const extra: EntityPreview[] = [];
        for (const id of missing) {
          const docEntity = getEntityById(id);
          if (docEntity) {
            extra.push(buildDocumentBackedPreview(docEntity, afterBlockEdit));
          }
        }
        return extra.length > 0
          ? [...store.state.drawing.entities, ...extra]
          : store.state.drawing.entities;
      })();
      // Codex P2 (#515 round 2, post-merge remediation of #493) — thread the
      // SAME one-truth `getEntityById` resolver into the PERSISTED document
      // reducer that the shell `properties/apply` path (line ~2014 above)
      // already receives. Without it, the document reducer's own
      // `preMutationDocEntity` scan (geometryReducer.ts) could miss an entity
      // on a slimmed-`spaces` large/streamed import that the resolver-aware
      // shell reducer resolved fine — persisting a different (or no) geometry
      // patch than what the Properties panel showed, diverging document/
      // export/undo from the shell.
      const geometryResult = geometryCommand
        ? reduceGeometryDocumentWithMutations(
            afterBlockEdit,
            geometryCommand,
            reducerPreviewEntities,
            getEntityById,
          )
        : null;
      const nextGeometryDocument = geometryResult?.document ?? afterBlockEdit;
      const geometryMutations = geometryResult?.mutations ?? NO_MUTATIONS;
      // CECOLOR — capture the post-reducer current colour for the live-store
      // sync applied after `set` (see `nextCeColorToSync` declaration above).
      // Only when the reducer actually ran (geometryResult truthy → the command
      // reached `reduceGeometryDocument` via the resolveGeometryDocumentCommand
      // allowlist) AND the command is the colour setter — so undo/redo, the CLI
      // COLOR, the picker and the AI all flow through this single seam.
      if (
        effectiveCommand.type === "header/set-cecolor" &&
        geometryResult
      ) {
        nextCeColorToSync = nextGeometryDocument.cecolor ?? { kind: "byLayer" };
      }
      // Combined diff for the entire dispatch — fed to `commitMutation`
      // (which updates the non-reactive cache + entity metadata index +
      // bumps the reactive version counter) AND to
      // `syncShellStateFromGeometryDocument` (which uses it as a no-op
      // fast-path: when nothing changed, skip the entity preview rebuild).
      const dispatchMutations: EntityMutation = mergeMutations(
        blockEditMutations,
        geometryMutations,
      );

      // ── COLLAB-LOCK-COMMAND — fail-closed EDIT veto ──────────────────────
      // Reject the WHOLE dispatch, atomically, when it touches an entity or
      // layer another collaborator currently soft-locks. Fires HERE — right
      // after `dispatchMutations` is computed, BEFORE any inverse
      // computation / history recording / `commitMutation` — so the early
      // `return` below naturally guarantees "no partial apply": nothing
      // past this point has run yet, and the callback returns straight back
      // to zustand with `store.state` — the PRISTINE pre-dispatch shell
      // state THIS `set((store) => {...})` updater was invoked with. (There
      // is NO `stateWithLastSubmitted`-named local in this scope — that
      // name belongs only to the separate `routeLayoutCommand` function;
      // `store.state` is the correct pristine value AT THIS EXACT LINE,
      // confirmed by reading its own use a few lines above as the
      // `resolveGeometryDocumentCommand`/`stampCurrentDimStyleOntoCreateCommand`
      // input — the same "before this dispatch" snapshot every other read
      // in this callback treats as pristine.)
      //
      // `dispatchMutations.added` entities are brand-new ids THIS SAME
      // dispatch just minted — they can never already be locked by another
      // collaborator, so effectively only their LAYER scope matters
      // (AutoCAD parity: you cannot draw onto a layer someone else holds
      // locked) — pushing the new id as `entityId` is harmless (a fresh id
      // can never match a live lock) rather than special-casing the pure
      // veto predicate for a third "layer-only" input shape. `removed`
      // entities carry no `.layer` in `EntityMutation` (only `entityId`),
      // so their PRE-mutation layer is resolved via `getEntityById` — the
      // SAME O(1) compact-cache resolver already used a few lines above
      // (`reducerPreviewEntities`) and, critically, STILL pre-mutation here
      // (`commitMutation` — the sole writer of the compact cache — has not
      // run yet). `updated` entities carry BOTH `.prev`/`.next` directly; a
      // rename or layer-reassignment inside the SAME dispatch also checks
      // the `.next` identity so a property edit cannot be used to bypass a
      // lock by moving the entity onto/off a locked layer mid-dispatch.
      //
      // Gated on a non-empty live `locks` map OR a non-"disconnected" collab
      // status first — the true zero-overhead fast path `evaluateCollabLockVeto`
      // itself also enforces, extended to the CALL SITE so a normal
      // (non-collab) dispatch never even builds the `touchedEntities`
      // projection. P1 (post-review, round 5) — the status half of this OR is
      // required for fail-closed correctness: `CollabLockClient.
      // resetTrackedState()` clears `locks` to EMPTY on every "connecting"/
      // "error" transition too (not just genuine disconnect), so a
      // `locks.size > 0`-only gate would SKIP the veto entirely during a live
      // reconnect window, silently allowing an edit whose true lock status is
      // unknown. See `collabLockVeto.ts`'s `authoritative` field.
      const collabLockStatusSnapshot = useCollabLockStore.getState().status;
      if (
        useCollabLockStore.getState().locks.size > 0 ||
        collabLockStatusSnapshot !== "disconnected"
      ) {
        const collabLockVetoTouched: Array<{
          entityId: string;
          layerName: string | undefined;
        }> = [];
        for (const removed of dispatchMutations.removed) {
          collabLockVetoTouched.push({
            entityId: removed.entityId,
            layerName: getEntityById(removed.entityId)?.layer,
          });
        }
        for (const updated of dispatchMutations.updated) {
          collabLockVetoTouched.push({
            entityId: updated.prev.id,
            layerName: updated.prev.layer,
          });
          if (
            updated.next.id !== updated.prev.id ||
            updated.next.layer !== updated.prev.layer
          ) {
            collabLockVetoTouched.push({
              entityId: updated.next.id,
              layerName: updated.next.layer,
            });
          }
        }
        for (const added of dispatchMutations.added) {
          collabLockVetoTouched.push({
            entityId: added.entity.id,
            layerName: added.entity.layer,
          });
        }
        // P1 (post-review) — `header/update-layer` / `header/delete-layer`
        // are STYLE-TABLE-ONLY mutations (`dispatchMutations.styleTablesTouched`,
        // no entity added/removed/updated — see `styleTablesReducer.ts`'s
        // `header/delete-layer` entity-remap NOTWITHSTANDING: the REMAP lands
        // in `dispatchMutations.updated` per-entity and is already covered
        // above; this is about the LAYER RECORD ITSELF), so the entity-derived
        // `collabLockVetoTouched` projection above NEVER sees them — a
        // `LOCK LAYER Walls` would silently fail to stop another collaborator
        // renaming/deleting the "Walls" layer DEFINITION. Check `effectiveCommand`
        // (the RESOLVED command — same variable the CECOLOR/viewport-clip seams
        // above already key off for exactly this "commandline/submit wrapper vs
        // typed command" reason) directly for the layer-table command types
        // and feed the target layer name through the dedicated
        // `touchedLayerNames` veto input (layer-scope-only check, no entity
        // involved).
        //
        // GitHub Codex review (PR #534, f0db1ad6) P2 — `header/rename-layer`
        // (Layer Manager rename path, T7 agent-bridge layer-mgmt) was MISSING
        // from this list. When the locked layer has no entities on it, a
        // rename produces zero `dispatchMutations.updated`/`.added` rows
        // (nothing to remap), so `collabLockVetoTouched` above stays empty
        // too — the rename slipped past the veto entirely, letting another
        // collaborator rename a `LOCK LAYER` target out from under the
        // server-side lock. The command's LOCKED identity is `oldName` (the
        // layer being renamed FROM), not `newName` — mirrors how
        // `header/delete-layer`/`header/update-layer` key off the layer's
        // CURRENT name, the one an existing `lock-request` would have been
        // taken against.
        const collabLockVetoTouchedLayerNames: string[] =
          effectiveCommand.type === "header/update-layer" ||
          effectiveCommand.type === "header/delete-layer"
            ? [effectiveCommand.layerName]
            : effectiveCommand.type === "header/rename-layer"
              ? [effectiveCommand.oldName]
              : [];
        const collabLockVeto = evaluateCollabLockVeto({
          touchedEntities: collabLockVetoTouched,
          touchedLayerNames: collabLockVetoTouchedLayerNames,
          locks: useCollabLockStore.getState().locks,
          selfParticipantId: useCollabLockStore.getState().selfParticipantId,
          authoritative: collabLockStatusSnapshot === "connected",
        });
        if (collabLockVeto) {
          // P1 (post-review, round 5) — record that THIS dispatch was
          // vetoed so the history/undo, history/redo, history/abort-batch
          // and history/replay-* handlers (which recursively `dispatch()`
          // the inverse/forward command with `__bypassHistory: true`) can
          // detect the rejection and refuse to advance the past/future
          // stacks — see the module-scoped flag's own doc comment.
          markDispatchCollabLockVetoed();
          const message =
            collabLockVeto.kind === "unavailable"
              ? t("collab.lock.edit_rejected_unavailable")
              : t("collab.lock.edit_rejected", {
                  target: collabLockVeto.scope.targetId,
                  owner: collabLockVeto.heldByName,
                });
          return {
            state: pushAuditEvent(store.state, command, message, "error"),
          };
        }
      }

      // ── Phase 4.1.c.2 follow-up (P1-1) — compute inverse BEFORE commitMutation ──
      // computeInverse must run on the pure DrawingDocument objects, not on
      // the cache-backed read-through wrapper that commitMutation produces.
      // Moving it here (after the geometry reducer produces nextGeometryDocument
      // but before commitMutation writes the cache) ensures:
      //   CREATE (geometry/create-*): inverse uses nextGeometryDocument to find
      //     the newly-minted entity id (produced by nextGeometryEntityId in the
      //     geometry reducer, now present in nextGeometryDocument).
      //   DELETE (geometry/delete-entity, shell/delete-entities): inverse uses
      //     preMutationDoc which still has the entity before deletion.
      //   PROPERTY (properties/apply): inverse uses preMutationDoc to snapshot
      //     old entity field values before the property change is applied.
      //   ARRAY (geometry/create-array* + geometry/arrayedit): inverse uses
      //     preMutationDoc to snapshot the pre-tag sources / old copies and
      //     dispatchMutations.added for the minted copy ids (FF-1/FF-2).
      //   Commands without an inverse contract: computeInverse returns null.
      let inverseCommand: EditorCommand | null = null;
      if (shouldRecordHistory) {
        const isDeleteCommand =
          command.type === "geometry/delete-entity" ||
          command.type === "shell/delete-entities";
        // Phase MC5: viewport-* commands mutate the geometry document (VP
        // overrides / displayLocked / frozenLayers), so preMutationDoc is
        // required to snapshot the PRE-mutation VP state for undo.
        const isViewportMutationCommand =
          command.type === "geometry/viewport-set-lock" ||
          command.type === "geometry/viewport-set-shadeplot" ||
          command.type === "geometry/viewport-set-scale" ||
          command.type === "geometry/viewport-set-view" ||
          command.type === "geometry/viewport-clip-set" ||
          command.type === "geometry/viewport-clip-set-polygon" ||
          command.type === "geometry/viewport-clip-remove" ||
          command.type === "geometry/viewport-vplayer";
        // VPCLIP via the command line resolves (in resolveGeometryDocumentCommand
        // / submitCommandText) to a typed viewport-clip command whose inverse
        // snapshots the PRE-mutation viewport; when the outer dispatch wrapper is
        // `commandline/submit` the `command.type` gate above cannot see it, so
        // also gate on the RESOLVED `effectiveCommand` (mirrors the CECOLOR
        // unwrap) so undo of a CLI-authored polygonal/rectangular clip restores
        // the pre-clip viewport rather than snapshotting the already-clipped one.
        const isResolvedViewportClipCommand =
          effectiveCommand.type === "geometry/viewport-clip-set-polygon" ||
          effectiveCommand.type === "geometry/viewport-clip-set" ||
          effectiveCommand.type === "geometry/viewport-clip-remove";
        // Phase 4.1.e: properties/apply updates the geometry document, so
        // preMutationDoc is needed to read old property values for the inverse.
        const needsPreMutationDoc =
          isDeleteCommand ||
          command.type === "properties/apply" ||
          isViewportMutationCommand ||
          isResolvedViewportClipCommand ||
          // BASE / $INSBASE — computeInverse snapshots the PRIOR insertion base
          // from the pre-mutation document so undo restores the previous point.
          command.type === "header/set-insertion-base" ||
          // CECOLOR / COLOR — computeInverse snapshots the PRIOR current colour
          // from the pre-mutation document so undo restores the previous colour.
          // FINDING 4 — also true when a `commandline/submit` "COLOR 5" resolved
          // to set-cecolor (so batch / AI COLOR is undoable as one atomic step).
          effectiveCommand.type === "header/set-cecolor" ||
          // CREATE_BLOCK — computeInverse reads the PRE-mutation doc to snapshot
          // the source entities removed by a delete/convert postAction (so undo
          // restores them) and to confirm the block name did not pre-exist.
          command.type === "geometry/create-block-definition" ||
          // REORDER-ATTDEFS (round-9 FINDING B) — BATTMAN Move Up/Down. The
          // package computeInverse has no reorder case (returns null); the
          // hand-built inverse below snapshots the PRE-mutation ATTDEF id order
          // from the block definition, so preMutationDoc must be available here.
          command.type === "geometry/reorder-attdefs" ||
          // UPDATE-ATTRIBUTE-VALUE(S) (Lane F round-? FINDING 2) — DDATTE /
          // ATTEDIT / EATTEDIT. The hand-built inverse below snapshots the
          // PRE-mutation INSERT's attributes[] + common fields, so
          // preMutationDoc must be available here.
          command.type === "geometry/update-attribute-value" ||
          command.type === "geometry/update-attribute-values" ||
          // ARRAY / ARRAYEDIT (FF-1/FF-2) — the forward tags the source
          // entities in place (and ARRAYEDIT deletes the old copies), so
          // computeInverse snapshots the PRE-mutation group members for the
          // restore half of its inverse; the minted copy ids come from
          // dispatchMutations.added, not the doc.
          command.type === "geometry/create-arrayrect" ||
          command.type === "geometry/create-arraypolar" ||
          command.type === "geometry/create-arraypath" ||
          command.type === "geometry/arrayedit" ||
          // HATCHEDIT — the forward overwrites the target hatch's pattern/scale/
          // angle/style/patternType/associative IN PLACE, so computeInverse must
          // read those PRE-mutation values from the document to build the
          // restoring inverse. Without preMutationDoc here, docForInverse would
          // be the already-mutated document and undo would "restore" the NEW
          // values (a no-op). Mirrors arrayedit / properties/apply above.
          command.type === "geometry/hatchedit" ||
          // REVCLOUD Object mode (REVCLOUD-MODES / HIST-REVCLOUD-INVERSE) —
          // the forward REPLACES the picked source entity with the cloud,
          // so computeInverse snapshots the PRE-mutation source for the
          // restore half of its inverse. Rectangular/Polygonal/Freehand
          // clouds keep the POST-mutation doc (plain single-append create).
          (command.type === "geometry/create-revcloud" &&
            command.creationMode === "Object") ||
          // MIRROR in-place (deleteSource=true) — the MIRRTEXT=0 readable-text
          // rotation re-derivation is NOT self-inverse for backward-facing text,
          // so computeInverse snapshots the PRE-mutation rotations into the
          // inverse's `restoreRotations` map. Needs preMutationDoc to read those
          // original rotations (the next doc holds the already-mirrored values).
          // GitHub Codex review, PR #558 round 8 P2 — gates on the RESOLVED
          // `effectiveCommand`, NOT the raw `command` (mirrors the CECOLOR /
          // viewport-clip unwrap above): a CLI-issued "MIRROR" resolves
          // through `commandline/submit`, where `command.type` stays
          // `"commandline/submit"` and only `effectiveCommand.type` carries
          // the resolved `"geometry/mirror-entities"` — the raw-`command`
          // gate silently missed this path since round 1, leaving
          // `docForInverse` on the already-mirrored document for every
          // CLI-issued MIRROR undo (readable-text rotation AND the
          // ROTATE/SCALE siblings' WORLD-attribute snapshots below share the
          // identical bug, found together and fixed together here).
          (effectiveCommand.type === "geometry/mirror-entities" &&
            effectiveCommand.deleteSource) ||
          // ROTATE (MIRRTEXT-DIMENSION-MLEADER-ATTRIB round 4 / GitHub Codex
          // review PR #558 P1) — computeInverse snapshots a Block's
          // per-attribute WORLD `overrideRotation` PRE-rotation into the
          // inverse's `restoreAttributeRotations` map (the angle arithmetic
          // itself is self-inverse, but `withWorldAttributeRotation`'s
          // presence-preserving write-back needs the ORIGINAL presence/value
          // to disambiguate "restore to absent" from "restore to an explicit
          // 0" on undo). Without preMutationDoc here, computeInverse read
          // `nextGeometryDocument` — the ALREADY-rotated document — so the
          // snapshot captured post-rotation angles/presence instead of the
          // true originals: a 10° attribute rotated by 30° undid to 40°
          // instead of back to 10°, and a genuinely-absent override was
          // captured as present (materialised by the forward rotate) instead
          // of null, so undo restored a spurious value instead of deleting it
          // — silently defeating the whole round-4 fix. GitHub Codex review
          // PR #558 round 8 P2 — gates on `effectiveCommand.type`, not raw
          // `command.type` (see the MIRROR entry above): a CLI-issued
          // "ROTATE" left this check blind, so `restoreAttributeRotations`
          // snapshotted the POST-rotation document for that path too.
          effectiveCommand.type === "geometry/rotate-entities" ||
          // SCALE (GitHub Codex review PR #558 round 7 P2) — same story as
          // ROTATE directly above: computeInverse snapshots a Block's
          // per-attribute WORLD `overrideHeight` PRE-scale into the
          // inverse's `restoreAttributeHeights` map (SCALE's own factor
          // arithmetic is self-inverse, but `presencePreservingValue`'s
          // write-back needs the ORIGINAL presence/value to disambiguate
          // "restore to absent" from "restore to an explicit 2.5" on
          // undo). Without preMutationDoc here the same class of bug as
          // ROTATE's would silently reappear for SCALE. Round 8 P2 — same
          // `effectiveCommand.type` fix as ROTATE/MIRROR above (a CLI-issued
          // "SCALE" left the raw-`command.type` check blind).
          effectiveCommand.type === "geometry/scale-entities" ||
          // XREF suite (ABB-S1 round-3 F3) — the inverse is a SNAPSHOT RESTORE of
          // the four xref-bearing document slices, captured from the PRE-mutation
          // document (the next doc already holds the mutated slices). Needs
          // preMutationDoc to read those original slices.
          command.type === "file/xattach" ||
          command.type === "file/xbind" ||
          command.type === "file/xunload" ||
          command.type === "file/xreload" ||
          command.type === "file/xdetach" ||
          command.type === "file/xclip" ||
          // D4 — TRIM/EXTEND/FILLET/CHAMFER/BREAK/JOIN/EXPLODE-ENTITIES/
          // EXPLODE-INSERT/LENGTHEN/STRETCH. computeInverse's
          // `buildModifyCommandSnapshotInverse` reads the PRE-mutation full
          // entity snapshot for every id the forward touched-in-place or
          // removed — `dispatchMutations.removed` carries only the
          // `entityId` (no payload, same P1-3 contract as
          // `geometry/delete-entity`), so `preMutationDoc` is the ONLY place
          // a removed source (JOIN sources, EXPLODE sources, BREAK's
          // whole-entity-consumed edge case) can be recovered for restore.
          command.type === "geometry/trim-entity" ||
          command.type === "geometry/extend-entity" ||
          command.type === "geometry/fillet-entities" ||
          command.type === "geometry/chamfer-entities" ||
          command.type === "geometry/break-entity" ||
          command.type === "geometry/join-entities" ||
          command.type === "geometry/explode-entities" ||
          command.type === "geometry/explode-insert" ||
          command.type === "geometry/lengthen-entity" ||
          command.type === "geometry/stretch-entities";
        const docForInverse = needsPreMutationDoc
          ? preMutationDoc
          : nextGeometryDocument;
        // FINDING 4, GENERALIZED (@codex P2, PR #468) — feed computeInverse the
        // RESOLVED command whenever `commandline/submit` mapped to one
        // (otherwise the wrapper `commandline/submit` matches no inverse case
        // → CLI-issued geometry — COLOR, or CREATE via LINE/CIRCLE/ARC/…—
        // would have no undo). All other (non-`commandline/submit`) commands
        // pass through as-is. See `effectiveCommand`'s doc comment above.
        inverseCommand = computeInverse(
          effectiveCommand,
          docForInverse,
          dispatchMutations,
        );
        // CECOLOR round-6 FINDING B — explicit inverse for `geometry/create-attdef`.
        // The package `computeInverse` has no attdef case (returns null) because
        // the new ATTDEF lands in `blocks[name].entities` — NOT a space — so it
        // never appears in the space-only `dispatchMutations.added` that the
        // create-block-insert inverse keys on. Build the inverse HERE where BOTH
        // the pre- and post-mutation documents are available: diff the named
        // block's entity array to find the freshly-minted ATTDEF id, then emit a
        // `geometry/remove-attdef` so UNDO removes it (history recorded above via
        // `docHeaderChanged`'s document-table fingerprint — `blocks[name].entities`
        // is in the fingerprint; redo cleared by the standard path).
        // No-op (null) when the forward was a no-op (silent BEDIT-mismatch reject)
        // so an idle create-attdef pushes no empty history entry.
        if (
          inverseCommand === null &&
          command.type === "geometry/create-attdef"
        ) {
          const blockName = command.blockName;
          const prevAttdefIds = new Set(
            (preMutationDoc.blocks[blockName]?.entities ?? [])
              .filter((e) => e.type === "Attdef")
              .map((e) => e.id),
          );
          const newAttdef = (
            nextGeometryDocument.blocks[blockName]?.entities ?? []
          ).find((e) => e.type === "Attdef" && !prevAttdefIds.has(e.id));
          if (newAttdef) {
            inverseCommand = {
              type: "geometry/remove-attdef",
              source: "system",
              blockName,
              attdefId: newAttdef.id,
              // CECOLOR round-6 FINDING B (P3) — the forward `handleCreateAttdef`
              // ONLY appends the ATTDEF to `blocks[name].entities`; it NEVER
              // cascades an ATTRIB onto existing INSERTs (ATTSYNC is a separate
              // explicit command). The precise inverse must therefore NOT touch
              // inserts: with `removeFromInserts:true`, removeAttdefMutation
              // strips EVERY same-tag ATTRIB from all inserts of the block, so a
              // pre-existing INSERT carrying an ATTRIB whose tag collides with the
              // freshly-authored ATTDEF tag (create-attdef does not enforce tag
              // uniqueness) would have that ATTRIB deleted by UNDO — an asymmetric
              // inverse that corrupts state the forward never created. The new
              // ATTDEF has no propagated instances to clean up, so false is exact.
              removeFromInserts: false,
            } as EditorCommand;
            // CECOLOR round-6 FINDING B (P2 redo) — REDO of create-attdef must be
            // session-independent. The default redo replays `recordedForward`
            // (the raw `geometry/create-attdef`), but `handleCreateAttdef` is a
            // SILENT NO-OP unless a matching block-edit space is active. After the
            // user EXITS BEDIT, UNDO still removes the ATTDEF (the inverse is
            // session-independent), but REDO would then no-op while consuming the
            // future slot → the ATTDEF is permanently lost. Override the recorded
            // forward to carry the already-materialized AttdefEntity via the
            // internal `__replayAttdef` field; `handleCreateAttdef` honours it by
            // re-inserting that exact entity directly into the block definition,
            // bypassing the gate (the genuine authoring gate is preserved for
            // first-time user creates). Symmetric with the session-independent
            // inverse above.
            recordedForwardOverride = {
              ...(recordedForward as object),
              __replayAttdef: newAttdef,
            } as unknown as EditorCommand;
          }
        }
        // CECOLOR round-9 FINDING B — explicit inverse for
        // `geometry/reorder-attdefs` (BATTMAN Move Up/Down). The package
        // `computeInverse` has no reorder case (returns null) — the reorder only
        // permutes `blocks[name].entities`, never appears in the space-only
        // `dispatchMutations`. A reorder is SELF-SYMMETRIC: its inverse is another
        // reorder back to the original ATTDEF id sequence. Snapshot the
        // PRE-mutation ATTDEF id order from the pre-mutation block definition and
        // emit a `geometry/reorder-attdefs` carrying it (history is recorded above
        // now that `docHeaderChanged`'s document-table fingerprint is order-sensitive
        // over `blocks[name].entities`; redo is cleared by the standard path). A
        // reorder changes the entity array order, so the stable-JSON fingerprint of
        // the document-minus-space-entities differs → docMutated. No-op (leave null)
        // when the forward was a
        // silent no-op (handleReorderAttdefs swallows invalid input → the block is
        // unchanged) so an idle reorder pushes no empty history entry.
        if (
          inverseCommand === null &&
          command.type === "geometry/reorder-attdefs"
        ) {
          const blockName = command.blockName;
          const prevAttdefIds = (
            preMutationDoc.blocks[blockName]?.entities ?? []
          )
            .filter((e) => e.type === "Attdef")
            .map((e) => e.id);
          const nextAttdefIds = (
            nextGeometryDocument.blocks[blockName]?.entities ?? []
          )
            .filter((e) => e.type === "Attdef")
            .map((e) => e.id);
          // Only emit the inverse when the order ACTUALLY changed (the forward was
          // not a silent reject / identity reorder) — otherwise undo would push a
          // no-op history entry. Same length + same set is guaranteed by the
          // reducer; compare the ordered sequences.
          const orderChanged =
            prevAttdefIds.length === nextAttdefIds.length &&
            prevAttdefIds.some((id, i) => id !== nextAttdefIds[i]);
          if (orderChanged) {
            inverseCommand = {
              type: "geometry/reorder-attdefs",
              source: "system",
              blockName,
              attdefIds: prevAttdefIds,
            } as EditorCommand;
          }
        }
        // Lane F round-? FINDING 2 — explicit inverse for
        // `geometry/update-attribute-value` / `geometry/update-attribute-values`
        // (DDATTE / ATTEDIT / EATTEDIT). The package `computeInverse` has no case
        // for either — they mutate `attributes[]` + common fields on a
        // SPACE-level INSERT, not the `blocks[name].entities` template the
        // attdef-authoring cases above key on — so a forward ATTRIB edit
        // silently produced NO undo entry even after Finding-1-sibling routing
        // made the edit reach the geometry document. Snapshot the PRE-mutation
        // INSERT (attributes[] + the four EATTEDIT-editable common fields) here
        // where `preMutationDoc` is available, and build a
        // `geometry/update-attribute-values` inverse carrying the FULL VERBATIM
        // snapshot on the internal `__restoreAttributes` field
        // (`restoreInsertAttributesVerbatim` in attributeReducerHelpers.ts) — a
        // raw field restore, not a re-run of the tag-matching mutation, so a
        // forward that CREATED a fresh ATTRIB entry (tag present on the ATTDEF
        // template but absent from this INSERT until edited —
        // updateAttributeValueMutation's "create" branch) is undone EXACTLY:
        // the restored array omits that tag entirely, which the tag-based
        // mutation could never express (update-or-create, never remove).
        // No-op (leave null) when nothing actually changed (an idle Apply with
        // no edited rows, or a silently-rejected tag) so no empty history
        // entry is pushed.
        if (
          inverseCommand === null &&
          (command.type === "geometry/update-attribute-value" ||
            command.type === "geometry/update-attribute-values")
        ) {
          const insertId = command.insertId;
          // Round-5 FINDING 2 — resolve the INSERT in EITHER a live space OR a
          // block definition (a nested INSERT whose BEDIT space was
          // saved/closed lives only in `blocks[name].entities`). The forward
          // mutation + verbatim restore already use this block-definition-aware
          // lookup (`resolveInsertAnywhere`/`patchInsertAnywhere`); the space-only
          // `findInsertById` here returned `null` for such a target, so the
          // inverse was NEVER recorded and the successful forward edit could not
          // be undone at all (not merely "restore can't find the target").
          const prevInsert = resolveInsertAnywhere(preMutationDoc, insertId);
          const nextInsert = resolveInsertAnywhere(
            nextGeometryDocument,
            insertId,
          );
          if (prevInsert && nextInsert) {
            const attributesChanged =
              JSON.stringify(prevInsert.attributes) !==
              JSON.stringify(nextInsert.attributes);
            const propsChanged =
              prevInsert.layer !== nextInsert.layer ||
              serializeColor(prevInsert.color) !==
                serializeColor(nextInsert.color) ||
              prevInsert.linetype !== nextInsert.linetype ||
              prevInsert.lineweight !== nextInsert.lineweight;
            if (attributesChanged || propsChanged) {
              inverseCommand = {
                type: "geometry/update-attribute-values",
                source: "system",
                insertId,
                updates: [],
                __restoreAttributes: {
                  attributes: prevInsert.attributes.map((a) => ({ ...a })),
                  layer: prevInsert.layer,
                  color: prevInsert.color,
                  linetype: prevInsert.linetype,
                  lineweight: prevInsert.lineweight,
                },
              } as unknown as EditorCommand;
            }
          }
        }
        // XREF suite (ABB-S1 round-3 F3) — explicit SNAPSHOT-RESTORE inverse for
        // every `file/x*` command. The package `computeInverse` has no xref case
        // (returns null) — the mutations make complex multi-field changes (INSERT
        // removal across every space + nested block for XDETACH; flag flip +
        // registry drop for XBIND; registry clip/status edits for XCLIP/XUNLOAD)
        // that no parametric inverse can reconstruct. The correct, complete
        // inverse is a verbatim restore of the four xref-bearing slices captured
        // from `preMutationDoc` (`docForInverse` IS preMutationDoc here — the
        // command is in `needsPreMutationDoc`). The geometry reducer's
        // `file/xref-restore-snapshot` case re-assembles + re-validates them, so
        // UNDO reverts the xref edit exactly and REDO replays the recorded forward
        // (AutoCAD parity — XATTACH/XDETACH/XCLIP are undoable). No-op (leave null)
        // when the forward changed none of the four slices (a silent reject /
        // identity edit) so an idle xref command pushes no empty history entry.
        if (
          inverseCommand === null &&
          (command.type === "file/xattach" ||
            command.type === "file/xbind" ||
            command.type === "file/xunload" ||
            command.type === "file/xreload" ||
            command.type === "file/xdetach" ||
            command.type === "file/xclip")
        ) {
          const slicesChanged =
            preMutationDoc.blocks !== nextGeometryDocument.blocks ||
            preMutationDoc.blockRecords !== nextGeometryDocument.blockRecords ||
            preMutationDoc.xrefRegistry !==
              nextGeometryDocument.xrefRegistry ||
            preMutationDoc.spaces !== nextGeometryDocument.spaces;
          if (slicesChanged) {
            inverseCommand = {
              type: "file/xref-restore-snapshot",
              source: "system",
              blocks: preMutationDoc.blocks,
              blockRecords: preMutationDoc.blockRecords,
              xrefRegistry: preMutationDoc.xrefRegistry,
              // PR #362 round-5 (F4 ROOT) — `preMutationDoc.spaces` are COMPACT-CACHE
              // read-through wrappers whose `entities` getter reads the LIVE cache.
              // Storing them verbatim makes the "snapshot" NOT frozen: when undo
              // later feeds these spaces to `assembleDrawingDocument`
              // (geometryReducer `file/xref-restore-snapshot`), the getter reads the
              // CURRENT (post-mutation / post-detach) cache → undo restores current
              // entities, not the pre-mutation ones. Freeze the spaces into CONCRETE
              // entity arrays so the inverse restores the true pre-mutation state.
              spaces: materializeDocumentSpacesConcretely(preMutationDoc).spaces,
            } as unknown as EditorCommand;
          }
        }
        // ── Lane J (D3-1) — style/layer TABLE + layout-plot snapshot inverse ──
        // The eight direct-set → dispatch commands (add/update dimension /
        // multileader / text style + layer, set-layout-plot-settings) AND the
        // four dispatch-routed delete/rename siblings all mutate document SLICES
        // (a whole styleTables edit; delete/rename ALSO remap every referencing
        // entity's styleName/layer across the spaces; plot-settings edits
        // layouts) that no closed-form parametric inverse can reconstruct. Like
        // the xref suite above, the complete inverse is a SNAPSHOT RESTORE of the
        // touched slices captured from `preMutationDoc` — `computeInverse` has no
        // case for these (returns null), so build it here where BOTH docs are in
        // scope. `docHeaderChanged` gates it to a REAL table/layout change
        // (value-based fingerprint), so a delete-of-a-nonexistent style / a
        // same-values update pushes no empty history entry (undo/redo cleared by
        // the standard path). AutoCAD parity — LAYER / DIMSTYLE / MLEADERSTYLE /
        // text-style / PAGESETUP edits are undoable.
        const isStyleTableTableCommand =
          command.type === "header/add-dimension-style" ||
          command.type === "header/update-dimension-style" ||
          command.type === "header/add-multileader-style" ||
          command.type === "header/update-multileader-style" ||
          command.type === "header/update-text-style" ||
          command.type === "header/add-layer" ||
          command.type === "header/update-layer" ||
          command.type === "header/delete-dimension-style" ||
          command.type === "header/rename-dimension-style" ||
          command.type === "header/delete-multileader-style" ||
          command.type === "header/delete-layer" ||
          command.type === "header/rename-layer";
        // Only the delete/rename siblings — and, since @codex PR #794 round 15
        // P2, `header/update-text-style` — CAN remap entities (styleName/layer
        // refs across spaces, or a CLEARED MTEXT measurement); the remaining
        // add/update commands touch styleTables only.
        // @codex P2 "Avoid snapshotting spaces when no refs were remapped" — the
        // command TYPE is not enough: a delete of an UNUSED style / a rename of a
        // style with NO referencing entities remaps NOTHING, so materialising +
        // freezing EVERY space into the undo entry is O(all-entities) waste and a
        // huge history payload (crippling on a cache-backed / large drawing). Gate
        // the `spaces` snapshot on the ACTUAL mutation signal —
        // `dispatchMutations` carries `updated` (styleName/layer remapped) /
        // `removed` (never for these, but defensive) records ONLY when an entity
        // truly changed. When nothing was remapped the `styleTables` snapshot
        // alone fully inverts the forward, and the restore reducer (keyed on the
        // ABSENCE of `spaces`) also skips its per-space diff.
        const isEntityRemapTableCommand =
          command.type === "header/delete-dimension-style" ||
          command.type === "header/rename-dimension-style" ||
          command.type === "header/delete-multileader-style" ||
          command.type === "header/delete-layer" ||
          command.type === "header/rename-layer" ||
          // @codex PR #794 round 15 P2 — a text-style edit that changes the
          // glyph metrics CLEARS groups 42/43 on every MTEXT drawn in that
          // style (`styleTablesReducer::sweepMtextMeasurementsForStyle`). The
          // pair has NO property label, so nothing but the pre-mutation
          // `spaces` snapshot can bring it back — without this the undo would
          // restore the FONT and leave the measurement lost, which is the
          // lossy-edit failure mode round 14's entity fix also had to avoid.
          // Still gated on `forwardRemappedEntities`, so a style edit that
          // cleared nothing (or a non-layout one) carries no spaces payload.
          command.type === "header/update-text-style";
        const forwardRemappedEntities =
          dispatchMutations.updated.length > 0 ||
          dispatchMutations.removed.length > 0;
        // @codex P2 — when a dimension-style delete/rename re-points the CURRENT
        // DIMSTYLE pointer at the chokepoint (the current style WAS the affected
        // one), carry the PRE-mutation pointer into the inverse so UNDO restores
        // it in lockstep with the table (the pointer is SHELL state the
        // document-slice snapshot cannot reach). Present ONLY when the pointer
        // actually moves — an unrelated delete/rename omits it. Exact-trimmed
        // compare mirrors both the reducer's entity remap and the forward
        // reconcile (`reconcileCurrentDimStyleAgainstTable`).
        const preDimStylePointer =
          store.state.propertyWorkflows.defaultObjectProperties[
            "Dimension style"
          ];
        const affectedDimStyleName =
          command.type === "header/rename-dimension-style"
            ? command.oldName.trim()
            : command.type === "header/delete-dimension-style"
              ? command.styleName.trim()
              : undefined;
        const carryPreDimStylePointer =
          affectedDimStyleName !== undefined &&
          preDimStylePointer === affectedDimStyleName;
        // @codex P2 — the value the FORWARD reconcile set the pointer to (its
        // "automatic value"): rename → the new name, delete → the CANONICAL
        // "Standard" spelling in the post-delete table (mirrors
        // `reconcileCurrentDimStyleAgainstTable` EXACTLY — it MUST equal what the
        // reconcile set, else the undo guard `current === forwardValue` below misses
        // when the imported table spells the mandatory style `STANDARD`/mixed case).
        // The UNDO reconcile restores `currentDimensionStyle` ONLY when the live
        // pointer STILL equals this — so a deliberate `Set Current` to another live
        // style AFTER the table edit (a non-undoable sysvar change) is not clobbered.
        const forwardDimStylePointerValue =
          command.type === "header/rename-dimension-style"
            ? command.newName.trim()
            : canonicalStandardDimStyleName(
                nextGeometryDocument.styleTables?.dimensionStyles,
              );
        // T7 (agent-bridge), local Codex round-5 P2 + round-9 P2 — the
        // CLAYER sibling of the DIMSTYLE pointer carry above.
        // `header/rename-layer` is the ONLY style-table command that
        // re-points CLAYER (delete-layer rejects deleting the current layer
        // at the wrapper, so it never moves the pointer — see the
        // CLAYER-reconcile module comment in editorStoreInitialState.ts).
        //
        // Round-9 fix — carry the old/new names UNCONDITIONALLY whenever the
        // rename actually succeeded, NOT gated on "CLAYER === oldName AT
        // RENAME TIME" (the round-5 version). `setCurrentLayer` dispatches a
        // `properties/apply` with an EMPTY selection — the SAME
        // current-pointer mechanism `setCurrentDimensionStyle` uses — and
        // `computeInverse` deliberately returns `null` for it
        // (`entityIds.length === 0` early-return, geometryMutations.ts), so
        // a `setCurrentLayer` call pushes NO history entry of its own (it is
        // a non-undoable sysvar-style pointer set, mirroring AutoCAD CLAYER).
        // That means a rename of a layer that was NOT current, followed by
        // `setCurrentLayer(newName)`, then Undo reaches THIS inverse
        // directly — the live CLAYER pointer at undo time is `newName` even
        // though it was something else entirely when the rename itself ran.
        // Gating the carry on rename-time state therefore silently OMITTED
        // `currentLayerName`, and the undo-time reconcile's generic
        // "missing layer → 0" fallback stranded CLAYER on "0" instead of the
        // restored `oldName`. Carrying unconditionally defers the "does this
        // undo actually need to move CLAYER" decision entirely to
        // `reconcileCurrentLayerAgainstTable`'s undo branch, which gates on
        // the LIVE pointer AT UNDO TIME (`currentLayer === forwardValue`) —
        // the only timing that is actually correct, since an intervening
        // non-history-tracked `setCurrentLayer` can move the pointer between
        // the rename and the undo.
        // Local Codex round-22 P1 (github PR #527 review) — resolve the
        // CANONICAL pre-rename name from `preMutationDoc` (case-insensitive
        // lookup, matching the reducer's own `namesEqualCaseInsensitive`
        // oldName resolution — round-21), NOT the raw `command.oldName.trim()`
        // verbatim. The AI/raw dispatch's `oldName` may differ only in case
        // from the row's TRUE stored spelling (e.g. oldName:"walls" against a
        // table row actually named "Walls") — undo must restore CLAYER to the
        // row's real casing, not to whatever casing the command happened to
        // carry, else `currentLayerName` below would carry a string that
        // matches NO row in the restored table (a dangling case-variant CLAYER
        // after undo).
        const canonicalOldLayerName =
          command.type === "header/rename-layer"
            ? (preMutationDoc.styleTables?.layers ?? []).find((l) =>
                namesEqualCaseInsensitive(l.name, command.oldName.trim()),
              )?.name
            : undefined;
        const trimmedNewLayerName =
          command.type === "header/rename-layer"
            ? command.newName.trim()
            : undefined;
        const nextLayerTableNames =
          nextGeometryDocument.styleTables?.layers ?? [];
        const layerRenameSucceeded =
          canonicalOldLayerName !== undefined &&
          trimmedNewLayerName !== undefined &&
          nextLayerTableNames.some((l) => l.name === trimmedNewLayerName) &&
          !nextLayerTableNames.some((l) => l.name === canonicalOldLayerName);
        if (
          inverseCommand === null &&
          isStyleTableTableCommand &&
          docHeaderChanged(preMutationDoc, nextGeometryDocument)
        ) {
          inverseCommand = {
            type: "header/restore-style-tables-snapshot",
            source: "system",
            // `styleTables` is a PLAIN document field (only `spaces` is the
            // cache-backed read-through), so a direct reference is a frozen
            // pre-mutation snapshot — no materialization needed.
            styleTables: preMutationDoc.styleTables,
            ...(isEntityRemapTableCommand && forwardRemappedEntities
              ? {
                  // PR #362 round-5 (F4 ROOT) precedent — `preMutationDoc.spaces`
                  // are compact-cache read-through wrappers whose `entities`
                  // getter re-reads the LIVE (post-`commitMutation`) cache. Freeze
                  // them into concrete entity arrays so undo restores the true
                  // pre-mutation entity styleName/layer refs, not the current ones.
                  // Only reached when the forward actually remapped ≥1 entity.
                  spaces: materializeDocumentSpacesConcretely(preMutationDoc)
                    .spaces,
                }
              : {}),
            ...(carryPreDimStylePointer
              ? {
                  currentDimensionStyle: preDimStylePointer,
                  currentDimensionStyleForwardValue: forwardDimStylePointerValue,
                }
              : {}),
            // T7 (agent-bridge), local Codex round-5 P2 + round-9 P2 — the
            // CLAYER sibling of the DIMSTYLE pointer carry directly above.
            ...(layerRenameSucceeded
              ? {
                  currentLayerName: canonicalOldLayerName,
                  currentLayerForwardValue: trimmedNewLayerName,
                }
              : {}),
            // Local Codex round-2 + round-4 P2 — `header/rename-layer` also
            // remaps a BLOCK-DEFINITION's own layer + its children
            // (`remapBlockLayerReferences`, styleTablesReducer.ts). `blocks`
            // is a PLAIN document field (like `styleTables` above — no
            // compact-cache read-through), so a direct pre-mutation
            // reference is already a frozen snapshot. The reducer returns
            // the SAME `blocks` reference when nothing matched, so a
            // reference-identity check is the exact "did the forward touch a
            // block" signal — carry it ONLY then, so an ordinary rename that
            // never touches a block stays byte-identical to before this fix.
            // @codex PR #794 round 15 P2 — `header/update-text-style` joins it:
            // `sweepMtextMeasurementsForStyle` also clears 42/43 on BLOCK-
            // DEFINITION MTEXT children (`blockChildEntitySchema` admits MTEXT,
            // and the block bounds path reads `mtextBoxSize` for them). Those
            // children have no `spaceId`, so `EntityMutation.updated` cannot
            // carry them and this snapshot is their ONLY undo — same reference-
            // identity gating, so a style edit that touched no block stays
            // byte-identical.
            ...((command.type === "header/rename-layer" ||
              command.type === "header/update-text-style") &&
            preMutationDoc.blocks !== nextGeometryDocument.blocks
              ? { blocks: preMutationDoc.blocks }
              : {}),
            // Local Codex round-10 P2 — the top-level ENTITIES-section RAW
            // (unsupported) siblings' group-8 layer tag sibling of the
            // `blocks` carry directly above (`remapRawEntityGroupsLayer`,
            // styleTablesReducer.ts). Same reference-identity gating.
            ...(command.type === "header/rename-layer" &&
            preMutationDoc.rawEntityGroups !==
              nextGeometryDocument.rawEntityGroups
              ? { rawEntityGroups: preMutationDoc.rawEntityGroups }
              : {}),
          } as unknown as EditorCommand;
        }
        // Lane J — layout-plot inverse: restore the PRE-mutation `layouts` +
        // `layoutsBySpaceId` (a materialised LAYOUT object is REMOVED on undo by
        // restoring the prior dicts, not just the settings). Plain fields — direct
        // reference snapshot. `docHeaderChanged` gates on the layouts fingerprint.
        if (
          inverseCommand === null &&
          command.type === "header/set-layout-plot-settings" &&
          docHeaderChanged(preMutationDoc, nextGeometryDocument)
        ) {
          inverseCommand = {
            type: "header/restore-style-tables-snapshot",
            source: "system",
            layouts: preMutationDoc.layouts,
            layoutsBySpaceId: preMutationDoc.layoutsBySpaceId,
          } as unknown as EditorCommand;
        }
      }

      // Capture the pre-mutation property snapshot before commitMutation.
      const propertiesApplyPrevEntityById =
        snapshotPropertiesApplyPrevEntities(
          shouldRecordHistory,
          effectivePropertiesApplyCommand,
          preMutationDoc,
        );

      // Phase 4 Wave 2 (codex F3 follow-up) — route every dispatch's
      // cache writes through the holder keyed by the CURRENT
      // document's id. The active id was previously left at whatever
      // `setActiveDocument(DEFAULT_DOCUMENT_ID)` (or an earlier import
      // path) set; a bare dispatch on a fresh store therefore went to
      // the default holder while `state.geometryDocument.id` pointed
      // at the initial-seed id — a hidden mismatch that became
      // visible the moment multi-doc routing landed. Sync the active
      // id BEFORE commitMutation so the cache write + the wrap below
      // observe the same holder.
      const docIdForCache = nextGeometryDocument.id || DEFAULT_DOCUMENT_ID;
      if (getActiveDocumentId() !== docIdForCache) {
        setActiveDocument(docIdForCache);
      }

      // Phase 3b PR #6 — commit mutations into the entity index +
      // compact cache BEFORE the shell resync.
      // `syncShellStateFromGeometryDocument` reads through the
      // metadata index and the compact cache when rebuilding entity
      // previews; committing first guarantees those structures
      // mirror `nextGeometryDocument` before the rebuild reads them.
      // `historyReplay` — GitHub Codex review PR #582 finding 1. The
      // entity-provenance ledger needs to tell "the history machinery is
      // putting a previously-existing entity back" apart from "the user
      // just authored something new", and `__bypassHistory` (computed as
      // `isBypassHistory` above) is exactly that signal: every replay
      // dispatch — `history/undo`, `history/redo`, the batch rollback and
      // both `history/replay-*` handlers — re-dispatches its stored
      // inverse/forward command with that flag, and nothing else sets it.
      // Passing the real signal is what let the ledger stop guessing from
      // content equality (two different entities can be identical).
      const commit = commitMutation(
        store.geometryDocument,
        nextGeometryDocument,
        dispatchMutations,
        { historyReplay: isBypassHistory },
      );

      // PR2 Codex P2/P3 (#217) — block definitions are mutated OUTSIDE
      // commitMutation's space-diff choke point: BEDIT/BSAVE/REFEDIT *and*
      // ordinary geometry commands issued while a block-edit session is active
      // pipe through syncBlockEditSpaceToDefinition, which rewrites
      // blocks[name].entities and returns a NEW `blocks` object. The guard must
      // therefore key on the blocks reference (not the command type, which is
      // null for ordinary in-session edits). A precise prev→next diff is fed
      // through applyBatch — exact-by-(container,id) removal (collision-safe even
      // if a block name equals a space id) and only changed blocks are walked
      // (no 685k space scan).
      if (nextGeometryDocument.blocks !== preMutationDoc.blocks) {
        refreshBlockDefDependencyIndexContainers(
          preMutationDoc,
          nextGeometryDocument,
        );
      }

      // Phase 3b PR #6 — Resync runs unconditionally. The legacy
      // `needsShellResyncFromGeometry` allowlist (~140 lines, ~80 case
      // arms) was a maintenance hazard: every new mutation command had
      // to be added to the list or the shell preview would go stale.
      // With the mutations payload threading from the geometry reducer,
      // the helper itself decides whether the entity preview rebuild is
      // needed (no-op mutations skip the rebuild; structural changes
      // trigger it). See `syncShellStateFromGeometryDocument`
      // mutations-arg JSDoc for the full contract.
      // ARRAYPATH-OUTCOME — `geometry/create-arraypath` is optimistic in the
      // shell reducer: it cannot know whether `handleCreateArrayPath` will
      // actually append clones, because the document reducer has not run at
      // that point in this updater. It DID run by now, and `dispatchMutations`
      // is its declared diff — so the row is corrected here from that ONE
      // number rather than from a second, speculative construction of the same
      // array (skill `state-mutation-integrity`: the audit row is a CONSUMER of
      // the mutation outcome, never a second diff). Named refusals raised by
      // `arrayPathDeclinedAudit` are pushed as `"warning"` and are left alone;
      // only the `"success"` row this dispatch pushed can be downgraded, and
      // only in place, so the ring order is byte-identical to before.
      const stateAfterArrayPathOutcome = applyArrayPathOutcomeToAudit(
        nextState,
        command,
        dispatchMutations.added.length,
        auditSequenceBefore,
      );
      const resyncedStateAfterGeometryResync =
        syncShellStateFromGeometryDocument(
          stateAfterArrayPathOutcome,
          nextGeometryDocument,
          {
            mutations: dispatchMutations,
            // #518 — see `geoActiveSpaceAuthoritative` computation above.
            geoActiveSpaceAuthoritative,
          },
        );

      // #518 (Codex P2, round-1 review) — clear selection on a
      // geo-authoritative space switch, mirroring `shell/activate-space`'s
      // AutoCAD-parity selection reset (shellUiReducer.ts's
      // `case "shell/activate-space"`: entityIds -> [], previousPickset <-
      // the just-cleared ids). BEDIT/BCLOSE/BSAVE/TESTBLOCK/CLOSETESTBLOCK/
      // REFEDIT/REFCLOSE bypass the shell reducer entirely (no `space/*`
      // case there), so without this the previous space's selected entity
      // ids would silently survive into (or out of) the synthetic edit
      // space — `selectSelectedEntities` resolves ids globally, so
      // Inspector / block-authoring actions could target an entity outside
      // the newly-active space. Only fires when THIS dispatch's
      // block-edit-family command actually moved `activeSpaceId` (BSAVE,
      // which stays in-session, leaves selection untouched).
      const resyncedStateBeforeSelectionPreservation = geoActiveSpaceAuthoritative
        ? {
            ...resyncedStateAfterGeometryResync,
            selection: {
              ...resyncedStateAfterGeometryResync.selection,
              entityIds: [],
              previousPickset:
                resyncedStateAfterGeometryResync.selection.entityIds,
            },
          }
        : resyncedStateAfterGeometryResync;

      // AutoCAD parity: property-only edits (dynamic/set, dynamic/reset)
      // must preserve selection — otherwise the Inspector unmounts on
      // every edit, making the editable customProperties feature
      // unusable. Shell resync's default wipe (see
      // storeHelpers.ts:99-101) is correct for structural changes
      // (delete/add/move/explode), but wrong for pure property
      // mutations that only update a single entity's customProperties.
      // AutoCAD never drops selection on Properties-panel edits; we
      // match that contract here.
      const selectionPreservingCommands = new Set<
        EditorCommand["type"] | DynamicBlockCommand["type"]
      >([
        "dynamic/set",
        "dynamic/reset",
        // Phase N.4 — same selection-preserving rule for representation
        // override edits (Properties-panel-style mutations).
        "geometry/set-insert-parameter-override",
        "geometry/clear-insert-parameter-override",
        "geometry/set-insert-visibility-state",
      ]);
      const resyncedStateBeforePurgeCleanup = selectionPreservingCommands.has(
        command.type,
      )
        ? {
            ...resyncedStateBeforeSelectionPreservation,
            selection: {
              ...resyncedStateBeforeSelectionPreservation.selection,
              entityIds: store.state.selection.entityIds,
            },
          }
        : resyncedStateBeforeSelectionPreservation;

      // Codex P2-3 (r5, PR #311) — drop CACHE-ONLY dead ids from the
      // selection + previousPickset. The shell reducer's
      // `shell/remove-space` arm filters through preview-derived
      // `removedEntityIds`, but in a streamed/cache-backed document the
      // deleted space's entities have NO shell previews — they live only
      // in the compact cache / metadata index, whose purge happens at the
      // `commitMutation` space-diff seam above. `purgedEntityIds` is that
      // seam's full dead-id set (one truth — no second membership index
      // bolted onto the reducer), so filtering here catches exactly the
      // ids the preview-based filter cannot see. Empty set on every
      // dispatch that does not remove a space → no-op fast path.
      const purgedEntityIds = commit.purgedEntityIds;
      let resyncedState = resyncedStateBeforePurgeCleanup;
      if (purgedEntityIds.size > 0) {
        const selection = resyncedStateBeforePurgeCleanup.selection;
        const liveSelectedIds = selection.entityIds.filter(
          (id) => !purgedEntityIds.has(id),
        );
        const livePickset = selection.previousPickset.filter(
          (id) => !purgedEntityIds.has(id),
        );
        if (
          liveSelectedIds.length !== selection.entityIds.length ||
          livePickset.length !== selection.previousPickset.length
        ) {
          resyncedState = {
            ...resyncedStateBeforePurgeCleanup,
            selection: {
              ...selection,
              entityIds: liveSelectedIds,
              previousPickset: livePickset,
            },
          };
        }
      }

      // Lane J (D3-1) — @codex round-2 P2 (findings 2 + 3). Keep CLAYER
      // (`defaultObjectProperties.Layer`) referencing a live layer-table entry
      // after ANY dispatch that shrank the layer table — a forward delete, an
      // undo that restores a pre-add table, or a redo that replays a delete
      // of the (since-made-)current layer past the wrapper guard. T7 (agent-
      // bridge), local Codex round-5 P2 — a `header/rename-layer` of the
      // CURRENT layer is handled FIRST as a forward-CARRY (CLAYER follows the
      // rename to `newName`, matching `reconcileCurrentDimStyleAgainstTable`'s
      // rename branch — a rename is not a removal, so AutoCAD keeps the
      // renamed layer current under its new name). One reconcile at the
      // chokepoint covers forward + undo + redo (all route here); resets to
      // "0" only when the current layer truly vanished, else returns the
      // same reference.
      resyncedState = reconcileCurrentLayerAgainstTable(
        resyncedState,
        command,
        nextGeometryDocument,
      );

      // @codex P2 — the DIMSTYLE-sysvar sibling of the CLAYER reconcile above.
      // Keep `defaultObjectProperties["Dimension style"]` in lockstep with a
      // dimension-style delete/rename AT the chokepoint (covers forward + undo +
      // redo in one place): a forward/redo rename re-points old→new, a delete
      // falls the pointer back to "Standard", and the delete/rename UNDO inverse
      // (`header/restore-style-tables-snapshot` carrying `currentDimensionStyle`)
      // restores the pre-mutation pointer — so undo never strands the pointer at
      // the now-missing new/deleted name (new dimensions would otherwise fall back
      // to the first style instead of the restored current style).
      resyncedState = reconcileCurrentDimStyleAgainstTable(
        resyncedState,
        command,
        nextGeometryDocument,
      );

      // Enrich every Dimension shell entity with the resolved DIMVAR
      // properties from the dimension styles table. The shell reducer
      // populates dimension previews with positional/measurement data
      // only — it does not look up the referenced dim style. Without
      // this enrichment the SVG renderer always falls back to default
      // DIMVARs (DIMTXT=2.5, no oblique arrows, etc.) regardless of
      // which style is currently applied to the dimension.
      const dimEnrichedEntities = enrichDimensionEntities(
        resyncedState.drawing.entities,
        nextGeometryDocument.styleTables?.dimensionStyles ?? [],
      );
      // Same enrichment pass for Multileader entities — without this
      // the renderer would never see updated MLEADERSTYLE values when
      // the user edits a style or applies a different one.
      const enrichedEntities = enrichMultileaderEntities(
        dimEnrichedEntities,
        nextGeometryDocument.styleTables?.multileaderStyles ?? [],
      );

      // Phase 4 Wave 2 Task 1 — assemble first (Zod parse runs in
      // dev/test), THEN overlay the read-through wrapper so the stored
      // document's `spaces[i].entities` materializes from the compact
      // cache on each access. Wrapping BEFORE `assembleDrawingDocument`
      // would trigger the entities getter during Zod parse and either
      // double-walk or break the validateDrawingModel invariant check.
      // The wrapper falls back to the plain `space.entities` array if
      // the compact cache for the active doc / space is empty, so the
      // small-drawing test path that never went through the cache
      // populate flow keeps reading the array directly.
      //
      // T6d-2 follow-up (live perf — @codex P2) — `assembleDrawingDocument`
      // unconditionally re-validates the WHOLE document: in PROD it runs an
      // O(N) `validateDrawingModel` Set-walk over EVERY `space.entities`,
      // which fires the read-through getter and re-materializes all 685k
      // entities from the compact store on EVERY dispatch — nullifying the
      // T6d-2 fast path (and T6c / T6d-1) at the store seam. For an
      // UPDATED-ONLY dispatch (no add, no remove, ≥1 update, and the block
      // slice unchanged so no BEDIT-in-session space re-parent) the
      // validateDrawingModel invariants — unique ids, entity.spaceId===space.id,
      // unique space ids, activeSpaceId present — hold by construction, so
      // `assembleDrawingDocumentInPlace` returns `next` untouched in PROD
      // (zero `space.entities` iteration → zero materialization). Creates /
      // deletes / structural edits have added/removed > 0 → full assemble
      // (correct). No-ops have updated === 0 → full assemble. The block-slice
      // identity compare against `preMutationDoc` is the BEDIT-in-session
      // safety: an in-session ordinary edit rewrites `blocks[name].entities`
      // (new `blocks` ref) and so falls back to the full path. Nothing between
      // the reducer return and here consumes `assembledDocument` — it is only
      // fed to `wrapDocumentAsReadThrough` below.
      const isUpdatedOnlyMutation =
        dispatchMutations.added.length === 0 &&
        dispatchMutations.removed.length === 0 &&
        dispatchMutations.updated.length > 0 &&
        nextGeometryDocument.blocks === preMutationDoc.blocks;
      const assembledDocument = isUpdatedOnlyMutation
        ? assembleDrawingDocumentInPlace(
            {
              ...nextGeometryDocument,
              activeSpaceId: resyncedState.drawing.activeSpaceId,
            },
            dispatchMutations.updated.map((u) => u.prev.id),
          )
        : assembleDrawingDocument({
            ...nextGeometryDocument,
            activeSpaceId: resyncedState.drawing.activeSpaceId,
          });
      // Phase 4 Wave 2 (codex F3 follow-up) — wrap with the same doc
      // id we just routed cache writes through. The pre-commit branch
      // above already called `setActiveDocument(docIdForCache)`, so
      // `getActiveDocumentId()` is in lockstep; we pass the explicit
      // value to keep the read/write keys obvious at the call site.
      const wrappedDocument = wrapDocumentAsReadThrough(
        assembledDocument,
        docIdForCache,
      );

      // GRIPS-SUBSYSTEM (§5.11) / Phase 4.1.f — full property-apply inverse.
      // The command-engine `computeInverse` `properties/apply` branch (run
      // at line ~2900) only snapshots COMMON_PROP_FIELD keys (Layer/Color/…)
      // because those are the only keys backed by DrawingEntity base fields
      // it can read from the document. Entity-GEOMETRY property keys (End X/Y,
      // Radius, Vertices, Control points, …) live ONLY on the EntityPreview
      // property bag, so a grip-drag or Properties-panel geometry edit yielded
      // a NULL inverse → no history entry → Ctrl+Z was a no-op. Here we have
      // BOTH the pre-mutation previews (store.state.drawing.entities) and the
      // post-mutation previews (resyncedState.drawing.entities), so we rebuild
      // the inverse from the prev/next property diff — the true "snapshot
      // whatever this apply mutated" one-truth inverse covering common AND
      // geometry keys (and grip pseudo→real translated patches alike).
      if (shouldRecordHistory && effectivePropertiesApplyCommand !== null) {
        const prevById = new Map(
          store.state.drawing.entities.map((e) => [e.id, e]),
        );
        // Codex P2-A — RAW-precision geometry snapshots for the inverse.
        // The EntityPreview property strings are formatNumber-rounded (3
        // decimals); replaying that on undo loses precision. Build
        // full-precision geometry-key maps from the pre/post DOCUMENT
        // entities so the inverse restores EXACT coordinates. Common
        // (non-geometry) keys keep coming from the formatted preview — they
        // are not precision-sensitive.
        //
        // Codex P2 (#317) — SCOPE the raw snapshot to the TOUCHED entity ids
        // only. `properties/apply` names its targets in `command.entityIds`,
        // so a grip drag on one entity must not walk (and serialize) every
        // entity in a 1000-entity drawing on EVERY pointermove. We stop the
        // per-space scan as soon as all touched ids are found so we never
        // materialize cache-backed spaces past the ones holding the targets.
        // @codex P1 (#420) — the TOUCHED ids come from the RESOLVED
        // `properties/apply` (its `entityIds`), so a typed `matchprop/apply`
        // resolved to a `properties/apply` (and the CLI MATCHPROP / CHPROP text
        // arms) snapshot the correct targets — not `command.entityIds`, which is
        // absent on a `matchprop/apply` / `commandline/submit` wrapper.
        const touchedIds = new Set(
          effectivePropertiesApplyCommand.entityIds ?? [],
        );
        const rawByIdFromDoc = (
          doc: typeof preMutationDoc,
        ): Map<string, Record<string, string>> => {
          const m = new Map<string, Record<string, string>>();
          if (touchedIds.size === 0) return m;
          for (const space of doc.spaces) {
            for (const e of space.entities) {
              if (!touchedIds.has(e.id)) continue;
              m.set(e.id, rawGeometryPropertyValues(e));
              if (m.size === touchedIds.size) return m;
            }
          }
          return m;
        };
        // D6 fix — `rawPrev` is derived from the EARLY (pre-`commitMutation`)
        // `propertiesApplyPrevEntityById` snapshot, NOT from a live read of
        // `preMutationDoc` at this point (see that snapshot's JSDoc above for
        // why a read here would silently observe POST-mutation data).
        const rawPrev = new Map<string, Record<string, string>>();
        for (const [id, e] of propertiesApplyPrevEntityById) {
          rawPrev.set(id, rawGeometryPropertyValues(e));
        }
        const rawNext = rawByIdFromDoc(nextGeometryDocument);
        // EPIC-COLOR-UNION S1 round-2 P2-B — LOSSLESS colour for the undo inverse.
        // The app undo path builds its inverse from the prev/next preview property
        // diff, where `Color` is the LOSSY display string ("#rrggbb"/"ByLayer").
        // Undoing a colour edit on a rich-colour entity (true-colour-with-ACI or
        // color-book) therefore replayed the lossy value and DROPPED the 62/430
        // siblings. Mirror the raw-geometry overlay: snapshot the FULL-precision
        // lossless colour (`serializeColor(coerceColor(e.color))`) from the
        // pre/post DOCUMENT entities and overlay it onto the inverse `Color` key.
        // For the common cases `serializeColor === colorToLegacyString`, so the
        // overlaid value equals the existing preview `Color` → no spurious diff
        // (`buildPropertiesApplyInverseFromSnapshots` restores `Color` only when
        // next!==prev, Category-B preserved). For a rich-colour edit the prev
        // carries the `!EC:` payload, which the forward+inverse apply consumes via
        // `commonFieldPatchValue → deserializeColor` (already lossless-aware).
        const colorByIdFromDoc = (
          doc: typeof preMutationDoc,
        ): Map<string, string> => {
          const m = new Map<string, string>();
          if (touchedIds.size === 0) return m;
          for (const space of doc.spaces) {
            for (const e of space.entities) {
              if (!touchedIds.has(e.id)) continue;
              m.set(
                e.id,
                serializeColor(coerceColor((e as { color?: unknown }).color)),
              );
              if (m.size === touchedIds.size) return m;
            }
          }
          return m;
        };
        // D6 fix — same reason as `rawPrev` above: derive from the early
        // pre-mutation snapshot, not a live `preMutationDoc` read here.
        const colorPrev = new Map<string, string>();
        for (const [id, e] of propertiesApplyPrevEntityById) {
          colorPrev.set(
            id,
            serializeColor(coerceColor((e as { color?: unknown }).color)),
          );
        }
        const colorNext = colorByIdFromDoc(nextGeometryDocument);
        const pairs: PropertyApplyPrevNext[] = [];
        // Codex P2-4 (#317) — build inverse pairs from the TOUCHED ids only.
        // A geometry resync rebuilds EVERY preview object (new identity), so
        // `prev !== next` for all entities; walking `resyncedState.drawing.
        // entities` therefore diffed the whole drawing on every grip
        // pointermove (the O(all) churn the touched-id raw-snapshot fix was
        // meant to remove). Index the post-mutation previews once, then visit
        // only the ids this `properties/apply` named.
        const nextById = new Map(
          resyncedState.drawing.entities.map((e) => [e.id, e]),
        );
        // @codex P2-A (#420 round-3) — on large / streamed imports BOTH shell
        // preview arrays are emptied above `SHELL_ENTITY_PREVIEW_THRESHOLD`, so
        // `prevById` / `nextById` miss every touched id, the pairs loop skips
        // them all, and the inverse is EMPTY → undo cannot restore the targets'
        // prior properties (the same threshold round-2 handled for source-read +
        // target-write). Backfill the missing touched previews from the PRE
        // (`propertiesApplyPrevEntityById`, the early frozen snapshot — D6
        // fix) / POST (`nextGeometryDocument`) document. The POST side still
        // routes through the coherent shell↔document resolver —
        // `() => undefined` forces the document projection, `null`
        // cacheLookup — because `nextGeometryDocument` carries the reducer's
        // concrete (never live-getter-backed) arrays, so reading it here is
        // always safe regardless of timing. No-op on small imports (every
        // touched id is already in the shell).
        for (const id of touchedIds) {
          if (!prevById.has(id)) {
            // D6 fix — resolve from the early pre-mutation entity snapshot
            // (`propertiesApplyPrevEntityById`) instead of calling
            // `resolveCoherentEntityPreview(..., preMutationDoc, ...)` here,
            // which would walk `preMutationDoc.spaces[i].entities` AFTER
            // `commitMutation` already wrote the post-mutation value into the
            // SAME live-backed cache that getter reads. `preMutationDoc`
            // itself (not its `.entities`) is still safe to pass here — style
            // tables / block records / insunits are plain document fields.
            const prevEntity = propertiesApplyPrevEntityById.get(id);
            if (prevEntity) {
              prevById.set(
                id,
                buildDocumentBackedPreview(prevEntity, preMutationDoc),
              );
            }
          }
          if (!nextById.has(id)) {
            const nextRes = resolveCoherentEntityPreview(
              id,
              () => undefined,
              nextGeometryDocument,
              null,
            );
            if (nextRes) nextById.set(id, nextRes.preview);
          }
        }
        for (const id of touchedIds) {
          const next = nextById.get(id);
          if (!next) continue;
          const prev = prevById.get(next.id);
          if (!prev || prev === next) continue;
          // Overlay the raw-precision geometry keys onto the formatted
          // preview snapshots. The forward apply consumes the SAME
          // preview-property labels (e.g. "Start X"), so the inverse stays a
          // valid `properties/apply` payload — only the precision changes.
          const rawPrevGeom = rawPrev.get(next.id);
          const rawNextGeom = rawNext.get(next.id);
          // P2-B — overlay the lossless Color as an INDEPENDENT spread (after the
          // raw-geometry overlay). Explicit `?? {}` spreads avoid the empty-`{}`-
          // truthy footgun of the prior ternary and keep the colour overlay
          // orthogonal to `rawGeometryPropertyValues` (left single-purpose).
          const colorPrevVal = colorPrev.get(next.id);
          const colorNextVal = colorNext.get(next.id);
          pairs.push({
            entityId: next.id,
            // Codex P2-2 (PR #403) — thread the target's EntityType so the
            // inverse can restore an INTRODUCED optional common key (Linetype
            // Scale / Thickness) to its schema DEFAULT (thickness default is
            // type-gated by THICKNESS_BEARING_ENTITY_TYPES).
            entityType: next.type,
            prev: {
              ...prev.properties,
              ...(rawPrevGeom ?? {}),
              ...(colorPrevVal !== undefined ? { Color: colorPrevVal } : {}),
            },
            next: {
              ...next.properties,
              ...(rawNextGeom ?? {}),
              ...(colorNextVal !== undefined ? { Color: colorNextVal } : {}),
            },
          });
        }
        inverseCommand = buildPropertiesApplyInverseFromSnapshots(pairs);

        // Codex round-4 #1/#5 (+ P2-d/P2-e) — typed-hatch restore for the
        // PALETTE path, COMPOSED with the common-prop inverse above (NOT
        // replacing it). A hatch edit through the Properties palette runs the
        // SAME translator branch as HATCHEDIT, so any typed-hatch field it
        // changes (patternName / scale / angle / hatchStyle / patternType /
        // associative / solidFill / gradient / customPatternLines / doubleLine)
        // needs a `geometry/hatchedit` restore on undo — those typed fields
        // live ONLY on the document entity, and several (raw scale/angle
        // precision, the DXF-70 solidFill flag, the 450/78 gradient/custom-line
        // blocks, an out-of-table 75/76 code, and — per N8 — a read-only-gated
        // doubleLine restore) are LOST or CORRUPTED/REJECTED by the
        // preview-string `properties/apply` inverse. But the SAME apply may
        // ALSO have changed common props (Layer / Color / Transparency /
        // LtScale), which ONLY the `properties/apply` inverse restores. So
        // whenever the apply changed any typed-hatch field on ANY touched
        // hatch, COMPOSE the common-prop inverse with a raw `geometry/
        // hatchedit` restore PER touched hatch via `history/replay-inverses`
        // (common-prop inverse FIRST, then one raw typed-hatch inverse per
        // hatch, so their raw values OVERWRITE the lossy preview-label hatch
        // keys the props inverse also carries for that same entity).
        //
        // @codex P2 (GitHub review round 1, N8) — this used to be gated on
        // `touchedIdList.length === 1`, restoring ONLY a single-hatch typed
        // apply. A multi-entity apply (e.g. a batch Properties edit or an AI
        // `properties/apply` targeting several hatches at once) that changed
        // BOTH "Pattern type" and "Double" fell through to the PLAIN
        // `properties/apply` inverse only — and replaying `Pattern
        // type=Predefined` + `Double=No` through that path gets the Double
        // restore REJECTED by the SAME readOnlyFromChanges gate the forward
        // edit relies on (Predefined is non-user-defined), leaving
        // `doubleLine=true` stuck on every touched hatch after undo. Iterate
        // ALL touched Hatch entities (not just a lone one) and compose ONE
        // raw restore per hatch that genuinely changed a typed field.
        const touchedIdList = [...touchedIds];
        const hatchInverseCmds: EditorCommand[] = [];
        for (const hatchId of touchedIdList) {
          const findHatch = (doc: typeof preMutationDoc) => {
            for (const space of doc.spaces) {
              const e = space.entities.find((x) => x.id === hatchId);
              if (e && e.type === "Hatch") return e;
            }
            return undefined;
          };
          // D6 fix — read the PRE-mutation hatch from the early snapshot;
          // `findHatch(preMutationDoc)` here would observe POST-mutation
          // data (see `propertiesApplyPrevEntityById` JSDoc above).
          const prevHatchCandidate = propertiesApplyPrevEntityById.get(hatchId);
          const prevHatch =
            prevHatchCandidate && prevHatchCandidate.type === "Hatch"
              ? prevHatchCandidate
              : undefined;
          const nextHatch = findHatch(nextGeometryDocument);
          if (!prevHatch || !nextHatch) continue;
          const jsonEq = (a: unknown, b: unknown): boolean =>
            JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
          // Build a restoring `geometry/hatchedit` carrying the PRE-mutation
          // value of EXACTLY the typed fields that CHANGED pre→post (verbatim
          // from the doc → full precision + verbatim gradient/custom-line/
          // raw-75/76). Scalars compared by `!==`; object fields by JSON.
          const hatchInverse: Record<string, unknown> = {
            type: "geometry/hatchedit",
            source: "system",
            entityId: hatchId,
          };
          let typedChanged = false;
          if (prevHatch.patternName !== nextHatch.patternName) {
            hatchInverse.patternName = prevHatch.patternName;
            // A pattern-name restore re-derives/clears the side-effect fields;
            // carry the prior solidFill / gradient / customPatternLines
            // VERBATIM so undo restores them exactly (preserve-source).
            hatchInverse.solidFill = prevHatch.solidFill ?? false;
            hatchInverse.gradient = prevHatch.gradient ?? null;
            hatchInverse.customPatternLines =
              prevHatch.customPatternLines ?? null;
            typedChanged = true;
          }
          if (prevHatch.patternScale !== nextHatch.patternScale) {
            hatchInverse.patternScale = prevHatch.patternScale;
            typedChanged = true;
          }
          if (prevHatch.patternAngle !== nextHatch.patternAngle) {
            hatchInverse.patternAngle = prevHatch.patternAngle;
            typedChanged = true;
          }
          if (prevHatch.hatchStyle !== nextHatch.hatchStyle) {
            hatchInverse.hatchStyle = prevHatch.hatchStyle;
            typedChanged = true;
          }
          if (prevHatch.patternType !== nextHatch.patternType) {
            hatchInverse.patternType = prevHatch.patternType;
            typedChanged = true;
          }
          if (prevHatch.associative !== nextHatch.associative) {
            hatchInverse.associative = prevHatch.associative;
            typedChanged = true;
          }
          // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 6) — the
          // "Double" typed descriptor's write is READ-ONLY-GATED per
          // instance (Pattern type = User defined). A `properties/apply`-
          // style undo replay carrying BOTH the reverted "Pattern type"
          // AND "Double" would have its Double restore REJECTED by that
          // SAME gate whenever the reverted Pattern type is non-user-
          // defined (`readOnlyFromChanges` evaluates the batch's OWN
          // Pattern-type value). This raw `geometry/hatchedit` scalar
          // restore bypasses per-field validation entirely — same as
          // hatchStyle/patternType/associative above — so undo always
          // lands on the EXACT pre-mutation `doubleLine`, never blocked.
          if (prevHatch.doubleLine !== nextHatch.doubleLine) {
            hatchInverse.doubleLine = prevHatch.doubleLine;
            typedChanged = true;
          }
          if (prevHatch.solidFill !== nextHatch.solidFill) {
            if (hatchInverse.solidFill === undefined) {
              hatchInverse.solidFill = prevHatch.solidFill ?? false;
            }
            typedChanged = true;
          }
          if (!jsonEq(prevHatch.gradient, nextHatch.gradient)) {
            if (hatchInverse.gradient === undefined) {
              hatchInverse.gradient = prevHatch.gradient ?? null;
            }
            typedChanged = true;
          }
          if (
            !jsonEq(
              prevHatch.customPatternLines,
              nextHatch.customPatternLines,
            )
          ) {
            if (hatchInverse.customPatternLines === undefined) {
              hatchInverse.customPatternLines =
                prevHatch.customPatternLines ?? null;
            }
            typedChanged = true;
          }
          if (typedChanged) {
            hatchInverseCmds.push(hatchInverse as unknown as EditorCommand);
          }
        }
        if (hatchInverseCmds.length > 0) {
          // COMPOSE: common-prop inverse FIRST (when present), then EVERY
          // touched hatch's raw typed inverse. Replaying in array order
          // applies the typed restores after the props restore, so the raw
          // scale/angle/75/76/solidFill/gradient/doubleLine values OVERWRITE
          // any lossy preview-label hatch keys the `properties/apply` inverse
          // also carries for that SAME entity (round-4 #1: do NOT drop the
          // common-prop restore; round-4 #5: trigger on ANY typed field incl
          // solidFill, not only gradient/customPatternLines; N8 GitHub review
          // round 1: cover EVERY touched hatch, not only a lone one).
          const batch: EditorCommand[] =
            inverseCommand !== null
              ? [inverseCommand, ...hatchInverseCmds]
              : hatchInverseCmds;
          inverseCommand = {
            type: "history/replay-inverses",
            source: "system",
            batch,
          } as unknown as EditorCommand;
        }

        // @codex round-4 P2-1 + round-5 (S15 Slice F, the D finding ESCALATED) —
        // typed-viewport RESTORE for the PALETTE undo, COMPOSED with the common-prop
        // inverse (mirrors the single-hatch block above). A viewport edit that changed
        // a DERIVED / COUPLED typed field (vpOverrideScale / viewHeight / viewCenter /
        // cannoscale / displayLocked / statusBitFlags / VP_OVERRIDES XDATA — set by
        // the Scale / View / Display-lock side-effect mutators OR the standalone-Height
        // viewHeight re-derive normalization) CANNOT be undone by the label-replay
        // `properties/apply` inverse: replaying the prior Scale LABEL through the
        // FORWARD `setViewportScaleEntity` ALWAYS re-writes vpOverrideScale + the
        // override block (so a viewport with NO override before does not round-trip),
        // and replaying the prior Height LABEL RE-RUNS the viewHeight re-derive
        // normalization (so an imported viewport whose vpOverrideScale MISMATCHES its
        // original viewHeight loses that original on undo). BOTH are FORWARD-only
        // transforms that fight the undo. RESOLUTION (@codex round-5, the "cleaner"
        // option): restore the EXACT prior typed snapshot via
        // `geometry/restore-viewport-clip` — a GENERAL in-place viewport-snapshot
        // REPLACE (despite the clip-centric name: it swaps the entity matched by id for
        // its full pre-edit `DrawingEntity`), so the undo NEVER re-runs the forward
        // normalization/mutator; vpOverrideScale ABSENCE + a mismatched-import
        // viewHeight + raw-precision frame/view fields restore VERBATIM. The replace
        // SUBSUMES the common-prop / plain-leaf inverse (whole-entity), so replay-order
        // is common-first, snapshot-restore LAST. Swept over EVERY touched viewport
        // (single AND multi-entity), one restore entry per affected viewport. Gated on
        // a coupled field actually changing so a PURE plain-leaf frame edit (Center X/Y
        // / Width / plain-Height-no-override) keeps the lighter label-replay + raw
        // overlays + the SELF-HEALING reducer clip recompute (which re-projects from the
        // restored frame fields on undo). Type-disjoint from the hatch block.
        {
          const jsonEq = (a: unknown, b: unknown): boolean =>
            JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
          const findEntityById = (
            doc: typeof preMutationDoc,
            id: string,
          ): DrawingEntity | undefined => {
            for (const space of doc.spaces) {
              const e = space.entities.find((x) => x.id === id);
              if (e) return e;
            }
            return undefined;
          };
          const findVpById = (
            doc: typeof preMutationDoc,
            id: string,
          ): DrawingEntity | undefined => {
            const e = findEntityById(doc, id);
            return e && e.type === "Viewport" ? e : undefined;
          };
          // The pre-edit TYPED SNAPSHOTS to compose into the inverse, one entry
          // per entity the label-replay `properties/apply` inverse cannot
          // restore on its own. Carried by `geometry/restore-viewport-clip`,
          // which is the repo's GENERAL in-place snapshot REPLACE (see the
          // block comment above): it matches by `viewport.id`, parses the
          // snapshot through `drawingEntitySchema` and swaps the whole entity,
          // with no viewport-specific behaviour anywhere in its reducer case.
          const snapshotRestores: Array<{
            spaceId: string;
            viewport: DrawingEntity;
          }> = [];
          for (const vpId of touchedIdList) {
            // D6 fix — read the PRE-mutation viewport from the early
            // snapshot; `findVpById(preMutationDoc, vpId)` here would
            // observe POST-mutation data (see `propertiesApplyPrevEntityById`
            // JSDoc above).
            const prevVpCandidate = propertiesApplyPrevEntityById.get(vpId);
            const prevVp =
              prevVpCandidate && prevVpCandidate.type === "Viewport"
                ? prevVpCandidate
                : undefined;
            const nextVp = findVpById(nextGeometryDocument, vpId);
            if (!prevVp || !nextVp) continue;
            // COUPLED carriers the label-replay inverse cannot restore (a re-derive /
            // mutator FORWARD transform). Plain leaves — center/width/height/viewTwist
            // — are NOT here (they round-trip via label-replay + raw overlays; a
            // Height edit that MATTERS re-derives viewHeight, which IS below).
            const coupledChanged =
              !jsonEq(
                (prevVp as ViewportEntity).vpOverrideScale,
                (nextVp as ViewportEntity).vpOverrideScale,
              ) ||
              (prevVp as ViewportEntity).vpOverrideUcsName !==
                (nextVp as ViewportEntity).vpOverrideUcsName ||
              (prevVp as ViewportEntity).viewHeight !==
                (nextVp as ViewportEntity).viewHeight ||
              !jsonEq(
                (prevVp as ViewportEntity).viewCenter,
                (nextVp as ViewportEntity).viewCenter,
              ) ||
              (prevVp as ViewportEntity).viewportCannoscaleName !==
                (nextVp as ViewportEntity).viewportCannoscaleName ||
              (prevVp as ViewportEntity).viewportCannoscaleFactor !==
                (nextVp as ViewportEntity).viewportCannoscaleFactor ||
              (prevVp as ViewportEntity).displayLocked !==
                (nextVp as ViewportEntity).displayLocked ||
              (prevVp as ViewportEntity).statusBitFlags !==
                (nextVp as ViewportEntity).statusBitFlags ||
              !jsonEq(
                (prevVp as { xdata?: unknown }).xdata,
                (nextVp as { xdata?: unknown }).xdata,
              );
            if (coupledChanged) {
              snapshotRestores.push({ spaceId: prevVp.spaceId, viewport: prevVp });
            }
          }
          // @codex #496 P2 (round-3) — a boundary-OBJECT properties edit (a
          // Properties-panel `Closed=false` on the LWPOLYLINE the viewport clips
          // to, or a vertex edit that degenerates it below the min-area / <3-vertex
          // clip-region threshold) is caught by `recomputeObjectDerivedViewportClips`,
          // which INVALIDATES the dependent viewport's FROZEN object clip as a SIDE
          // EFFECT — dropping the 340 handle + the group-90 non-rect bit so a later
          // save can never emit a dangling 340. That viewport is NOT in
          // `touchedIdList` (the command named the BOUNDARY, not the viewport), so
          // the coupled-field sweep above never sees it, and the label-replay
          // `properties/apply` inverse restores ONLY the boundary. Replaying that
          // inverse RE-RUNS the recompute, but the handle is already gone, so the
          // viewport can no longer re-associate → undo would leave the VPCLIP
          // PERMANENTLY removed. Sweep the geometry mutation diffs for a viewport
          // whose PREV carried a real clip that NEXT lost (the SAME three-carrier
          // `viewportHasRealClip` truth the delete / modify-command inverses use in
          // command-engine) and restore its full pre-edit snapshot — deduped against
          // the directly-touched restores above and the command's own targets. This
          // is the `properties/apply` sibling of the `geometry/restore-viewport-clip`
          // pairing the modify-command inverses already do for the same side-effect
          // invalidation. No-op (fast path preserved) when the apply touched no clip
          // boundary — the overwhelmingly common case.
          {
            const alreadyRestoring = new Set(
              snapshotRestores.map((r) => r.viewport.id),
            );
            for (const entry of dispatchMutations.updated) {
              const prev = entry.prev;
              const next = entry.next;
              if (prev.type !== "Viewport" || next.type !== "Viewport") continue;
              // The directly-touched viewports are handled by the coupled sweep
              // above; only re-associate a viewport the apply did NOT name.
              if (touchedIds.has(prev.id) || alreadyRestoring.has(prev.id)) continue;
              // Clip was present PRE-edit and CLEARED post-edit (the invalidation
              // path). A still-clipped or never-clipped viewport is skipped.
              if (!viewportHasRealClip(prev) || viewportHasRealClip(next)) continue;
              snapshotRestores.push({ spaceId: entry.spaceId, viewport: prev });
              alreadyRestoring.add(prev.id);
            }
          }
          // @codex PR #785 round 2, F2 — PRESENCE-BIT restore, the third sweep.
          // The label-replay inverse restores VALUES; it cannot restore the
          // difference between an ABSENT optional and one written with the
          // value its absence displays. An imported heavy POLYLINE with no
          // group 40 shows the inherited "Start segment width" 0, so editing
          // that width and undoing replayed "0" and left an EXPLICIT zero —
          // the entity did not return to its pre-edit state and the next
          // export emitted a group the file never had (the writer is
          // presence-preserving by design: on a POLYLINE the 40/41 defaults are
          // what a VERTEX without its own 40/41 INHERITS, so the group's
          // existence is data).
          //
          // Detection is STRUCTURAL, not a per-field list: an apply that made
          // ANY previously-absent field present, at any depth (an entity
          // default, a per-vertex width override, a nested style leaf), is
          // exactly the case the string replay cannot express — so the pre-edit
          // TYPED SNAPSHOT is composed in and restores the entity verbatim.
          // A hand-maintained sentinel per optional would have to be remembered
          // for the NEXT optional too; this cannot miss one. Gated, so a
          // presence-preserving edit (the overwhelmingly common case — the
          // field already existed) keeps the lighter label-replay + raw
          // overlays exactly as before.
          //
          // The restore travels as `geometry/restore-entity-snapshot`, NOT as
          // the viewport-clip restore the two sweeps above use: the snapshot is
          // an ARBITRARY entity (the defect fixture is a heavy POLYLINE), so
          // carrying it in a field named `viewport` mis-filed every such undo in
          // the audit trail under the `viewport` category and needed an
          // `as unknown as EditorCommand` cast to get past the schema. The
          // dedicated command is schema-typed end to end (no cast) and audits
          // under `modify`. Its own list, so the viewport sweeps keep emitting
          // `geometry/restore-viewport-clip` unchanged (that command also
          // carries the document-scope `restoreRawObjectGroups` re-attach).
          const presenceSnapshotRestores: Array<{
            spaceId: string;
            entity: DrawingEntity;
          }> = [];
          {
            const alreadyRestoring = new Set(
              snapshotRestores.map((r) => r.viewport.id),
            );
            for (const id of touchedIdList) {
              if (alreadyRestoring.has(id)) continue;
              // D6 fix — the PRE-mutation entity comes from the early frozen
              // snapshot, never a live `preMutationDoc` read here.
              const prevEntity = propertiesApplyPrevEntityById.get(id);
              const nextEntity = findEntityById(nextGeometryDocument, id);
              if (!prevEntity || !nextEntity) continue;
              // @codex PR #794 round 14 P2 — the SECOND thing the display-string
              // replay cannot express, and the mirror of the presence bit above:
              // a field the apply CLEARED that no property label names. An MTEXT
              // edit that changes the laid-out block invalidates the imported
              // groups 42/43 (`mtextLayoutMeasurementInvalidation`, applied by
              // the geometryReducer's post-steps MTEXT normalization), and
              // replaying the prior `Contents` / `Height` / `Width` label re-runs
              // that forward clear — so without the typed snapshot the undo would
              // leave the measurement at 0 and the edit would be LOSSY. Detected
              // by the model-level `mtextLayoutMeasurementDropped` rather than a
              // second structural walk: this loss is a DECLARED write-path
              // consequence with a named owner, not an anonymous diff, and
              // reporting every present→absent/changed field here would fire on
              // ordinary edits of compact-store-backed entities (the exact reason
              // `firstGainedOptionalFieldPath` scopes itself to one direction).
              if (
                firstGainedOptionalFieldPath(prevEntity, nextEntity) === null &&
                !mtextLayoutMeasurementDropped(prevEntity, nextEntity)
              ) {
                continue;
              }
              presenceSnapshotRestores.push({
                spaceId: prevEntity.spaceId,
                entity: prevEntity,
              });
              alreadyRestoring.add(id);
            }
          }
          // Both restores can be needed by ONE apply (a viewport edit that also
          // materialised an absent optional), so they compose into the same
          // `history/replay-inverses` batch — whole-entity replaces, replayed
          // after the common-prop / label-replay inverse they subsume.
          const composedRestores: EditorCommand[] = [];
          if (snapshotRestores.length > 0) {
            composedRestores.push({
              type: "geometry/restore-viewport-clip",
              source: "system",
              viewports: snapshotRestores,
            } as unknown as EditorCommand);
          }
          if (presenceSnapshotRestores.length > 0) {
            const presenceRestoreCmd: EditorCommand = {
              type: "geometry/restore-entity-snapshot",
              source: "system",
              entities: presenceSnapshotRestores,
            };
            composedRestores.push(presenceRestoreCmd);
          }
          if (composedRestores.length > 0) {
            const batch: EditorCommand[] =
              inverseCommand !== null
                ? [inverseCommand, ...composedRestores]
                : composedRestores;
            inverseCommand = {
              type: "history/replay-inverses",
              source: "system",
              batch,
            } as unknown as EditorCommand;
          }
        }
      }

      let nextHistory = resyncedState.history;
      // `isEmptyMutation` covers entity + styleTable changes. `docHeaderChanged`
      // catches metadata-only mutations (cannoscale, annoAllVisible, scalelist,
      // etc.) whose reducers return NO_MUTATIONS but still produce a new document.
      const docMutated =
        !isEmptyMutation(dispatchMutations) ||
        docHeaderChanged(preMutationDoc, nextGeometryDocument);
      if (shouldRecordHistory && docMutated) {
        // Phase 4.1.f — batch accumulation path.
        // ALL mutating commands during a batch are recorded in forwards,
        // even if inverseCommand is null (non-invertible command). The
        // inverses list stores nulls for those slots; abort-batch and
        // replay-inverses skip null entries.
        if (resyncedState.history.current !== null) {
          if (inverseCommand === null) {
            // Non-invertible command inside batch: warn so the developer
            // knows abort/undo will be partial for this batch.
            console.warn(
              `[history] batch command "${command.type}" has no inverse — ` +
                "abort-batch will skip this command's undo.",
            );
          }
          const batchCurrent = resyncedState.history.current;
          nextHistory = {
            ...resyncedState.history,
            current: {
              ...batchCurrent,
              // forwards: always append in dispatch order (the STAMPED command —
              // FINDING B — so redo restores the original CECOLOR-resolved colour).
              // P2 redo: create-attdef uses the session-independent override.
              forwards: [
                ...batchCurrent.forwards,
                recordedForwardOverride ?? recordedForward,
              ],
              // inverses: prepend so array is already reversed (last-cmd-inverse
              // first) — commit-batch replays in array order = undo in reverse.
              // null slots are skipped by abort-batch and replay-inverses.
              inverses: [inverseCommand, ...batchCurrent.inverses],
            },
          };
        } else if (inverseCommand !== null) {
          const now = Date.now();
          const entry: HistoryEntry = {
            id: now.toString(),
            // FINDING B (round-3) — record the STAMPED command so redo restores
            // the original CECOLOR-resolved colour (not the live CECOLOR at redo).
            // P2 redo: create-attdef uses the session-independent override so
            // REDO re-adds the ATTDEF even after the user has exited BEDIT.
            forward: recordedForwardOverride ?? recordedForward,
            inverse: inverseCommand,
            summary: command.type,
            spaceId: store.state.drawing.activeSpaceId,
            selectionBefore: [...store.state.selection.entityIds],
            selectionAfter: [...resyncedState.selection.entityIds],
            timestamp: now,
          };
          const rawPast = [...resyncedState.history.past, entry];
          // Cap to depthCap — drop oldest entry when over the limit.
          // depthCap === 0 means undo is disabled; the past stack stays empty.
          const cap = resyncedState.history.depthCap;
          const cappedPast =
            cap === 0
              ? []
              : rawPast.length > cap
                ? rawPast.slice(rawPast.length - cap)
                : rawPast;
          const dropped = rawPast.length - cappedPast.length;
          nextHistory = {
            ...resyncedState.history,
            past: cappedPast,
            // P1-2: Any new mutation clears the redo stack, regardless of
            // whether an inverse was produced. If no inverse is produced
            // (inverseCommand===null) the user still made a change, so
            // redo must be invalidated to avoid re-applying stale commands.
            future: [],
            droppedCount: resyncedState.history.droppedCount + dropped,
          };
        } else {
          // inverseCommand === null: command type is not invertible.
          // Only clear redo stack when geometry was actually mutated —
          // same-ref means the geometry reducer was a no-op (non-mutating
          // command such as a view/selection change), so future stays intact.
          if (
            resyncedState.history.future.length > 0 &&
            nextGeometryDocument !== preMutationDoc
          ) {
            nextHistory = {
              ...resyncedState.history,
              future: [],
            };
          }
        }
      }

      // VP-property-only commands (lock/scale/vplayer/clip/shadeplot) mutate
      // the VP entity but do NOT change model-space topology. Scene3DStage
      // render caches (spatial index, cull results) depend only on topology, so
      // we skip the renderTopologyVersion bump for these command types to avoid
      // O(N) rebuild on every VP lock click. Shade-plot is plot-time-only (zero
      // on-screen render effect), so it belongs here too.
      //
      // Key off the RESOLVED `effectiveCommand.type`, NOT the raw `command.type`:
      // a scripted / batch / AI `VPCLIP …` arrives as a `commandline/submit`
      // wrapper whose `.type` matches NONE of these, so gating on `command.type`
      // would MISS it and force the O(N) topology rebuild the equivalent typed
      // dispatch avoids (@codex #496 P3). `effectiveCommand` unwraps the wrapper to
      // the resolved `geometry/viewport-clip-*` exactly as the inverse logic above
      // already does; a direct typed dispatch has `effectiveCommand === command`.
      const isViewportPropertyOnlyCommand =
        effectiveCommand.type === "geometry/viewport-set-lock" ||
        effectiveCommand.type === "geometry/viewport-set-shadeplot" ||
        effectiveCommand.type === "geometry/viewport-set-scale" ||
        effectiveCommand.type === "geometry/viewport-set-view" ||
        effectiveCommand.type === "geometry/viewport-vplayer" ||
        effectiveCommand.type === "geometry/viewport-clip-set" ||
        effectiveCommand.type === "geometry/viewport-clip-set-polygon" ||
        effectiveCommand.type === "geometry/viewport-clip-remove";

      // Lane J (D3-1) — @codex round-2 P2 ("Do not invalidate topology for
      // table-only edits"). A style/layer/DIMSTYLE/text-style edit (and any
      // undo/redo of one) declares a NON-EMPTY `styleTablesTouched` but EMPTY
      // entity arrays, so `commitMutation` treats it as non-no-op and bumps
      // `indexVersion`. Copying that bump into `renderTopologyVersion` would
      // force Scene3DStage's viewport culling / traversal caches (keyed on
      // renderTopologyVersion — spatial position topology) to rebuild O(N) even
      // though NO entity was added / removed / moved: the entity set and its
      // spatial topology are byte-for-byte identical. Layer colour / visibility
      // propagates through the INDEPENDENT `styleTables.layers` / `layerStates`
      // reactive subscriptions (LayerVisibilityEffect + the bulk-load effect),
      // not renderTopologyVersion, so appearance still updates. This mirrors the
      // pre-Lane-J direct-set path, which never touched renderTopologyVersion.
      //
      // Scoped to a STYLE-TABLE touch (`styleTablesTouched.size > 0`) so it can
      // NOT swallow the `commitMutation` space-PURGE bump (LAYOUT DELETE of a
      // streamed space bumps `indexVersion` via `purgeSpaceFromCaches` with
      // EMPTY `dispatchMutations` — that genuinely changes topology and must
      // still bump). A delete-layer / rename-style that ACTUALLY remaps
      // referencing entities carries `updated` records (entity-level), so it is
      // NOT entity-level-no-op → keeps the bump (parity with `properties/apply`
      // changing an entity's layer).
      const isTableOnlyMutation =
        isEntityLevelNoOpMutation(dispatchMutations) &&
        dispatchMutations.styleTablesTouched.size > 0;

      // ── ABB-S1 — revision DUAL-BUMP at the universal mutation chokepoint ──
      // EVERY command (batched or non-batched, geometry/table/context/selection)
      // converges on this generic `set`. We classify what the command ACTUALLY
      // changed by STATE-DIFFing the live sub-slices (the convergent, oracle-free
      // mechanism — `diffBumpClasses`), NOT by guessing from the command kind.
      // Grounding the bump in the real change is what makes every path correct in
      // one move:
      //   - the CLI `commandline/submit` that resolves to a geometry command
      //     bumps geometry/snapshot even though its raw type classifies to null
      //     (the diff sees the entity mutation, not the wrapper type);
      //   - CANNOSCALE-sync that MATERIALISES entities bumps geometry too (the
      //     diff sees `updated`), remove-ACTIVE-space bumps context (activeSpaceId
      //     changed), block-edit `createIfMissing` / save bumps table
      //     (docHeaderChanged sees the new/edited block definition) — no runtime-
      //     dependent mis-classification;
      //   - a NO-OP commit (re-select the same set, re-activate the current space,
      //     clear an already-empty selection, a geometry command that changed
      //     nothing) bumps NOTHING (every diff field is false).
      // `shouldRecordHistory` excludes history/* markers AND the __bypassHistory
      // inverse re-dispatches (undo/redo/abort/replay) — those bump explicitly at
      // their own markers (by the APPLIED inverse's diff), abort bumps nothing.
      //
      // entityStateChanged: an entity was added/removed/updated (NOT a style-table
      //   touch — that is a TABLE change). → geometry.
      // tableChanged: a style table was touched OR a header/layout-table/block-
      //   definition field differs (the same `docHeaderChanged` predicate that
      //   drives `docMutated`). → table.
      // contextChanged / selectionChanged: VALUE/membership diff of the pre-vs-
      //   post shell state (so a reducer re-spreading an unchanged selection
      //   object, or re-activating the current space, does NOT bump).
      const filteredForwardShellEntities =
        shellEntitiesWithoutTransitionalSyntheticSpaces(
          store.state.drawing.entities,
          resyncedState.drawing.entities,
          preMutationDoc,
          nextGeometryDocument,
        );
      const entityStateChanged =
        dispatchMutations.added.length > 0 ||
        dispatchMutations.removed.length > 0 ||
        dispatchMutations.updated.length > 0 ||
        // ABB-S1 G3 #4 — CHPROP / MATCHPROP / Properties edits flow through
        // `reducePropertyCommand`, which mutates the SHELL preview store
        // (`state.drawing.entities`) and produces NO `dispatchMutations` (those
        // come from the geometry document). Without this the diff missed every
        // property edit → no geometry bump. The shell-entities diff catches it.
        // #518 (Codex P1 follow-up) — excludes ONLY TRANSITIONAL synthetic
        // block-edit/block-test/refedit rows (space presence flipped by this
        // dispatch): the P1 fix forces a full shell rebuild on a
        // geo-authoritative space switch so those rows actually reach
        // `state.drawing.entities` (UI visibility), but the switch alone must
        // stay CONTEXT-only here too, mirroring `dispatchMutations`' own
        // synthetic-space exclusion (`documentWithoutSyntheticSpaces`) above.
        // A STABLE synthetic space (a real geometry/* edit issued WHILE
        // already inside an open BEDIT/REFEDIT session) is NOT excluded — its
        // `dispatchMutations` is non-empty anyway (one of the three checks
        // above), so this only ever matters for the space/* transition itself.
        shellEntitiesChanged(
          filteredForwardShellEntities.prev,
          filteredForwardShellEntities.next,
        );
      const tableChanged =
        dispatchMutations.styleTablesTouched.size > 0 ||
        // round-4 GENERALIZATION 1 — `docHeaderChanged` is now the document-MINUS-
        // entities-MINUS-context fingerprint (`documentTableFingerprint`), so ANY
        // non-entity-array structural change (styleTables/layers/blocks incl
        // dynamicMetadata/blockRecords/xref/layouts/units/plot AND every future
        // field) registers as table with no per-field enumeration.
        docHeaderChanged(preMutationDoc, nextGeometryDocument);
      // round-4 F4 — compare the DOC's activeSpaceId too (BEDIT/REFEDIT switch
      // `geometryDocument.activeSpaceId` while the shell active space lags).
      const contextChanged = contextChangedBetween(
        preMutationDoc,
        nextGeometryDocument,
        store.state,
        resyncedState,
      );
      const selectionChanged = selectionMembershipChanged(
        store.state.selection.entityIds,
        resyncedState.selection.entityIds,
      );
      const stateDiff: RevisionStateDiff = {
        entityStateChanged,
        tableChanged,
        contextChanged,
        selectionChanged,
      };
      const committedClasses = shouldRecordHistory
        ? diffBumpClasses(stateDiff)
        : EMPTY_BUMP_CLASSES;
      const committed = shouldRecordHistory && hasAnyBumpClass(committedClasses);

      let nextRevisions = store.revisions;
      let nextPendingBumpClasses = store.pendingBumpClasses;
      if (committed) {
        if (resyncedState.history.current !== null) {
          // Inside an open batch: accumulate; the single DUAL-BUMP fires at
          // commit-batch (depth 1, non-empty).
          nextPendingBumpClasses = unionBumpClasses(
            store.pendingBumpClasses ?? EMPTY_BUMP_CLASSES,
            committedClasses,
          );
        } else {
          // Non-batched single command: DUAL-BUMP now.
          nextRevisions = bumpForClasses(store.revisions, committedClasses);
        }
      }

      return {
        state: {
          ...resyncedState,
          drawing: { ...resyncedState.drawing, entities: enrichedEntities },
          history: nextHistory,
        },
        geometryDocument: wrappedDocument,
        // Phase 3b PR #6 — bump the reactive version counter so React
        // subscribers latched onto the (non-reactive) entityMetadataIndex
        // re-evaluate. `commitMutation` returns the index's internal
        // version; we mirror it into Zustand state. No-op mutations
        // preserve the current version (commitMutation early-outs on
        // isNoOp), so no spurious re-renders on UI-only commands.
        entityMetadataIndexVersion: commit.indexVersion,
        renderTopologyVersion:
          isViewportPropertyOnlyCommand || isTableOnlyMutation
            ? store.renderTopologyVersion
            : commit.indexVersion,
        // ABB-S1 — revision counters / batch accumulator (see block above).
        revisions: nextRevisions,
        pendingBumpClasses: nextPendingBumpClasses,
      };
    });

    // CECOLOR — apply the captured live-store sync AFTER `set` (so the zustand
    // updater above stays side-effect-free). The live `cecolorStore` MIRRORS
    // `doc.cecolor` (the single source of truth — round-8 FINDING B): a forward
    // set (CLI COLOR / picker / AI `SET_CURRENT_COLOR`) and an undo/redo replay
    // (`__bypassHistory`) both just reflect the resulting `doc.cecolor` into the
    // live session — neither persists to localStorage (CECOLOR is per-drawing,
    // saved in the DXF `$CECOLOR` HEADER). The live current colour — read by the
    // create-dispatch boundary and the ribbon swatch — tracks `doc.cecolor`,
    // keeping forward⇔undo symmetric.
    if (nextCeColorToSync !== null) {
      useCeColorStore.getState().setCeColor(nextCeColorToSync);
    }
  },
  appendShellEntity: ({ entity, summary, source = "scene", commandText }) =>
    set((store) => {
      const syntheticCommand = {
        type: "commandline/submit" as const,
        source,
        text: commandText ?? entity.type.toUpperCase(),
      };

      // P2 #2 (post-review, round 3) — LOCAL fail-closed EDIT veto. This
      // action is a real ENTITY CREATION that BYPASSES `dispatch()`'s
      // generic `set` entirely (see the ABB-S1 note below on why it is NOT
      // rerouted through `dispatch()`/`commitMutation`), so it also bypasses
      // `dispatch()`'s own collab-lock veto (a few thousand lines up in this
      // file). Without a check HERE, `LOCK LAYER Walls` would fail to stop
      // another collaborator drawing a brand-new entity onto the "Walls"
      // layer via whatever interactive-draft command completes through THIS
      // path instead of the normal geometry-command
      // (`dispatchWithMleaderEditGuards` → `dispatch()`) path. This only ever
      // CREATES a new entity — a fresh `entity.id` can never already be
      // locked — so only the LAYER-scope check matters; mirrors the main
      // veto call site's `added` handling (`collabLockVetoTouched.push({
      // entityId: added.entity.id, layerName: added.entity.layer })`), with
      // the layer name read from `entity.properties.Layer` (`EntityPreview`
      // carries no top-level `.layer` field — `Layer ?? layer` is the same
      // fallback the scene/property read sites already use for this preview
      // shape, e.g. `StagePointerHandlers.ts`).
      //
      // Gated on a non-empty live `locks` map OR a non-"disconnected" collab
      // status, mirroring the main veto call site's zero-overhead-when-
      // disconnected guard — a non-collab session pays zero extra cost. P1
      // (post-review, round 5) — the status half is required so a live
      // reconnect window (`locks` cleared but the tab WAS connected) is not
      // silently treated as "no session" here either; see `collabLockVeto.ts`'s
      // `authoritative` field.
      const collabLockStatusSnapshot = useCollabLockStore.getState().status;
      if (
        useCollabLockStore.getState().locks.size > 0 ||
        collabLockStatusSnapshot !== "disconnected"
      ) {
        const layerName = entity.properties.Layer ?? entity.properties.layer;
        const collabLockVeto = evaluateCollabLockVeto({
          touchedEntities: [{ entityId: entity.id, layerName }],
          touchedLayerNames: [],
          locks: useCollabLockStore.getState().locks,
          selfParticipantId: useCollabLockStore.getState().selfParticipantId,
          authoritative: collabLockStatusSnapshot === "connected",
        });
        if (collabLockVeto) {
          // Reject the WHOLE creation, atomically — do NOT append the
          // entity, do NOT bump revisions. Only `state` is returned so
          // `store.revisions` (and everything else) passes through
          // unchanged, exactly like the `dispatch()` veto's early return.
          const message =
            collabLockVeto.kind === "unavailable"
              ? t("collab.lock.edit_rejected_unavailable")
              : t("collab.lock.edit_rejected", {
                  target: collabLockVeto.scope.targetId,
                  owner: collabLockVeto.heldByName,
                });
          return {
            state: pushAuditEvent(store.state, syntheticCommand, message, "error"),
          };
        }
      }

      return {
        state: pushAuditEvent(
          {
            ...store.state,
            ui: {
              activePanel: "properties",
            },
            drawing: {
              ...store.state.drawing,
              entities: appendCreatedEntity(store.state.drawing.entities, entity),
            },
            selection: {
              entityIds: [entity.id],
              // Q25.C: snapshot prior pickset on auto-select after entity
              // creation, so SELECT P recovers the pre-create selection.
              previousPickset: store.state.selection.entityIds,
            },
          },
          syntheticCommand,
          summary,
          "success",
          // #479 F-appendShell — the synthetic `commandline/submit` command carries
          // NO entity id, so `affectedEntityIdsForCommand` would resolve `[]` for
          // this interactive-draft creation. Thread the freshly-created id through
          // the reducer-allocated-ids parameter (as the geometry-create dispatch
          // arms do) so the persisted audit row links to the created object rather
          // than losing the affected-entity evidence in the admin detail panel.
          undefined,
          [entity.id],
        ),
        // ABB-S1 — this is a real ENTITY CREATION (the interactive-draft scene
        // path) that BYPASSES the dispatch generic `set`, so it must bump the
        // geometry revision directly. The synthetic `commandline/submit` tag is
        // not classifiable, so the geometry class is asserted here. Auto-select
        // ALSO changes selection membership.
        //
        // Gate #4 P3 (DEFERRED — follow-up, not this PR): unlike the batched
        // dispatch path (which ACCUMULATES bump classes into `pendingBumpClasses`
        // and folds them ONCE at commit), this helper bumps `revisions` IMMEDIATELY
        // even when a history batch is open — so an agent observing the revision
        // counters sees a mid-batch bump here vs deferred-to-commit elsewhere. NOT
        // changed here because it is NOT a small, safe edit: `appendShellEntity`
        // adds the entity OUTSIDE the forward/inverse transaction (it records no
        // inverse), so the entity PERSISTS past an abort — meaning the immediate
        // direct-bump matches the abort-survives semantics the net-diff abort path
        // (`computeRevisionStateDiff`) ALSO detects, and rerouting it into the
        // accumulators risks a double/under-bump with that machinery (the same
        // accumulator/abort-diff seam that took multiple @codex rounds to settle).
        // Tracked for a dedicated revision-bump-consistency follow-up.
        revisions: bumpForClasses(store.revisions, {
          geometry: true,
          table: false,
          context: false,
          selection: true,
        }),
      };
    }),
  recordAnnotativeStylePropagation: ({ styleKind, styleName }) =>
    set((store) => {
      // PR-10 — translate (styleKind, styleName) into the deferred
      // audit message; both messages take a {name} parameter so the
      // pluck is uniform. Direction + cannoscaleName accepted in the
      // input shape for future use when real propagation lands; today
      // they're consumed by the prompt UI only.
      const summaryKey =
        styleKind === "dimstyle"
          ? "audit.dimstyle.annotative_propagation_deferred"
          : "audit.mleaderstyle.annotative_propagation_deferred";
      const summary = t(summaryKey, { name: styleName });
      // Synthetic command tag — keeps the audit row keyed under a
      // recognisable type so future telemetry filters can match.
      const command = {
        type: "commandline/submit" as const,
        source: "scene" as const,
        text:
          styleKind === "dimstyle"
            ? `DIMSTYLE ${styleName} ANNO PROPAGATE (deferred)`
            : `MLEADERSTYLE ${styleName} ANNO PROPAGATE (deferred)`,
      };
      return {
        state: pushAuditEvent(store.state, command, summary, "warning"),
      };
    }),
  recordBeditDeferredAction: ({ kind, scaleName }) =>
    set((store) => {
      // PR-17 — replaces the inline `console.info` / `console.warn`
      // calls in PrimitiveAtlasStage.tsx (BEDIT annotative tab strip).
      // Both actions are deferred placeholders today; pushing through
      // the audit channel keeps the user-visible feedback consistent
      // with every other state-changing UI interaction.
      const summaryKey =
        kind === "preview"
          ? "audit.bedit.decomposition_preview_deferred"
          : "audit.bedit.generate_decomposition_deferred";
      const command = {
        type: "commandline/submit" as const,
        source: "scene" as const,
        text:
          kind === "preview"
            ? `BEDIT preview decomposition (scale=${scaleName}, deferred)`
            : `BEDIT generate decomposition (scale=${scaleName}, deferred)`,
      };
      return {
        state: pushAuditEvent(store.state, command, t(summaryKey), "warning"),
      };
    }),
  recordViewOp: ({ opType, summary, source = "commandline", tone = "info", detail }) =>
    set((store) => ({
      state: pushViewOpAuditEvent(store.state, opType, source, summary, tone, detail),
    })),
  recordUiClick: ({ target, controlId, summary, source = "toolbar", tone = "info" }) =>
    set((store) => ({
      state: pushUiInteractionAuditEvent(
        store.state,
        describeUiClickOperation({ target, controlId }),
        source,
        summary ?? `UI click: ${target}/${controlId}`,
        tone,
      ),
    })),
  recordUiToggle: ({ setting, enabled, summary, source = "toolbar", tone = "info" }) =>
    set((store) => ({
      state: pushUiInteractionAuditEvent(
        store.state,
        describeUiToggleOperation({ setting, enabled }),
        source,
        summary ?? `UI toggle: ${setting} ${enabled ? "on" : "off"}`,
        tone,
      ),
    })),
  recordUiNav: ({ navKind, from, to, summary, source = "toolbar", tone = "info" }) =>
    set((store) => ({
      state: pushUiInteractionAuditEvent(
        store.state,
        describeUiNavOperation({ navKind, from, to }),
        source,
        summary ?? `UI nav (${navKind}): ${from} -> ${to}`,
        tone,
      ),
    })),
  // STORE-SPLIT slice 2 — importDxfText / importDxfTextAsync moved verbatim
  // to editorStoreImportActions.ts (composed below via
  // ...createDxfImportActionsSlice(set, get, deps)).
  // STORE-SPLIT slice 3 — the two File-handle import variants
  // (importDxfFileAsync INSERT-style streaming merge; importDxfFileAsyncReplacing
  // OPEN-style streaming replace) moved verbatim to editorStoreImportFileActions.ts
  // (composed below via ...createDxfImportFileActionsSlice(set, get, deps)).
  // STORE-SPLIT slice 2 — the DXF text-import + import-progress-lifecycle
  // action family (importDxfText / importDxfTextAsync / abortDxfImport /
  // setImportProgress / clearImportProgress), extracted verbatim to
  // editorStoreImportActions.ts. set/get here ARE useEditorStore.setState /
  // useEditorStore.getState (zustand passes the same functions into the
  // initializer), so the composed actions behave byte-identically to the
  // previous inline definitions. The two rebuild* deps are the module-level
  // editorStore.ts exports the inline bodies called before (they stay here
  // because they bump the reactive index-version counters via
  // useEditorStore.setState — a store-owned seam).
  ...createDxfImportActionsSlice(set, get, {
    rebuildEntityMetadataIndexFromImport,
    rebuildEntityMetadataIndexFromImportAsync,
  }),
  // STORE-SPLIT slice 3 — the two `File`-handle import variants
  // (importDxfFileAsync / importDxfFileAsyncReplacing), extracted verbatim to
  // editorStoreImportFileActions.ts. set/get here ARE useEditorStore.setState /
  // useEditorStore.getState (the former `useEditorStore.getState()` call sites
  // inside the moved bodies are now `get()`), so the composed actions behave
  // byte-identically. The async rebuild dep stays a module-level editorStore.ts
  // export (it bumps the reactive index-version counters via
  // useEditorStore.setState — a store-owned seam).
  ...createDxfImportFileActionsSlice(set, get, {
    rebuildEntityMetadataIndexFromImportAsync,
  }),
  auditExportReferences: (options) => {
    const document = useEditorStore.getState().geometryDocument;
    // PR1b Codex P2 (iter-3) — SHARED per-space cache snapshot. For a STREAMED
    // file the inline `space.entities[]` are empty (entities live in the compact
    // cache); the exporter reads them from a per-space `entitiesSnapshot`.
    // `buildExportEntitiesSnapshot` decides PER SPACE (not on all-spaces-empty)
    // so a paper space retaining an inline Viewport no longer suppresses the
    // model-space cache snapshot. BOTH the warn-only audit AND the serialise see
    // the SAME effective entity source.
    const entitiesSnapshot = buildExportEntitiesSnapshot(document);
    // PR1b Codex P2 (iter-5) — the snapshot (above) is built UNCONDITIONALLY so
    // the preview / render-driven serialise still sees the correct entity source
    // (streaming provenance + linetypeScale). The warn-only audit transcript
    // EMIT below is gated by `emitWarning` (default true): only user-initiated
    // export emits, so an ordinary edit/import preview no longer pushes a "save"
    // warning the user never triggered.
    if (options?.emitWarning === false) {
      return entitiesSnapshot;
    }
    // PR1b Phase 2a — warn-only export gate (plan §9 OQ2): audit the document
    // for unresolved references BEFORE export and surface a transcript warning
    // if any remain. NEVER blocks the save — the export proceeds regardless.
    const warning = buildExportAuditWarning(document, entitiesSnapshot);
    // PR1b Codex P2 FIX-3 — no dedupe. As of iter-5 the small-drawing
    // preview-effect calls with `{ emitWarning: false }` and returns ABOVE this
    // point, so the ONLY path that reaches here is an EXPLICIT user-initiated
    // export (Download / Copy / Ctrl+S). Each such action legitimately re-warns
    // on the CURRENT state. The previous (docId, text) dedupe keyed on the
    // unresolved-reference COUNT only: a normal edit that changes WHICH reference
    // dangles while keeping the same count (and the same document.id) produced
    // identical key → the genuinely-new dirty state was silently suppressed.
    // Dropping the dedupe makes every explicit export re-warn on the live state,
    // which is the correct behaviour for an intentional user action.
    if (warning !== null) {
      set((store) => ({
        state: pushAuditEvent(
          store.state,
          EXPORT_AUDIT_TRANSCRIPT_COMMAND,
          warning,
          "warning",
        ),
      }));
    }
    return entitiesSnapshot;
  },
  exportDxfText: () => {
    const document = useEditorStore.getState().geometryDocument;
    // Single audit-emit point shared with App.tsx's download/copy/preview path.
    // Returns the SAME per-space snapshot used for the serialise below.
    const entitiesSnapshot =
      useEditorStore.getState().auditExportReferences();
    // Codex #342 F1 — project the LIVE editable HEADER sysvars (MIRRTEXT /
    // OSMODE) from the session sysvar store onto the export document so a
    // SETVAR-then-save persists the live value, not the stale document field.
    // Shared with App.tsx's export seam via `applyLiveHeaderSysvars` (no clone).
    const liveValues = useSysvarStore.getState().values;
    const exportDocument = applyLiveHeaderSysvars(document, {
      MIRRTEXT: liveValues.MIRRTEXT,
      OSMODE: liveValues.OSMODE,
    });
    // CECOLOR — project the LIVE current colour (`cecolorStore`) onto the export
    // document so a COLOR-then-save persists the live value, not the stale
    // document field. No-op when the live colour == doc.cecolor (plain open→save
    // round-trip). Shared with App.tsx's export seam via `applyLiveCeColor`.
    const exportDocumentWithCeColor = applyLiveCeColor(
      exportDocument,
      getCeColor(),
    );
    return exportGeometryDocumentToDxfText(
      exportDocumentWithCeColor,
      entitiesSnapshot,
    );
  },
  hydrateSessionShell: (input) =>
    set((store) => ({
      state: hydrateEditorSessionShellState(store.state, input),
    })),
  // STORE-SPLIT slice 1 / Lane J (D3-1) — the style-table + layout-plot-settings
  // action family (14 actions: update/add/delete/rename dimension styles,
  // update/add/delete multileader styles, updateTextStyle, add/update/delete/
  // rename layer, setCurrentLayer, setLayoutPlotSettings), in
  // `editorStoreStyleTables.ts`. Lane J routes the eight formerly-direct-`set`
  // add/update/plot actions through `dispatch` (undo-able); T7 (agent-bridge)
  // moved the stray direct-`set` `setCurrentLayer` into the SAME slice
  // (dispatches `properties/apply` with an empty selection — the identical
  // current-pointer mechanism `setCurrentDimensionStyle` already uses) and
  // added `renameLayer` (dispatches the new `header/rename-layer`) — so the
  // slice now needs only `get` (every action dispatches instead of writing
  // state directly).`get` IS `useEditorStore.getState`.
  ...createStyleTableActionsSlice(get),
  executeBatchCommands: (commands) => {
    const results: BatchCommandResult[] = [];
    commands.forEach((text, index) => {
      // Skip empty strings at the start (they require an active draft session)
      // but still dispatch them to let the command engine handle Enter-to-finish logic.
      try {
        useEditorStore.getState().dispatch({
          type: "commandline/submit",
          source: "commandline",
          text: text === "" ? " " : text,
        });
        results.push({ index, text, ok: true });
      } catch (error) {
        results.push({
          index,
          text,
          ok: false,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    });
    return results;
  },
  reset: () => {
    // PR-V1 — clear non-reactive caches so test runs and explicit
    // resets don't leak metadata between cases.
    //
    // post-V9 Section 0 Iter 6.D — `clearMutationStore` clears the
    // `entityMetadataIndex` and the compact cache (the only
    // non-reactive entity stores left after dropping the legacy
    // `entitiesCache: Map`) AND bumps the module-internal version
    // counter. The `__entityMetadataIndex` alias is the same singleton.
    clearMutationStore();
    // COLLAB-LOCK-COMMAND (post-review, round 4) — `reset()` is a TRUE
    // document SWAP (builds a brand-new blank document, mints a fresh
    // documentEpoch just below) — drop any live collab-lock connection so
    // the fail-closed veto never operates against a stale companion room
    // keyed by the document being replaced. `disconnect()` is idempotent/
    // safe to call when already disconnected (the common case in tests).
    useCollabLockStore.getState().disconnect();
    const freshDocument = buildInitialGeometryDocument();
    set((store) => ({
      state: buildInitialEditorShellState(),
      geometryDocument: freshDocument,
      importProgress: { ...INITIAL_IMPORT_PROGRESS },
      // PR-V4 — bump the version so any subscriber that latched onto
      // an old index snapshot re-renders against the cleared state.
      entityMetadataIndexVersion: store.entityMetadataIndexVersion + 1,
      renderTopologyVersion: store.renderTopologyVersion + 1,
      // ABB-S1 — reset() builds a fresh blank document (a document swap): reset
      // the five counters and MINT a new documentEpoch (monotonic, never repeats).
      revisions: mintNewDocumentEpoch(store.revisions),
      pendingBumpClasses: null,
      pendingDirectSetBumpClasses: null,
      // round-4 GENERALIZATION 2 — reset() is a document swap; drop the base.
      pendingBatchBaseDoc: null,
      pendingBatchBaseState: null,
      pendingBatchBaseAuditSequence: null,
      // Gate #4 round-2 (FIX C) — reset() is a non-commit close: any in-flight
      // batch's staged telemetry is DISCARDED.
      lastBatchClosedBy: "discard",
    }));
    // CECOLOR (round-8 FINDING B, round-9 P3 hardening) — enforce the single-
    // source-of-truth invariant AT THE STORE LAYER: `reset()` rebuilds a fresh
    // ByLayer document, so mirror the live `cecolorStore` to the SAME fresh
    // `doc.cecolor` (template-default ByLayer). Previously only App.tsx's
    // `resetShellState` wrapper mirrored live→ByLayer; folding it here means ANY
    // caller of `reset()` (current or future) keeps doc and live in agreement,
    // so the desync this round eliminated cannot be reintroduced by a direct
    // store-level reset. Hydrate FROM the fresh doc's cecolor (not a literal) so
    // the mirror always tracks whatever `buildInitialGeometryDocument` seeds.
    useCeColorStore
      .getState()
      .hydrateCeColor(buildCecolorHydration(freshDocument));
  },
});

export const useEditorStore = create<EditorStore>()(editorStoreInitializer);

// PR2 (plan §13) — register the active-document accessor the DrawingObjectGraph
// facade uses for `getLayer` / `getBlockDefinition`. Injected here (rather than
// imported into `drawingObjectGraph.ts`) so the facade module stays free of a
// `drawingObjectGraph → editorStore` import cycle.
setDrawingObjectGraphDocumentProvider(
  () => useEditorStore.getState().geometryDocument,
);

// Phase 0 (П35Р instrumentation) — wrap setState to count store mutations
// during import. Negligible overhead; lets the metrics reporter attribute
// React subscriber re-evaluation cost.
{
  const originalSetState = useEditorStore.setState.bind(useEditorStore);
  useEditorStore.setState = ((updater: unknown, replace?: boolean): void => {
    recordSetStoreCall();
    return originalSetState(
      updater as Parameters<typeof originalSetState>[0],
      replace as Parameters<typeof originalSetState>[1],
    );
  }) as typeof useEditorStore.setState;
}

if (typeof globalThis !== "undefined") {
  (globalThis as { __useEditorStore?: typeof useEditorStore }).__useEditorStore = useEditorStore;
  // Phase 0 — install global accessor for `window.__import_metrics`.
  installImportMetricsGlobalAccessor();
  // Auto-enable so smoke harness gets data without explicit opt-in.
  enableImportMetrics();
}

// post-V9 Section 0 — expose perf-measure readers on `globalThis` so the
// out-of-process runner (`scripts/section0-baseline.mjs`) can pull the
// span list via `page.evaluate(() => window.__section0_getMeasures())`
// without needing to know the entry-name prefix.
if (typeof globalThis !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = globalThis as any;
  g.__section0_getMeasures = getSection0Measures;
  g.__section0_clearMeasures = clearSection0Measures;
  // Iter 1 — Hypothesis A test helper. Returns entity-size stats for the
  // currently imported drawing (or null if nothing imported yet). Reads
  // from the reactive `geometryDocument.spaces[].entities` (post-V9
  // Section 0 Iter 6.D — slim-state-as-pointer keeps that array
  // populated for huge imports too). See section0Instrumentation.ts
  // for methodology notes.
  g.__section0_sampleEntities = sampleEntitySizes;
  // Iter 2 — Hypothesis E test helper. Builds a typed-array compact
  // store from all LINE entities and reports per-entity heap retention
  // for comparison against the standard record path measured in Iter 1.
  // Returns Promise<CompactLineMeasurement | null>; runner awaits it via
  // page.evaluate(...).
  g.__section0_measureCompactLines = measureCompactLineRepresentation;
  // post-V9 Section 0 Iter 4 — expose the compact store via a getter
  // so the measurement runner can compute heap retention without
  // tearing the renderer / reducers down to read it.
  g.__compactCache = getCompactCache;
}

// CC-1a (FIX 3) — module-level singleton guard + retained handle for the
// telemetry server channel. Keeping the handle lets us dispose the store
// subscription + flush interval on Vite HMR (otherwise every hot update
// leaks a duplicate subscription → duplicate sends) and reinstall with a
// fresh lastSeen high-water mark. In production the module evaluates once,
// so this is a plain single install.
let telemetryServerChannelHandle: ReturnType<
  typeof installDefaultTelemetryServerChannel
> | null = null;

// Expose batch runner on window for AI / external automation.
// Usage from DevTools or AI scripts:
//   window.editorBatchCommands(["LINE","0,0","5000,0",""])
if (typeof window !== "undefined") {
  // CC-1a — observe the in-memory audit ring and transport every emitted
  // event to the backend telemetry ingest (batched, retrying, no
  // fire-and-forget loss). Covers the 6 in-memory-emitting telemetry axes
  // (cmd-dim / cmd-extend / sub-mlv / sub-grips / sub-ai-draft /
  // sub-telemetry) plus any future REGEN view-op audit event (which does
  // not pass through geometryShellReducer).
  if (!telemetryServerChannelHandle) {
    telemetryServerChannelHandle =
      installDefaultTelemetryServerChannel(useEditorStore);
  }
  // Dispose + clear on HMR so a hot reload reinstalls a single fresh channel
  // (with a re-seeded lastSeen) instead of stacking subscriptions.
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      telemetryServerChannelHandle?.dispose();
      telemetryServerChannelHandle = null;
    });
  }

  (window as unknown as Record<string, unknown>).editorBatchCommands = (
    commands: string[],
  ) => useEditorStore.getState().executeBatchCommands(commands);

  // CC-2b (S-AIDRAFT-WIRE) — AI safe-contour entry. Mirrors editorBatchCommands
  // but takes the structured AI-command shape instead of command-line strings.
  // Translates via the CC-2a fail-closed guard and routes the result through
  // the SAME dispatch (validation → audit → grouped undo). AI never writes
  // entities directly. The CC-2d drafting panel calls this with a localized
  // undo label; here it is the automation/test entry (no UI surface).
  //   window.editorRunAiCommand({ action: "INSERT_BLOCK", blockName: "A", … })
  // SECURITY (CC-2b): the window mount exposes ONLY { context, label } — NOT
  // `descriptors`. The CC-2a fail-closed guard must always check against the
  // canonical registry; allowing a caller to inject a permissive `descriptors`
  // array here would let any action self-declare `aiCallable:true` and bypass
  // the guard. Descriptor injection stays internal/test-only.
  // Reads every INSERT (Block) entity id in the live document, in document
  // order. Passed to the safe contour as `resolveInsertIds` so it can resolve
  // the LAST_INSERT_ID_SENTINEL placeholder on INSERT_BLOCK attribute updates
  // (diff before/after a create to find the freshly-minted id). Delegates to
  // the shared CC-2d collector (`aiDispatchContext.ts`) — the same one-truth
  // reader the AI drafting panel wires into its direct safe-contour calls —
  // which iterates the live read-through `geometryDocument.spaces[].entities`
  // (cache-backed for streamed docs, inline fallback for legacy docs) in one
  // linear pass, so both virtualised and inline documents are read correctly.
  const collectInsertIds = (): string[] =>
    collectAiInsertIds(useEditorStore.getState().geometryDocument);

  // T2 (agent-editing) — snapshot every entity (id → fingerprint) so the safe
  // contour can surface the CREATED / AFFECTED entity ids of an AI turn. Same
  // one-truth reader (`collectAiEntitySnapshot`, `aiDispatchContext.ts`) the AI
  // drafting panel + the agent-bridge mount wire into their contour calls.
  const collectEntitySnapshot = () =>
    collectAiEntitySnapshot(useEditorStore.getState().geometryDocument);

  // Derive-and-merge the DEFAULT AiDispatchContext from the LIVE store. The
  // shared `aiDispatchContext.ts` module owns BOTH the derivation
  // (activeSpaceId + kind==="layout" paperSpaceLayoutIds + layoutContext —
  // the #287-12 / #290-14 / #292-P2 fail-closed fixes) and the defined-only
  // override merge (#292 P2: explicitly-undefined override fields never
  // clobber derived values). One truth for the window mounts AND the CC-2d
  // drafting panel; this thin wrapper just reads the live shell state at
  // call time.
  // Stamp the live MIRRTEXT (Codex #342 F2) as a BASE value the derived
  // context carries, so the window AI entry points (`editorRunAiCommand` /
  // `editorRunAiCommandBatch`) honour a live `SETVAR MIRRTEXT 1` exactly like
  // the AI panel + draft/CLI paths. The MIRROR translator stamps
  // `command.mirrtext ?? context.mirrtext ?? 0`, so this fills the gap only
  // when the AI proposal omits its own value.
  // Codex #342 F5: pass the live MIRRTEXT through the `base` parameter (the
  // THIRD arg) so it goes through the SAME defined-only merge as `override`.
  // The previous `{ mirrtext: live, ...override }` spread let an override
  // carrying `mirrtext: undefined` clobber the live value (the spread overwrote
  // it, then the defined-only override loop skipped it → translator fell back to
  // 0). With base+override both defined-only: an explicit caller `mirrtext`
  // still WINS, and an explicitly-undefined override NEVER erases the live base.
  // `buildAiDispatchContext` stays store-free (acyclic dep direction); the live
  // sysvar is read HERE, the shell-side seam.
  const resolveAiDispatchContext = (
    override: AiDispatchContext | undefined,
  ): AiDispatchContext =>
    resolveAiDispatchContextFromState(
      useEditorStore.getState().state,
      override,
      { mirrtext: useSysvarStore.getState().values.MIRRTEXT },
    );

  // ABB-S4 — run a contour call under the shared single-in-flight latch (held as
  // "window-seam"). If ANOTHER in-page client (the bridge mid-turn, or the panel
  // Accept) holds the latch, REFUSE rather than nest into its open batch (a nested
  // begin-batch depth+1 would fold this turn into theirs, breaking one-undo-per-
  // turn). Released in `finally` so a thrown contour reject never leaks the latch.
  const runUnderInFlightLatch = (
    run: () => AiSafeContourResult,
  ): AiSafeContourResult => {
    if (!tryAcquireInFlight("window-seam")) {
      throw new Error(
        "agent-bridge single-in-flight: another in-page client (bridge / panel) holds an open AI turn; refusing to nest",
      );
    }
    try {
      return run();
    } finally {
      releaseInFlight("window-seam");
    }
  };
  (window as unknown as Record<string, unknown>).editorRunAiCommand = (
    command: AiCommand,
    options?: Pick<RunAiThroughSafeContourOptions, "context" | "label">,
  ): AiSafeContourResult =>
    runUnderInFlightLatch(() =>
      runAiCommandThroughSafeContour(
        command,
        (cmd) => useEditorStore.getState().dispatch(cmd),
        {
          context: resolveAiDispatchContext(options?.context),
          label: options?.label,
          resolveInsertIds: collectInsertIds,
          snapshotEntities: collectEntitySnapshot,
        },
      ),
    );
  (window as unknown as Record<string, unknown>).editorRunAiCommandBatch = (
    commands: readonly AiCommand[],
    options?: Pick<RunAiThroughSafeContourOptions, "context" | "label">,
  ): AiSafeContourResult =>
    runUnderInFlightLatch(() =>
      runAiCommandBatchThroughSafeContour(
        commands,
        (cmd) => useEditorStore.getState().dispatch(cmd),
        {
          context: resolveAiDispatchContext(options?.context),
          label: options?.label,
          resolveInsertIds: collectInsertIds,
          snapshotEntities: collectEntitySnapshot,
        },
      ),
    );

  // Dimension style management API exposed to tests / DevTools.
  // Mirrors AutoCAD DIMSTYLE New/Rename/Delete workflow.
  (window as unknown as Record<string, unknown>).editorAddDimensionStyle = (
    name: string,
    basedOn?: string,
  ) => useEditorStore.getState().addDimensionStyle(name, basedOn);
  (window as unknown as Record<string, unknown>).editorDeleteDimensionStyle = (
    name: string,
  ) => useEditorStore.getState().deleteDimensionStyle(name);
  (window as unknown as Record<string, unknown>).editorRenameDimensionStyle = (
    oldName: string,
    newName: string,
  ) => useEditorStore.getState().renameDimensionStyle(oldName, newName);

  // Multileader style management API — exposed for E2E tests and the
  // forthcoming MLEADERSTYLE Manager dialog. Mirrors the dimension
  // style API exactly.
  (window as unknown as Record<string, unknown>).editorAddMultileaderStyle = (
    name: string,
    basedOn?: string,
  ) => useEditorStore.getState().addMultileaderStyle(name, basedOn);
  (window as unknown as Record<string, unknown>).editorUpdateMultileaderStyle =
    (name: string, updates: Partial<MultileaderStyle>) =>
      useEditorStore.getState().updateMultileaderStyle(name, updates);
  (window as unknown as Record<string, unknown>).editorDeleteMultileaderStyle =
    (name: string) => useEditorStore.getState().deleteMultileaderStyle(name);

  // Layer management API — exposed for E2E tests and the
  // LayerManagerDialog. Mirrors the dimension/multileader style API.
  (window as unknown as Record<string, unknown>).editorAddLayer = (
    name: string,
  ) => useEditorStore.getState().addLayer(name);
  (window as unknown as Record<string, unknown>).editorUpdateLayer = (
    name: string,
    updates: Partial<LayerDefinition>,
  ) => useEditorStore.getState().updateLayer(name, updates);
  (window as unknown as Record<string, unknown>).editorDeleteLayer = (
    name: string,
  ) => useEditorStore.getState().deleteLayer(name);

  // Direct command dispatch (AI / DevTools automation).
  (window as unknown as Record<string, unknown>).editorDispatch = (
    cmd: EditorCommand,
  ) => useEditorStore.getState().dispatch(cmd);
  (window as unknown as Record<string, unknown>).editorGetState = () =>
    useEditorStore.getState();

  // SpaceContext — exposes enterViewport / exitViewport / getState for E2E
  // scenarios that need to programmatically enter MSPACE_VIA_VP or check
  // the current mode without relying on a synthetic dblclick.
  (window as unknown as Record<string, unknown>).spaceContextGetState =
    () => useSpaceContextStore.getState();
  (window as unknown as Record<string, unknown>).spaceContextEnterViewport = (
    viewportId: string,
  ) => useSpaceContextStore.getState().enterViewport(viewportId);
  (window as unknown as Record<string, unknown>).spaceContextExitViewport =
    () => useSpaceContextStore.getState().exitViewport();

  // Sysvar store — exposes the raw OSMODE/DYNMODE/etc. bitmask values for
  // E2E scenarios that need a panel-independent readback (the command
  // transcript only mounts when the history panel is active). Mirrors the
  // spaceContextGetState pattern; editorStore already imports
  // useSysvarStore, so this adds no new module dependency edge.
  (window as unknown as Record<string, unknown>).sysvarGetState = () =>
    useSysvarStore.getState();

  // COLLAB-LOCK-PROPERTY (W3.5) — collab-lock store readback + a direct
  // `setState` seed, for E2E/local-functional-check scenarios that need to
  // exercise the Properties-panel lock rows WITHOUT standing up a real
  // `/collab/ws` companion + a second participant (the WS transport itself
  // is already covered by COLLAB-LOCK-COMMAND's own test suite — this
  // exposure is only for driving the READ-THROUGH property surface).
  // Mirrors `spaceContextGetState`/`sysvarGetState` above.
  (window as unknown as Record<string, unknown>).collabLockGetState = () =>
    useCollabLockStore.getState();
  (window as unknown as Record<string, unknown>).collabLockSetState = (
    partial: Partial<CollabLockStoreState>,
  ) => useCollabLockStore.setState(partial);

  // Low-level entity patch (for cases with no public command yet, e.g.
  // updating Viewport view params). Bypasses validation superRefine hooks
  // only inside the patch path — re-parses against schema afterwards.
  (window as unknown as Record<string, unknown>).editorPatchEntity = (
    entityId: string,
    changes: Record<string, unknown>,
  ) => {
    const store = useEditorStore.getState();
    const nextDoc = patchEntityById(store.geometryDocument, entityId, changes);
    useEditorStore.setState((s) => ({
      ...s,
      geometryDocument: nextDoc,
      state: syncShellStateFromMergedGeometryDocument(s.state, nextDoc),
    }));
    return { ok: true };
  };

  // DXF import / export exposed for DevTools + automation.
  (window as unknown as Record<string, unknown>).editorImportDxfText = (
    source: string,
  ) => useEditorStore.getState().importDxfText(source);
  (window as unknown as Record<string, unknown>).editorImportDxfTextAsync = (
    source: string,
  ) => useEditorStore.getState().importDxfTextAsync(source);
  // AutoCAD OPEN-style file import (replaces current drawing). The
  // legacy `editorImportDxfFileAsync` (INSERT-style merge) is also
  // exposed below for E2E coverage of the merge path.
  (
    window as unknown as Record<string, unknown>
  ).editorImportDxfFileAsyncReplacing = (file: File) =>
    useEditorStore.getState().importDxfFileAsyncReplacing(file);
  (window as unknown as Record<string, unknown>).editorImportDxfFileAsync = (
    file: File,
  ) => useEditorStore.getState().importDxfFileAsync(file);
  (window as unknown as Record<string, unknown>).editorExportDxfText = () =>
    useEditorStore.getState().exportDxfText();

  // Block-definitions inspection — needed to work with block library
  // (e.g. enumerate available block names before inserting).
  (window as unknown as Record<string, unknown>).editorGetBlocks = () => {
    const store = useEditorStore.getState();
    const blocks = store.geometryDocument?.blocks ?? {};
    return Object.entries(blocks).map(([key, b]) => ({
      key,
      name: b.name,
      handle: b.handle,
      entityCount: (b.entities ?? []).length,
      rawEntityGroupsCount: (b.rawEntityGroups ?? []).length,
      basePoint: b.basePoint,
      description: b.description,
    }));
  };

  // Read-only inspection API for tests / DevTools.
  // Returns a JSON-serializable snapshot of the parts of the editor state
  // that tests typically want to assert against (dimension styles, text
  // styles, entities, selection). NOT a full state dump — intentionally
  // narrow so it is stable and cheap.
  (window as unknown as Record<string, unknown>).editorInspect = () => {
    const store = useEditorStore.getState();
    // Flatten entities from all spaces (model + layouts) so tests can
    // query by id without caring about space topology.
    const geoEntities = (store.geometryDocument?.spaces ?? []).flatMap(
      (space) => space.entities ?? [],
    );
    return {
      dimensionStyles:
        store.geometryDocument?.styleTables?.dimensionStyles ?? [],
      textStyles: store.geometryDocument?.styleTables?.textStyles ?? [],
      multileaderStyles:
        store.geometryDocument?.styleTables?.multileaderStyles ?? [],
      layers: store.geometryDocument?.styleTables?.layers ?? [],
      // Full geometry entities (with DXF fidelity fields)
      entities: geoEntities,
      // Shell entities list (simplified, as shown in entity cards)
      shellEntities: store.state?.drawing?.entities ?? [],
      selection: store.state?.selection?.entityIds ?? [],
      activeSpaceId: store.state?.drawing?.activeSpaceId,
      // CC-2d — active inspector panel, so the AI-panel e2e specs can
      // assert the ribbon AI button actually flips the shell state.
      activePanel: store.state?.ui?.activePanel,
      // Block dictionaries — exposed so E2E tests can verify the
      // PURGE / RENAME / WBLOCK / DXF-import paths without having
      // to peek at the raw zustand store. Keys are block names; the
      // values are the BlockDefinition / BlockRecord objects from
      // the document.
      blocks: store.geometryDocument?.blocks ?? {},
      blockRecords: store.geometryDocument?.blockRecords ?? {},
      // Audit feed events — useful for tests that need to verify a
      // toast / summary fired without poking at the DOM.
      auditEvents: store.state?.audit?.events ?? [],
    };
  };

  // Dispatch API for E2E / DevTools. Thin passthrough to the store's
  // dispatch — the reducer validates & schemas-parse at entry, so this
  // is safe to call from page.evaluate() in tests. Returns `true`
  // unconditionally (the reducer silently ignores invalid payloads
  // rather than throwing, matching how the UI uses it).
  (window as unknown as Record<string, unknown>).editorDispatch = (
    command: unknown,
  ) => {
    try {
      useEditorStore.getState().dispatch(command as never);
      return true;
    } catch {
      return false;
    }
  };
}
